/*
 * MathZoo Dashboard - Firebase Data Structure Documentation
 *
 * === NEW FIREBASE STRUCTURE (Current) ===
 * users/
 *   {userId}/
 *     grades/
 *       grade1/
 *         {difficulty}/ (easy/medium/hard)
 *           sessions/
 *             {sessionId}/
 *               metadata: { startedAt, endedAt, sessionDuration }
 *               summary: { totalCorrect, totalWrong, totalQuestions, totalTimeSpent, xpEarned }
 *               questions: {
 *                 correct: { [questionId]: { questionText, correctAnswer, userAnswer, conceptualArea, ... } }
 *                 wrong: {
 *                   [questionId]: {
 *                     [attemptNumber]: { attemptNumber, questionText, correctAnswer, userAnswer, errorType,
 *                                       masteryIndicator, remediationSuggestion, conceptualArea,
 *                                       needsReview, retryPriority, confidenceLevel, ... }
 *                   }
 *                 }
 *                 skipped: { [questionId]: {...} }
 *               }
 *               // NOTE: questions.wrong uses a nested attempts map (keyed 1, 2, 3 ...) so a single
 *               // question can have multiple wrong attempts before being answered correctly.
 *               // Use flattenWrongAttempts() in App.js to get a flat array of attempt objects.
 *               learningMetrics: { focusScore, accuracyTrend, speedTrend }
 *
 * === OLD FIELD MAPPINGS (Deprecated) ===
 * Old Field Name           → New Field Name
 * ────────────────────────────────────────
 * totalDurationSeconds     → summary.totalTimeSpent
 * earnedXP                 → summary.xpEarned
 * correctAnswers           → summary.totalCorrect
 * wrongAnswers             → summary.totalWrong
 * startTime                → metadata.startedAt
 * questionsAnswered[]      → questions.{correct/wrong/skipped}
 * question.isCorrect       → (derived from category: correct/wrong/skipped)
 * question.selectedAnswer  → question.userAnswer
 * question.problemType     → question.conceptualArea
 *
 * === MISSING/UNAVAILABLE FIELDS ===
 * The following fields were used in the old dashboard but are NOT available in the new Firebase structure:
 *
 * 1. Time-of-day data (morning/evening performance) - metadata.startedAt exists but no pre-categorization
 * 2. Peer comparison data - No peer average data in Firebase (hardcoded benchmarks used instead)
 * 3. Historical accuracy trends - Must be calculated from session history
 * 4. Focus/Rush/Distracted behavioral flags per session - Must be inferred from learningMetrics.focusScore and time metrics
 * 5. Streak data - Not available in current structure
 * 6. Badge/achievement data - Not available in current structure
 *
 * === NEW FEATURES ENABLED ===
 * The new structure provides rich question-level analytics:
 * - errorType: Specific error classification (off_by_one, place_value_error, conceptual, etc.)
 * - masteryIndicator: mastered, developing, needs_practice
 * - remediationSuggestion: AI-generated specific advice
 * - needsReview: Boolean flag for questions requiring attention
 * - retryPriority: high, medium, low
 * - confidenceLevel: Student's self-reported confidence
 * - conceptualArea: Detailed categorization (addition, subtraction, word_problems, etc.)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, AreaChart, Area, ReferenceLine } from 'recharts';
import { TrendingUp, Clock, Star, Target, Award, Brain, AlertCircle, CheckCircle, Trophy, Activity, BookOpen, Crown, LogOut, Globe, FileText, Printer, RefreshCw, CreditCard, Home, GitBranch, Sparkles, ChevronDown, ChevronUp, Check, MessageCircle, ArrowRight, CheckSquare, Square, Lock, Calendar, Search, Compass, Info } from 'lucide-react';
import LoginPage from './LoginPage';
import PrivacyPage from './PrivacyPage';
import { auth } from './firebase';
import { onAuthStateChanged, signOut, getIdToken } from 'firebase/auth';
import { generatePerformanceReport } from './services/pdfService';
import { getUser, getUserByEmail } from './services/db';
import { getDinnerPracticesForGrade, DEFAULT_DINNER_PRACTICES } from './services/dinnerPracticeService';
import { trackEvent, trackPageView } from './utils/analytics';

// App colors shared across components
const appColors = {
  primary: '#81a550',
  secondary: '#66AA44',
  accent: '#e2c269',
  background: '#F5E4BA',
  success: '#81a550',
  warning: '#FF9800',
  error: '#F44336',
  textPrimary: '#333333',
  textSecondary: '#666666',
  textLight: '#999999',
  grade1: '#81C784',
  grade2: '#FFB74D',
  grade3: '#E57373',
  separatorBrown: '#e9d09b'
};

const defaultFontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

// ─── Auth helper ─────────────────────────────────────────────────────────────
/**
 * Returns the current user's Firebase ID token, or null if not signed in.
 * Used to attach an Authorization header to all API calls so the server
 * can verify the caller's identity.
 */
async function getAuthToken() {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await getIdToken(user, true);
  } catch {
    return null;
  }
}

/**
 * Builds the Authorization header object.
 * Returns {} if the user is not signed in (graceful degradation in dev).
 */
async function authHeaders() {
  const token = await getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Available animal avatars (matching app assets)
const availableAnimals = ['bear', 'bird', 'cat', 'cow', 'deer', 'dog', 'elephant', 'giraffe', 'hippo', 'horse', 'koala', 'lion', 'monkey', 'mouse', 'panda', 'pig', 'rabbit', 'sheep', 'squirrel', 'tiger'];

const getAnimalImage = (animal) => {
  const animalName = animal?.toLowerCase() || 'koala';
  if (availableAnimals.includes(animalName)) {
    return `/animals/${animalName}.png`;
  }
  return '/animals/koala.png'; // Default fallback
};

const getAccuracyColor = (accuracy, difficulty) => {
  const benchmarks = { easy: 75, medium: 65, hard: 55 };
  const benchmark = benchmarks[difficulty] || 70;
  const diff = accuracy - benchmark;
  if (diff >= 5) return appColors.success;
  if (diff >= -5) return appColors.warning;
  return appColors.error;
};

// Reusable Components (Defined outside to prevent unnecessary unmounting)
const FloatingCard = ({ children, style = {} }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.5s',
    ...style
  }}>
    {children}
  </div>
);

const TabButton = ({ id, label, icon: Icon, active, onClick }) => (
  <button
    onClick={() => onClick(id)}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '12px',
      fontWeight: '500',
      fontSize: '14px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s',
      background: active
        ? `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.secondary} 100%)`
        : 'transparent',
      color: active ? 'white' : '#666',
      boxShadow: active ? '0 4px 12px rgba(129, 165, 80, 0.3)' : 'none'
    }}
  >
    <Icon size={16} />
    <span>{label}</span>
  </button>
);

const InsightCard = ({ type, title, description, actionable, animal }) => {
  const configs = {
    strength: { icon: CheckCircle, color: appColors.success },
    weakness: { icon: AlertCircle, color: appColors.error },
    tip: { icon: Brain, color: appColors.accent }
  };
  const config = configs[type] || { icon: Target, color: appColors.primary };
  const Icon = config.icon;
  return (
    <FloatingCard style={{
      padding: '20px',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            padding: '8px',
            borderRadius: '12px',
            backgroundColor: config.color + '33',
            height: 'fit-content'
          }}>
            <Icon size={20} color={config.color} />
          </div>
          <h4 style={{ fontWeight: '600', margin: 0, color: appColors.textPrimary, flex: 1 }}>{title}</h4>
          {animal && <div style={{ fontSize: '24px' }}>{animal}</div>}
        </div>
        <p style={{ fontSize: '14px', marginBottom: '8px', margin: 0, color: appColors.textSecondary }}>{description}</p>
      </div>
      {actionable && (
        <div style={{
          fontSize: '12px',
          padding: '12px',
          borderRadius: '12px',
          fontWeight: '500',
          backgroundColor: config.color + '15',
          color: config.color,
          marginTop: '12px'
        }}>
          {actionable}
        </div>
      )}
    </FloatingCard>
  );
};

const UserProfileCard = ({ userData, gradeStats, analytics, isPro, isMobile }) => {
  const isSmall = window.innerWidth < 480;
  return (
  <FloatingCard style={{ padding: '24px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: isSmall ? '8px' : '16px' }}>
      <div style={{ position: 'relative' }}>
        <div style={{
          width: isSmall ? '64px' : '84px',
          height: isSmall ? '64px' : '84px',
          borderRadius: '50%',
          backgroundColor: '#F5E4BA',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <img
            src={getAnimalImage(userData?.profile?.avatar)}
            alt={userData?.profile?.avatar || 'avatar'}
            style={{
              width: isSmall ? '52px' : '70px',
              height: isSmall ? '52px' : '70px',
              objectFit: 'contain'
            }}
          />
        </div>
        {isPro && (
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            backgroundColor: 'white',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Crown size={14} color="#F6C112" fill="#F6C112" />
          </div>
        )}
      </div>

      {/* ── Info + session badges ── */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', minWidth: 0 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* Name row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <h2 style={{ fontSize: isSmall ? '16px' : '20px', fontWeight: 'bold', color: appColors.textPrimary, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userData?.profile?.name || 'Loading...'}
              </h2>
              {isPro && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '4px 12px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: 'white',
                  backgroundColor: 'rgb(129, 165, 80)',
                  borderRadius: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  flexShrink: 0
                }}>
                  <Crown size={14} color="#F6C112" fill="#F6C112" />
                  PRO
                </span>
              )}
            </div>
            {/* Session badges inline with name — only on < 480px */}
            {isSmall && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <div title="Completed sessions" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '16px', backgroundColor: appColors.success + '20', border: `1px solid ${appColors.success}40`, cursor: 'default' }}>
                  <CheckCircle size={14} color={appColors.success} />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: appColors.success }}>{analytics?.completedSessions || 0}</span>
                </div>
                <div title="In progress sessions" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '16px', backgroundColor: appColors.warning + '20', border: `1px solid ${appColors.warning}40`, cursor: 'default' }}>
                  <Clock size={14} color={appColors.warning} />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: appColors.warning }}>{analytics?.inProgressSessions || 0}</span>
                </div>
              </div>
            )}
          </div>

          <p style={{ fontSize: '14px', color: appColors.primary, margin: '0 0 8px 0' }}>
            Age: {userData?.profile?.age || '-'}
          </p>
          <div style={{ display: 'flex', gap: '16px', fontSize: '14px' }}>
            <span style={{ fontWeight: '600', color: appColors.textPrimary }}>
              Current: Level {userData?.progress?.currentGrade || '-'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '24px', marginTop: '8px', fontSize: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
              <Clock size={16} color={appColors.textSecondary} />
              <span style={{ color: appColors.textSecondary }}>
                Total Play time: {(() => {
                  let totalSeconds = 0;
                  Object.values(gradeStats || {}).forEach(gradeData => {
                    totalSeconds += gradeData.stats?.totalTimeSeconds || 0;
                  });
                  if (totalSeconds < 60) return `${Math.round(totalSeconds)} sec`;
                  return `${Math.round(totalSeconds / 60)} min`;
                })()}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
              <Trophy size={16} color="#FFD700" />
              <span style={{ fontWeight: '500', color: appColors.primary }}>
                {analytics?.totalXP || 0} XPs
              </span>
            </div>
          </div>
        </div>

        {/* Session badges on the right — only on ≥ 480px (original main style) */}
        {!isSmall && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', alignSelf: 'flex-start', flexShrink: 0, ...(isMobile ? {} : { padding: '5px 16px 5px 12px', borderRadius: '12px', backgroundColor: appColors.background + '60' }) }}>
            {!isMobile && (
              <span style={{ fontSize: '13px', color: appColors.textSecondary, fontWeight: '500' }}>Sessions:</span>
            )}
            <div title="Completed sessions" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '16px', backgroundColor: appColors.success + '20', border: `1px solid ${appColors.success}40`, cursor: 'default' }}>
              <CheckCircle size={14} color={appColors.success} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: appColors.success }}>{analytics?.completedSessions || 0}</span>
            </div>
            <div title="In progress sessions" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '16px', backgroundColor: appColors.warning + '20', border: `1px solid ${appColors.warning}40`, cursor: 'default' }}>
              <Clock size={14} color={appColors.warning} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: appColors.warning }}>{analytics?.inProgressSessions || 0}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  </FloatingCard>
  );
};


const Dashboard = ({ userId, initialUserData, onLogout }) => {
  const [userData, setUserData] = useState(initialUserData || null);
  const [sessions, setSessions] = useState([]);
  const [gradeStats, setGradeStats] = useState({}); // Stats from DB (source of truth)
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('thisWeek');
  const [hasNewDataNotification, setHasNewDataNotification] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null);
  const [childProfileOpen, setChildProfileOpen] = useState(false);
  const childProfileRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (childProfileRef.current && !childProfileRef.current.contains(event.target)) {
        setChildProfileOpen(false);
      }
    }
    if (childProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [childProfileOpen]);

  const [hoveredHeaderBtn, setHoveredHeaderBtn] = useState(null);
  const [dinnerPracticeIndex, setDinnerPracticeIndex] = useState(0);
  const [todoChecklist, setTodoChecklist] = useState({ item1: false, item2: false, item3: false });
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const [highlightedCategory, setHighlightedCategory] = useState(null);
  const highlightTimeoutRef = useRef(null);

  const triggerCategoryHighlight = (categoryName) => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setHighlightedCategory(categoryName);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedCategory(null);
    }, 2500);
  };
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    trackEvent('tab_view', { tab: tabId });
    if (tabId === 'insights') {
      setHasNewDataNotification(false);
      const lastPlayedTime = userData?.progress?.lastPlayed;
      if (lastPlayedTime && userId) {
        localStorage.setItem(`mathzoo_seen_played_${userId}`, lastPlayedTime);
      }
    }
  };
  const [trendsView, setTrendsView] = useState('weekly');
  // mobileMenuOpen removed — replaced by inline tab bar on mobile
  const [generatedProblems, setGeneratedProblems] = useState([]);
  const [isGeneratingPractice, setIsGeneratingPractice] = useState(false);
  // true = problems came from Gemini API; false = local fallback generator
  const [problemsFromAPI, setProblemsFromAPI] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState(1);
  const [isMobile] = useState(window.innerWidth <= 768);

  // Payment state
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // Language selection for print
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [problemsLanguage, setProblemsLanguage] = useState('en');
  const [needsGeneration, setNeedsGeneration] = useState(false);
  const generatorRef = useRef(null);
  // Tracks when we intentionally cleared problems for a new generation.
  // Prevents the auto-load init effect from firing a second request when
  // handleGenerateNew sets generatedProblems to [] before the API returns.
  const isManualGenerationRef = useRef(false);
  // Tracks the last userId that was successfully loaded, preventing duplicate
  // Firebase fetches when handleLogin + onAuthStateChanged both set the same userId.
  const loadedUserIdRef = useRef(null);

  // AI Report State
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  // Initialise from localStorage so the "Download PDF Report" button survives
  // page refreshes and tab switches (report data can be large, so we only
  // write/read it for the current user and clear it after the PDF is saved).
  const reportStorageKey = userId ? `mz_report_${userId}` : null;
  const [generatedReport, setGeneratedReport] = useState(() => {
    if (!userId) return null;
    try {
      const saved = localStorage.getItem(`mz_report_${userId}`);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Persist generatedReport to localStorage so the "Download PDF Report" button
  // survives page refreshes and tab switches.
  // • When a report is ready  → save to localStorage
  // • When report is cleared  → remove from localStorage (after PDF download)
  // • When userId changes     → do nothing; the lazy-init above already read the
  //                             correct key on mount, and a new login will get
  //                             its own key.
  useEffect(() => {
    if (!reportStorageKey) return;
    if (generatedReport) {
      try {
        localStorage.setItem(reportStorageKey, JSON.stringify(generatedReport));
      } catch {
        // localStorage full or unavailable — silently ignore
      }
    } else {
      localStorage.removeItem(reportStorageKey);
    }
  }, [generatedReport, reportStorageKey]);

  // Rate limit state — shown when API returns 429
  const [rateLimitInfo, setRateLimitInfo] = useState(null); // { type: 'report'|'practice', resetAt: ISO string }

  // Reports generated count state and persistence
  const [reportsCount, setReportsCount] = useState(() => {
    if (!userId) return 0;
    try {
      const todayStr = new Date().toDateString();
      const savedDate = localStorage.getItem(`mz_reports_date_${userId}`);
      if (savedDate === todayStr) {
        const savedCount = localStorage.getItem(`mz_reports_count_${userId}`);
        return savedCount ? parseInt(savedCount, 10) : 0;
      }
      return 0;
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    if (!userId) return;
    try {
      const todayStr = new Date().toDateString();
      const savedDate = localStorage.getItem(`mz_reports_date_${userId}`);
      if (savedDate !== todayStr) {
        localStorage.removeItem(`mz_reports_count_${userId}`);
        localStorage.removeItem(`mz_reports_date_${userId}`);
        setReportsCount(0);
      }
      
      const savedDownload = localStorage.getItem(`mz_report_downloaded_${userId}`);
      const hasReport = localStorage.getItem(`mz_report_${userId}`) !== null;
      if (hasReport && savedDownload === 'false') {
        setHasDownloadedLatestReport(false);
      } else {
        setHasDownloadedLatestReport(true);
      }
    } catch (e) {
      console.error(e);
    }
  }, [userId]);

  const incrementReportsCount = () => {
    if (!userId) return;
    const todayStr = new Date().toDateString();
    const newCount = reportsCount + 1;
    setReportsCount(newCount);
    try {
      localStorage.setItem(`mz_reports_date_${userId}`, todayStr);
      localStorage.setItem(`mz_reports_count_${userId}`, newCount.toString());
    } catch (e) {
      console.error(e);
    }
  };

  // State to track if the freshly generated report has been downloaded
  const [hasDownloadedLatestReport, setHasDownloadedLatestReport] = useState(() => {
    if (!userId) return true;
    try {
      const saved = localStorage.getItem(`mz_report_downloaded_${userId}`);
      // If we have a generated report in local storage but the downloaded key is 'false', it was not downloaded
      const hasReport = localStorage.getItem(`mz_report_${userId}`) !== null;
      if (hasReport && saved === 'false') {
        return false;
      }
      return true;
    } catch {
      return true;
    }
  });

  // Check if user is PRO (check dashboardSubscription under profile for web subscriptions)
  const isPro = userData?.profile?.dashboardSubscription?.status === 'PRO' ||
    userData?.profile?.subscription === 'PRO';

  const generatePracticeProblems = useCallback((count = 3, lang = 'en') => {
    // Default to generating problems for the active user grade instead of overall weakest historical grade
    const targetGrade = parseInt(userData?.progress?.currentGrade || 1);

    // ALWAYS use English canonical type keys — the badge JSX lookup table translates them for display.
    const c = { addition: "Addition", subtraction: "Subtraction", multiplication: "Multiplication", division: "Division", word: "Word Problems" };

    // Clean single-line explanations per language × type (no numbers, no story phrasing).
    // Mirrors the EXPLANATIONS table in api/generate-practice.js.
    const EXPS = {
      en: { Addition: 'Add the numbers together to find the total.', Subtraction: 'Subtract to find out how many are left.', Multiplication: 'Multiply to find the total amount.', Division: 'Divide to find out how many each one gets.' },
      bg: { Addition: 'Събери числата, за да намериш общия брой.', Subtraction: 'Извади, за да намериш колко остава.', Multiplication: 'Умножи, за да намериш общия брой.', Division: 'Раздели, за да намериш колко се пада на всеки.' },
      nl: { Addition: 'Tel de getallen op om het totaal te vinden.', Subtraction: 'Trek af om te zien wat er overblijft.', Multiplication: 'Vermenigvuldig om het totaal te vinden.', Division: 'Deel om te zien hoeveel elk krijgt.' },
      de: { Addition: 'Addiere die Zahlen, um die Gesamtzahl zu finden.', Subtraction: 'Subtrahiere, um herauszufinden, was übrig bleibt.', Multiplication: 'Multipliziere, um die Gesamtzahl zu finden.', Division: 'Dividiere, um herauszufinden, wie viel jeder bekommt.' },
      ru: { Addition: 'Сложи числа, чтобы найти общее количество.', Subtraction: 'Вычти, чтобы узнать, сколько осталось.', Multiplication: 'Умножь, чтобы найти общее количество.', Division: 'Раздели, чтобы узнать, сколько достанется каждому.' },
      es: { Addition: 'Suma los números para encontrar el total.', Subtraction: 'Resta para saber cuánto queda.', Multiplication: 'Multiplica para encontrar el total.', Division: 'Divide para saber cuánto le toca a cada uno.' },
    };
    const exps = EXPS[lang] || EXPS.en;

    // Canonical question prefix per language for symbolic drills
    const Q_PREFIX = { en: 'What is', bg: 'Колко е', nl: 'Wat is', de: 'Was ist', ru: 'Сколько будет', es: '¿Cuánto es' };
    const qpfx = Q_PREFIX[lang] || Q_PREFIX.en;

    // Multi-language templates
    const langTemplates = {
      en: {
        names: ['Emma', 'Noah', 'Olivia', 'Liam', 'Sophia', 'Mason', 'Ava', 'Jacob', 'Isabella', 'Ethan'],
        items: ['apples', 'candies', 'toys', 'books', 'pencils', 'stickers', 'marbles', 'cookies', 'cards', 'flowers'],
        add: {
          q: (n, a, b, i) => `${n} has ${a} ${i}. ${n} gets ${b} more. How many ${i} does ${n} have?`,
          exp: (i, a, b) => `We need to find the total number of ${i} by adding ${a} and ${b}.`,
          steps: (a, b, s) => [`Start with ${a}`, `Add ${b} more`, `So, the total is... ?`]
        },
        sub: {
          q: (n, a, b, i) => `${n} has ${a} ${i}. ${n} gives away ${b}. How many ${i} are left?`,
          exp: (i, a, b) => `Subtract the amount given away from the starting amount.`,
          steps: (a, b, d) => [`Start with ${a}`, `Subtract ${b}`, `Which leaves... ?`]
        },
        mul: {
          q: (n, a, b, i) => `${n} has ${a} groups of ${i}. Each group has ${b} ${i}. Total?`,
          exp: () => `Multiplication is repeated addition of same-sized groups.`,
          steps: (a, b, p) => [`${a} groups`, `${b} in each`, `So, all together we have... ?`]
        },
        div: {
          q: (n, a, b, i) => `${n} has ${a} ${i} to share equally among ${b} friends. How many each?`,
          exp: () => `Division is splitting a total into equal smaller groups.`,
          steps: (a, b, r) => [`Divide ${a} items`, `Among ${b} friends`, `So, each friend gets... ?`]
        },
        common: { tip: 'Take your time with each problem. If stuck, break the problem into smaller steps. Making mistakes is part of learning!', addQ: 'What is', subQ: 'What is', mulQ: 'What is', divQ: 'What is' }
      },
      nl: {
        names: ['Daan', 'Lotte', 'Milan', 'Emma', 'Thijs', 'Sophie', 'Luuk', 'Julia', 'Bram', 'Tess'],
        items: ['appels', 'snoepjes', 'speelgoed', 'boeken', 'potloden', 'stickers', 'knikkers', 'koekjes', 'kaarten', 'bloemen'],
        add: {
          q: (n, a, b, i) => `${n} heeft ${a} ${i}. ${n} krijgt er ${b} bij. Hoeveel ${i} heeft ${n} nu?`,
          exp: (i, a, b) => `We tellen ${a} en ${b} bij elkaar op om het totaal te vinden.`,
          steps: (a, b, s) => [`Begin met ${a}`, `Doe er ${b} bij`, `Dus, het totaal is... ?`]
        },
        sub: {
          q: (n, a, b, i) => `${n} heeft ${a} ${i}. ${n} geeft er ${b} weg. Hoeveel ${i} blijven er over?`,
          exp: (i, a, b) => `Trek het aantal dat weggegeven is af van het beginbedrag.`,
          steps: (a, b, d) => [`Begin met ${a}`, `Trek er ${b} vanaf`, `Dat laat over... ?`]
        },
        mul: {
          q: (n, a, b, i) => `${n} heeft ${a} groepen van ${i}. Elke groep heeft ${b} ${i}. Totaal?`,
          exp: () => `Vermenigvuldigen is herhaald optellen van gelijke groepen.`,
          steps: (a, b, p) => [`${a} groepen`, `${b} in elke`, `Dus, samen hebben we... ?`]
        },
        div: {
          q: (n, a, b, i) => `${n} heeft ${a} ${i} om eerlijk te verdelen over ${b} vrienden. Hoeveel elk?`,
          exp: () => `Delen is het verdelen van een totaal in gelijke kleinere groepen.`,
          steps: (a, b, r) => [`Verdeel ${a} items`, `Over ${b} vrienden`, `Dus, iedereen krijgt... ?`]
        },
        common: { tip: 'Neem de tijd. Als je vastloopt, verdeel het probleem dan in kleine stappen. Fouten maken hoort bij het leren!', addQ: 'Hoeveel is', subQ: 'Hoeveel is', mulQ: 'Hoeveel is', divQ: 'Hoeveel is' }
      },
      de: {
        names: ['Lukas', 'Mia', 'Leon', 'Emma', 'Finn', 'Hannah', 'Elias', 'Sofia', 'Ben', 'Lea'],
        items: ['Äpfel', 'Süßigkeiten', 'Spielzeug', 'Bücher', 'Bleistifte', 'Sticker', 'Murmeln', 'Kekse', 'Karten', 'Blumen'],
        add: {
          q: (n, a, b, i) => `${n} hat ${a} ${i}. ${n} bekommt ${b} dazu. Wie viele ${i} hat ${n} insgesamt?`,
          exp: (i, a, b) => `Wir addieren ${a} und ${b}, um die Gesamtzahl zu finden.`,
          steps: (a, b, s) => [`Beginne mit ${a}`, `Addiere ${b}`, `Also ist die Gesamtzahl... ?`]
        },
        sub: {
          q: (n, a, b, i) => `${n} hat ${a} ${i}. ${n} gibt ${b} weg. Wie viele ${i} bleiben übrig?`,
          exp: (i, a, b) => `Ziehe die weggegebene Menge von der Startmenge ab.`,
          steps: (a, b, d) => [`Beginne mit ${a}`, `Subtrahiere ${b}`, `Das lässt übrig... ?`]
        },
        mul: {
          q: (n, a, b, i) => `${n} hat ${a} Gruppen von ${i}. Jede Gruppe hat ${b} ${i}. Gesamt?`,
          exp: () => `Multiplikation ist wiederholte Addition gleich großer Gruppen.`,
          steps: (a, b, p) => [`${a} Gruppen`, `${b} in jeder`, `Also haben wir zusammen... ?`]
        },
        div: {
          q: (n, a, b, i) => `${n} hat ${a} ${i} zum gleichmäßigen Teilen unter ${b} Freunden. Wie viele erhält jeder?`,
          exp: () => `Division ist das Aufteilen eines Ganzen in gleiche kleinere Gruppen.`,
          steps: (a, b, r) => [`Teile ${a} Gegenstände`, `Unter ${b} Freunden`, `Also bekommt jeder... ?`]
        },
        common: { tip: 'Nimm dir Zeit. Wenn du nicht weiterkommst, teile die Aufgabe in kleine Schritte auf. Fehler gehören zum Lernen dazu!', addQ: 'Was ist', subQ: 'Was ist', mulQ: 'Was ist', divQ: 'Was ist' }
      },
      es: {
        names: ['Mateo', 'Sofía', 'Santiago', 'Valentina', 'Matías', 'Isabella', 'Sebastián', 'Camila', 'Dylan', 'Valeria'],
        items: ['manzanas', 'dulces', 'juguetes', 'libros', 'lápices', 'pegatinas', 'canicas', 'galletas', 'cartas', 'flores'],
        add: {
          q: (n, a, b, i) => `${n} tiene ${a} ${i}. ${n} recibe ${b} más. ¿Cuántas ${i} tiene ${n} en total?`,
          exp: (i, a, b) => `Sumamos ${a} y ${b} para encontrar el total de ${i}.`,
          steps: (a, b, s) => [`Empieza con ${a}`, `Suma ${b}`, `Entonces, el total es... ?`]
        },
        sub: {
          q: (n, a, b, i) => `${n} tiene ${a} ${i}. ${n} regala ${b}. ¿Cuántas ${i} le quedan?`,
          exp: (i, a, b) => `Resta la cantidad regalada de la cantidad inicial.`,
          steps: (a, b, d) => [`Empieza con ${a}`, `Resta ${b}`, `Lo que nos deja con... ?`]
        },
        mul: {
          q: (n, a, b, i) => `${n} tiene ${a} grupos de ${i}. Cada grupo tiene ${b} ${i}. ¿Total?`,
          exp: () => `La multiplicación es la suma repetida de grupos del mismo tamaño.`,
          steps: (a, b, p) => [`Tienes ${a} grupos`, `${b} in cada uno`, `Así que en total tenemos... ?`]
        },
        div: {
          q: (n, a, b, i) => `${n} tiene ${a} ${i} para compartir por igual entre ${b} amigos. ¿Cuánto recibe cada uno?`,
          exp: () => `La división es dividir un total en grupos más pequeños e iguales.`,
          steps: (a, b, r) => [`Divide ${a} objetos`, `Entre ${b} amigos`, `Alors chaque reçoit... ?`]
        },
        common: { tip: 'Tómate tu tiempo. Si te bloqueas, divide el problema en pasos pequeños. ¡Cometer errores es parte del aprendizaje!', addQ: '¿Cuánto es', subQ: '¿Cuánto es', mulQ: '¿Cuánto es', divQ: '¿Cuánto es' }
      },
      bg: {
        names: ['Иван', 'Мария', 'Георги', 'Елена', 'Димитър', 'Анна', 'Николай', 'Петя', 'Стефан', 'Йоана'],
        items: ['ябълки', 'бонбони', 'играчки', 'книги', 'моливи', 'стикери', 'мраморни топчета', 'бисквитки', 'карти', 'цветя'],
        add: {
          q: (n, a, b, i) => `${n} има ${a} ${i}. ${n} получава още ${b}. Колко ${i} има ${n}?`,
          exp: (i, a, b) => `Трябва да съберем ${a} и ${b}, за да намерим общия брой ${i}.`,
          steps: (a, b, s) => [`Започни с ${a}`, `Добави ${b}`, `Значи общо стават... ?`]
        },
        sub: {
          q: (n, a, b, i) => `${n} има ${a} ${i}. ${n} дава на други ${b}. Колко ${i} му остават?`,
          exp: (i, a, b) => `Извади подареното количество от първоначалното.`,
          steps: (a, b, d) => [`Започни с ${a}`, `Извади ${b}`, `Тогава остават... ?`]
        },
        mul: {
          q: (n, a, b, i) => `${n} има ${a} групи от ${i}. Всяка група има по ${b} ${i}. Общо?`,
          exp: () => `Умножението е последователно събиране на групи с еднакъв размер.`,
          steps: (a, b, p) => [`Имаш ${a} групи`, `По ${b} във всяка`, `Така че общо имаме... ?`]
        },
        div: {
          q: (n, a, b, i) => `${n} има ${a} ${i}, които трябва да раздели поравно между ${b} приятели. По колко получава всеки?`,
          exp: () => `Делението е разделяне на общото количество на равни по-малки групи.`,
          steps: (a, b, r) => [`Раздели ${a} предмета`, `Между ${b} приятели`, `Значи всеки получава... ?`]
        },
        common: { tip: 'Не бързай. Ако се затрудниш, раздели задачата на части. Грешките са част от обучението!', addQ: 'Колко е', subQ: 'Колко е', mulQ: 'Колко е', divQ: 'Колко е' }
      },
      ru: {
        names: ['Иван', 'Анна', 'Максим', 'Мария', 'Артем', 'София', 'Михаил', 'Елена', 'Даниил', 'Виктория'],
        items: ['яблок', 'конфет', 'игрушек', 'книг', 'карандашей', 'наклеек', 'шариков', 'печений', 'карточек', 'цветов'],
        common: {
          tip: 'Не торопись. Если застрял, разбей задачу на части. Ошибки - это часть обучения!',
          addQ: 'Сколько будет',
          subQ: 'Сколько будет',
          mulQ: 'Сколько будет',
          divQ: 'Сколько будет'
        },
        add: {
          q: (n, a, b, i) => `У ${n} было ${a} ${i}. ${n} получил(а) еще ${b}. Сколько ${i} у ${n} всего?`,
          exp: (i, a, b) => `Нужно сложить ${a} и ${b}, чтобы найти общее количество.`,
          steps: (a, b, s) => [`Начни с ${a}`, `Прибавь ${b}`, `Значит, всего получается... ?`]
        },
        sub: {
          q: (n, a, b, i) => `У ${n} было ${a} ${i}. ${n} отдал(а) ${b}. Сколько ${i} осталось?`,
          exp: (i, a, b) => `Вычти количество отданных предметов из начального.`,
          steps: (a, b, d) => [`Начни с ${a}`, `Вычти ${b}`, `Тогда остается... ?`]
        },
        mul: {
          q: (n, a, b, i) => `У ${n} есть ${a} групп ${i}. В каждой группе по ${b}. Сколько ${i} всего?`,
          exp: () => `Умножение - это повторное сложение групп одинакового размера.`,
          steps: (a, b, p) => [`У тебя ${a} групп`, `По ${b} в каждой`, `Так что всего у нас... ?`]
        },
        div: {
          q: (n, a, b, i) => `У ${n} есть ${a} ${i}, чтобы поровну разделить между ${b} друзьями. Сколько получит каждый?`,
          exp: () => `Деление - это распределение общего количества на равные части.`,
          steps: (a, b, r) => [`Раздели ${a} предметов`, `На ${b} друзей`, `Значит, каждый получает... ?`]
        }
      }
    };

    const t = langTemplates[lang] || langTemplates.en;
    const common = t.common || langTemplates.en.common;

    const problems = [];
    for (let i = 0; i < count; i++) {
      let problem;
      let allowedTypes = ['add', 'sub'];
      if (targetGrade >= 2) allowedTypes.push('mul');
      if (targetGrade >= 3) allowedTypes.push('div');

      const selectedType = allowedTypes[Math.floor(Math.random() * allowedTypes.length)];
      const isWordProblem = Math.random() < 0.4; // Slightly more word problems for PRO

      if (selectedType === 'add') {
        // Addition
        const a = Math.floor(Math.random() * (10 * targetGrade)) + 1;
        const b = Math.floor(Math.random() * (10 * targetGrade)) + 1;
        const sum = a + b;
        if (isWordProblem) {
          const name = t.names[Math.floor(Math.random() * t.names.length)];
          const item = t.items[Math.floor(Math.random() * t.items.length)];
          problem = {
            question: t.add.q(name, a, b, item),
            answer: sum,
            type: c.word,
            explanation: t.add.exp(item, a, b),
            steps: t.add.steps(a, b, sum)
          };
        } else {
          problem = {
            question: `${qpfx} ${a} + ${b}?`,
            answer: sum,
            type: c.addition,
            explanation: exps.Addition,
            steps: t.add.steps(a, b, sum),
            tip: common.tip
          };
        }
      } else if (selectedType === 'sub') {
        // Subtraction
        const a = Math.floor(Math.random() * (10 * targetGrade)) + 10;
        const b = Math.floor(Math.random() * a) + 1;
        const diff = a - b;
        if (isWordProblem) {
          const name = t.names[Math.floor(Math.random() * t.names.length)];
          const item = t.items[Math.floor(Math.random() * t.items.length)];
          problem = {
            question: t.sub.q(name, a, b, item),
            answer: diff,
            type: c.word,
            explanation: t.sub.exp(item, a, b),
            steps: t.sub.steps(a, b, diff),
            tip: t.common.tip
          };
        } else {
          problem = {
            question: `${qpfx} ${a} − ${b}?`,
            answer: diff,
            type: c.subtraction,
            explanation: exps.Subtraction,
            steps: t.sub.steps(a, b, diff),
            tip: common.tip
          };
        }
      } else if (selectedType === 'mul') {
        // Multiplication
        const a = Math.floor(Math.random() * (5 * targetGrade)) + 1;
        const b = Math.floor(Math.random() * 10) + 1;
        const prod = a * b;
        if (isWordProblem && targetGrade > 1) {
          const name = t.names[Math.floor(Math.random() * t.names.length)];
          const item = t.items[Math.floor(Math.random() * t.items.length)];
          problem = {
            question: t.mul.q(name, a, b, item),
            answer: prod,
            type: c.word,
            explanation: t.mul.exp(),
            steps: t.mul.steps(a, b, prod),
            tip: t.common.tip
          };
        } else {
          problem = {
            question: `${qpfx} ${a} × ${b}?`,
            answer: prod,
            type: c.multiplication,
            explanation: exps.Multiplication,
            steps: t.mul.steps(a, b, prod),
            tip: common.tip
          };
        }
      } else {
        // Division
        const b = Math.floor(Math.random() * 10) + 1;
        const answer = Math.floor(Math.random() * (5 * targetGrade)) + 1;
        const a = b * answer;
        if (isWordProblem && targetGrade > 1) {
          const name = t.names[Math.floor(Math.random() * t.names.length)];
          const item = t.items[Math.floor(Math.random() * t.items.length)];
          problem = {
            question: t.div.q(name, a, b, item),
            answer: answer,
            type: c.word,
            explanation: t.div.exp(),
            steps: t.div.steps(a, b, answer),
            tip: t.common.tip
          };
        } else {
          problem = {
            question: `${qpfx} ${a} ÷ ${b}?`,
            answer: answer,
            type: c.division,
            explanation: exps.Division,
            steps: t.div.steps(a, b, answer),
            tip: common.tip
          };
        }
      }
      problems.push({ ...problem, id: i + 1 });
    }
    return problems;
  }, [userData?.progress?.currentGrade]);


  const loadUserData = useCallback(async () => {
    if (!userId) {
      console.warn('⚠️ No userId provided, skipping data load');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🔥 Fetching data from Firebase Realtime Database for user:', userId);

      // Fetch all user data via central db service
      const allUserData = await getUser(userId);
      console.log('✅ Firebase Data Loaded:', allUserData);

      // Handle case where user exists in Auth but has no data in DB yet
      if (!allUserData) {
        console.warn('⚠️ No data found for user in database');
        setUserData({ profile: { name: 'New User', avatar: 'panda' }, progress: {} });
        setGradeStats({});
        setSessions([]);
        setLoading(false);
        return;
      }

      // Extract profile data from Firebase (new structure: profile is at root level)
      const profileData = allUserData?.profile || {
        name: 'MathPanda',
        age: 7,
        avatar: 'panda',
        subscription: 'basic',
        dailyPlaytime: 10
      };
      console.log('👤 Profile Data:', profileData);

      // Dashboard only shows Expert mode data (not Basic or Test)
      // Expert subscription data is under 'expert/grades'
      const gradesData = allUserData?.expert?.grades || {};
      const grades = Object.keys(gradesData).filter(key => key.startsWith('grade'));
      console.log('📊 Found Expert grades:', grades);

      // Progress data is now at root level with new structure
      const currentGrade = allUserData?.progress?.currentGrade || grades.length || 1;
      const progressData = allUserData?.progress || {
        currentGrade: currentGrade,
        totalXP: 0,
        totalTimeSpent: 0,
        lastPlayed: null,
        gradeStatuses: {}
      };
      console.log('📈 Progress Data:', progressData);

      setUserData({ profile: profileData, progress: progressData });

      const lastPlayedTime = progressData?.lastPlayed;
      if (lastPlayedTime) {
        const storedLastSeen = localStorage.getItem(`mathzoo_seen_played_${userId}`);
        if (activeTab === 'insights') {
          setHasNewDataNotification(false);
          localStorage.setItem(`mathzoo_seen_played_${userId}`, lastPlayedTime);
        } else if (!storedLastSeen) {
          localStorage.setItem(`mathzoo_seen_played_${userId}`, lastPlayedTime);
          setHasNewDataNotification(false);
        } else if (new Date(lastPlayedTime) > new Date(storedLastSeen)) {
          setHasNewDataNotification(true);
        } else {
          setHasNewDataNotification(false);
        }
      } else {
        setHasNewDataNotification(false);
      }


      // Extract stats from DB (source of truth) and sessions for detailed views
      if (gradesData && typeof gradesData === 'object') {
        const sessionsList = [];
        const statsFromDB = {};

        // Iterate through each grade (grade1, grade2, grade3, etc.)
        console.log('🔍 Processing grades from wrapper:', Object.keys(gradesData));
        Object.keys(gradesData).forEach(gradeKey => {
          if (!gradeKey.startsWith('grade')) return;

          const gradeNum = parseInt(gradeKey.replace('grade', ''), 10);
          const gradeData = gradesData[gradeKey];
          if (!gradeData) return;

          // Store grade-level stats from DB
          statsFromDB[gradeNum] = {
            stats: gradeData.stats || {},
            difficulties: {}
          };

          // Iterate through each difficulty level (easy, medium, hard)
          ['easy', 'medium', 'hard'].forEach(difficultyKey => {
            const difficultyData = gradeData[difficultyKey];
            if (!difficultyData) return;

            // Store difficulty-level stats from DB (source of truth)
            statsFromDB[gradeNum].difficulties[difficultyKey] = {
              stats: difficultyData.stats || {},
              sessionCount: difficultyData.sessions ? Object.keys(difficultyData.sessions).length : 0
            };

            // Also extract sessions for detailed question-level views
            if (difficultyData.sessions && typeof difficultyData.sessions === 'object') {
              Object.keys(difficultyData.sessions).forEach(sessionKey => {
                const session = difficultyData.sessions[sessionKey];
                if (session && typeof session === 'object') {
                  sessionsList.push({
                    ...session,
                    grade: gradeNum,
                    difficulty: difficultyKey,
                    sessionId: sessionKey
                  });
                }
              });
            }
          });
        });

        console.log('📊 Stats from DB:', statsFromDB);
        console.log('📝 Sessions for details:', sessionsList.length);

        setGradeStats(statsFromDB);
        setSessions(sessionsList);
      } else {
        console.warn('No user data found');
        setGradeStats({});
        setSessions([]);
      }

      setLoading(false);
      console.log('✅ Data loading completed successfully');
    } catch (error) {
      console.error('❌ Error loading data:', error);
      setLoading(false);
    }
  }, [userId, activeTab]);

  useEffect(() => {
    // Guard: skip if we already loaded data for this exact userId.
    // handleLogin + onAuthStateChanged both set the same userId, which causes
    // loadUserData to fire twice. The ref ensures only the first call proceeds.
    if (userId && userId !== loadedUserIdRef.current) {
      loadedUserIdRef.current = userId;
      loadUserData();
    }
  }, [userId, loadUserData]);

  // Payment success/cancel handler
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (sessionId && success) {
      setShowPaymentSuccess(true);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Hide message after 5 seconds
      setTimeout(() => setShowPaymentSuccess(false), 5000);
      // Reload user data to get updated subscription status
      loadUserData();
    } else if (canceled) {
      setPaymentError('Payment was canceled. You can try again anytime.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loadUserData]);

  // Handle Upgrade to PRO
  const handleUpgradeToPro = async () => {
    if (!userId) {
      setPaymentError('Please log in to upgrade');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      // Use relative path for production (Vercel), or REACT_APP_API_URL for local dev
      let apiUrl = process.env.REACT_APP_API_URL || '';
      if (apiUrl.endsWith('/')) {
        apiUrl = apiUrl.slice(0, -1);
      }
      const response = await fetch(`${apiUrl}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify({
          email: userData?.profile?.parentEmail || 'user@example.com',
          successUrl: `${window.location.origin}?session_id={CHECKOUT_SESSION_ID}&success=true`,
          cancelUrl: `${window.location.origin}?canceled=true`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError('Failed to start checkout. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  // Handle Manage Subscription (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const handleManageSubscription = async () => {
    if (!userId) {
      alert('Please log in to manage subscription');
      return;
    }

    setIsProcessingPayment(true);
    try {
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify({
          returnUrl: window.location.origin,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Portal error:', error);
      alert('Failed to open subscription management. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Handle Generate AI Report
  const handleGenerateReport = async (forceNew = false) => {
    if (!userId || !isPro) {
      alert('PRO subscription required to generate reports');
      return;
    }

    // Calculate advanced metrics locally
    const advancedMetrics = (() => {
      if (!sessions || sessions.length === 0) return {};
      const latestSession = sessions[sessions.length - 1];
      const metrics = latestSession.learningMetrics || {};

      const velocitySum = sessions.reduce((acc, s) => acc + (s.learningMetrics?.learningVelocity || 0), 0);
      const persistenceSum = sessions.reduce((acc, s) => acc + (s.learningMetrics?.persistenceScore || 0), 0);
      const avgVelocity = sessions.length ? (velocitySum / sessions.length).toFixed(2) : 0;
      const avgPersistence = sessions.length ? (persistenceSum / sessions.length).toFixed(1) : 0;

      const getMode = (arr) => {
        if (arr.length === 0) return null;
        const modeMap = {};
        let maxEl = arr[0], maxCount = 1;
        for (let i = 0; i < arr.length; i++) {
          let el = arr[i];
          if (modeMap[el] == null) modeMap[el] = 1;
          else modeMap[el]++;
          if (modeMap[el] > maxCount) {
            maxEl = el;
            maxCount = modeMap[el];
          }
        }
        return maxEl;
      };

      const dominantStyles = sessions.map(s => s.learningMetrics?.dominantResponseStyle).filter(Boolean);
      const mostFrequentStyle = getMode(dominantStyles) || 'Balanced';

      return {
        concentrationLevel: metrics.concentrationLevel || 'normal',
        masteryLevel: metrics.masteryLevel || 'developing',
        focusScore: metrics.focusScore || 0,
        accuracyTrend: metrics.accuracyTrend || 'stable',
        speedTrend: metrics.speedTrend || 'steady',
        learningVelocity: parseFloat(avgVelocity),
        persistenceScore: parseFloat(avgPersistence),
        dominantResponseStyle: mostFrequentStyle,
        sessionQuality: metrics.sessionQuality || 'good',
        improvementTrend: metrics.improvementTrend || 'stable',
        skillsData: radarData // Pass the pre-calculated radar scores directly
      };
    })();

    // If report is already generated and we are not forcing a new one, download it but inject local metrics
    if (generatedReport && !forceNew) {
      setIsGeneratingReport(true);
      await generatePerformanceReport(generatedReport, userData, advancedMetrics, sessions);
      setIsGeneratingReport(false);
      setHasDownloadedLatestReport(true);
      try {
        localStorage.setItem(`mz_report_downloaded_${userId}`, 'true');
      } catch (e) {
        console.error(e);
      }
      return;
    }

    try {
      setIsGeneratingReport(true);

      // Pre-calculate behavioral stats for consistency with dashboard
      const behavioralStats = {
        focused_pct: sessionQualityData.find(d => d.name === 'Focused Sessions')?.value || 0,
        rushed_pct: sessionQualityData.find(d => d.name === 'Rushed Sessions')?.value || 0,
        distracted_pct: sessionQualityData.find(d => d.name === 'Distracted Sessions')?.value || 0
      };

      // Prepare data for API
      const reportPayload = {
        userData: {
          profile: userData.profile,
          progress: userData.progress
        },
        sessions: sessions.slice(-50), // Send last 50 sessions context
        gradeStats,
        selectedGrade,
        skillsData: radarData, // Explicitly pass the dashboard's calculated skills data
        behavioralStats, // Add this for consistency
        advancedMetrics: advancedMetrics // Use the locally calculated metrics
      };

      // Use relative path for production, or REACT_APP_API_URL/localhost:3002 for local dev
      let apiUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3002' : '');
      if (apiUrl.endsWith('/')) {
        apiUrl = apiUrl.slice(0, -1);
      }

      const response = await fetch(`${apiUrl}/api/generate-ai-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify(reportPayload)
      });

      if (response.status === 429) {
        const errData = await response.json().catch(() => ({}));
        setRateLimitInfo({ type: 'report', resetAt: errData.resetAt || null });
        setIsGeneratingReport(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || 'Failed to generate report');
      }

      const reportData = await response.json();
      if (!reportData.createdAt) {
        reportData.createdAt = new Date().toISOString();
      }
      setGeneratedReport(reportData);
      setHasDownloadedLatestReport(false);
      try {
        localStorage.setItem(`mz_report_downloaded_${userId}`, 'false');
      } catch (e) {
        console.error(e);
      }
      incrementReportsCount();
      setIsGeneratingReport(false);
      trackEvent('generate_report');

    } catch (error) {
      console.error('Report generation error:', error);
      alert(`Report generation failed: ${error.message}`);
      setIsGeneratingReport(false);
    }
  };



  const calculateAnalytics = () => {
    if (!sessions.length) return null;

    // Use stats from DB as source of truth
    let totalCorrect = 0, totalWrong = 0, totalSkipped = 0;
    let totalTimeSpent = 0, totalXPEarned = 0;
    const gradeStatsCalc = {};
    const difficultyStatsCalc = {};

    // Aggregate from gradeStats (DB source of truth)
    Object.entries(gradeStats).forEach(([gradeNum, gradeData]) => {
      const grade = parseInt(gradeNum, 10);

      // Get grade-level stats (totalTimeSeconds and totalXP are at grade level)
      const gradeStats = gradeData.stats || {};
      const gradeTimeSpent = gradeStats.totalTimeSeconds || 0;
      const gradeXP = gradeStats.totalXP || 0;

      gradeStatsCalc[grade] = { correct: 0, wrong: 0, skipped: 0, total: 0, timeSpent: gradeTimeSpent, xpEarned: gradeXP };

      // Accumulate time and XP from grade-level stats
      totalTimeSpent += gradeTimeSpent;
      totalXPEarned += gradeXP;

      Object.entries(gradeData.difficulties || {}).forEach(([difficulty, diffData]) => {
        const stats = diffData.stats || {};
        const correct = stats.totalCorrect || 0;
        const wrong = stats.totalWrong || 0;
        const skipped = stats.totalSkipped || 0;
        const total = correct + wrong + skipped;

        // Accumulate grade totals (correct/wrong/skipped from difficulty level)
        gradeStatsCalc[grade].correct += correct;
        gradeStatsCalc[grade].wrong += wrong;
        gradeStatsCalc[grade].skipped += skipped;
        gradeStatsCalc[grade].total += total;

        // Accumulate difficulty totals
        if (!difficultyStatsCalc[difficulty]) {
          difficultyStatsCalc[difficulty] = { correct: 0, wrong: 0, skipped: 0, total: 0 };
        }
        difficultyStatsCalc[difficulty].correct += correct;
        difficultyStatsCalc[difficulty].wrong += wrong;
        difficultyStatsCalc[difficulty].skipped += skipped;
        difficultyStatsCalc[difficulty].total += total;

        // Accumulate overall totals
        totalCorrect += correct;
        totalWrong += wrong;
        totalSkipped += skipped;
      });
    });

    const totalQuestions = totalCorrect + totalWrong + totalSkipped;

    // totalTimeSpent is in seconds from DB
    const totalTime = totalTimeSpent;
    const totalXP = totalXPEarned;
    const avgResponseTime = totalTime > 0 && totalQuestions > 0 ? totalTime / totalQuestions : 0;

    // Count completed sessions (isCompleted = true in metadata)
    const completedSessions = sessions.filter(s => s.metadata?.isCompleted === true).length;
    const inProgressSessions = sessions.length - completedSessions;

    return {
      totalQuestions,
      correctAnswers: totalCorrect,
      wrongAnswers: totalWrong,
      skippedQuestions: totalSkipped,
      accuracy: totalQuestions > 0 ? (totalCorrect / totalQuestions * 100).toFixed(1) : 0,
      avgResponseTime: avgResponseTime.toFixed(1),
      totalXP,
      totalSessions: sessions.length,
      completedSessions,
      inProgressSessions,
      gradeStats: gradeStatsCalc,
      difficultyStats: difficultyStatsCalc
    };
  };

  const analytics = calculateAnalytics();

  const fetchPracticeProblems = useCallback(async (lang = 'en') => {
    if (!isPro) {
      setGeneratedProblems(generatePracticeProblems(3, lang));
      setProblemsLanguage(lang);
      setProblemsFromAPI(false);
      return;
    }

    setIsGeneratingPractice(true);
    setProblemsLanguage(lang);

    try {
      // Find the user's current active grade
      const targetGrade = parseInt(userData?.progress?.currentGrade || 1);

      // Determine their target difficulty level by finding the first one they haven't "mastered" (< 80% accuracy)
      const currentGradeSessions = sessions.filter(s => parseInt(s.grade) === targetGrade);
      const difficulties = ['easy', 'medium', 'hard'];
      let latestDifficulty = 'easy';

      for (const diff of difficulties) {
        const diffSessions = currentGradeSessions.filter(s => s.difficulty.toLowerCase() === diff);
        const accuracy = diffSessions.length > 0
          ? (diffSessions.reduce((acc, s) => acc + (s.summary?.accuracyPercent || 0), 0) / diffSessions.length)
          : 0;

        if (accuracy < 80) {
          latestDifficulty = diff;
          break;
        }
        latestDifficulty = diff; // If all mastered, stay on Hard
      }

      // Compute real accuracy for the current grade (used in payload + UI)
      const allCurrentGradeAccuracies = currentGradeSessions
        .map(s => s.summary?.accuracyPercent || 0);
      const realAccuracy = allCurrentGradeAccuracies.length > 0
        ? Math.round(allCurrentGradeAccuracies.reduce((a, b) => a + b, 0) / allCurrentGradeAccuracies.length)
        : 50;

      console.log(`📊 [Difficulty Detection] Calculated Level: ${targetGrade}, Difficulty: ${latestDifficulty}, Accuracy: ${realAccuracy}%`);

      const currentGradeConfig = {
        grade: `Level ${targetGrade}`,
        difficulty: latestDifficulty,
        accuracy: realAccuracy,
      };

      // Unified behavioralStats — same path used in auto and button paths
      const sharedBehavioralStats = {
        focused_pct:    sessionQualityData.find(d => d.name === 'Focused Sessions')?.value    || 0,
        rushed_pct:     sessionQualityData.find(d => d.name === 'Rushed Sessions')?.value     || 0,
        distracted_pct: sessionQualityData.find(d => d.name === 'Distracted Sessions')?.value || 0,
      };

      const payload = {
        language: lang,
        targetGrade: targetGrade,
        count: 9,
        weakestGrade: currentGradeConfig,
        behavioralStats: sharedBehavioralStats,
        mostWrongAnswers: getMostWrongAnswers().slice(0, 5),
      };

      let apiUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3002' : '');
      if (apiUrl.endsWith('/')) {
        apiUrl = apiUrl.slice(0, -1);
      }
      console.log(`🚀[API Call] Fetching practice for Grade ${targetGrade}, Difficulty: ${latestDifficulty}, Lang: ${lang}`);
      console.log(`   URL: ${apiUrl}/api/generate-practice`);

      const authHeaderObj = await authHeaders();
      if (!authHeaderObj.Authorization && window.location.hostname === 'localhost') {
        console.log('⚠️ [Local Dev Notice] No auth token available — using local practice problem generation.');
        setGeneratedProblems(generatePracticeProblems(9, lang));
        setProblemsFromAPI(false);
        setIsGeneratingPractice(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/generate-practice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaderObj,
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 429) {
        const errData = await response.json().catch(() => ({}));
        setRateLimitInfo({ type: 'practice', resetAt: errData.resetAt || null });
        // Fall back to local problems so generatedProblems.length > 0 and the
        // auto-load useEffect doesn't immediately re-fire and loop forever.
        setGeneratedProblems(generatePracticeProblems(9, lang));
        setProblemsFromAPI(false);
        setIsGeneratingPractice(false);
        return;
      }

      if (response.status === 401 || response.status === 403) {
        console.warn(`⚠️ [API Auth Notice] Status ${response.status} - Using local practice problem generation.`);
        setGeneratedProblems(generatePracticeProblems(9, lang));
        setProblemsFromAPI(false);
        setIsGeneratingPractice(false);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌[API Error] Status ${response.status}:`, errorText);
        throw new Error('Failed to generate practice problems');
      }

      const data = await response.json();
      console.log('✅[API Success] Received problems:', data.length);
      setGeneratedProblems(data);
      setProblemsFromAPI(true);
      trackEvent('generate_practice', { count: data.length, language: lang });
    } catch (error) {
      console.error('❌[Fetch Error] Error fetching practice problems:', error);
      console.log('⚠️ Falling back to local generation...');
      setGeneratedProblems(generatePracticeProblems(9, lang));
      setProblemsFromAPI(false);
    } finally {
      setIsGeneratingPractice(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, userData, sessions]);

  // NOTE: The old sanitizer/re-classifier useEffect was removed.
  // The server (api/generate-practice.js) now produces fully deterministic, correct
  // type labels (English canonical), explanations, steps, and questions.
  // Any client-side re-processing was fighting the server output and causing
  // wrong explanations (e.g. "Subtract" on Addition cards) and wrong type labels.

  // Track the previous tab to distinguish between tab switch and internal interaction
  const lastActiveTabRef = useRef(activeTab);
  const lastInteractionRef = useRef("");

  // Handle scroll stability for AI Tutor tab - prevent "jumping" away from the action area during updates
  useEffect(() => {
    // Determine if the tab just changed to aitutor
    const hasJustEnteredTab = lastActiveTabRef.current !== 'aitutor' && activeTab === 'aitutor';
    lastActiveTabRef.current = activeTab;

    if (activeTab === 'aitutor' && generatorRef.current && !loading) {
      const stateKey = `${generatedProblems.length}-${problemsLanguage}-${isGeneratingPractice}`;

      if (lastInteractionRef.current !== stateKey) {
        lastInteractionRef.current = stateKey;

        // Only trigger scroll stabilization if we were ALREADY on this tab.
        // This ensures that clicking the tab itself stays at the top of the page.
        if (!hasJustEnteredTab) {
          setTimeout(() => {
            if (generatorRef.current) {
              if (!isGeneratingPractice) {
                generatorRef.current.focus({ preventScroll: true });
              }
            }
          }, 50);
        }
      }
    }
  }, [generatedProblems, problemsLanguage, activeTab, loading, isGeneratingPractice]);

  useEffect(() => {
    // Skip if a manual generation (Generate New button) is already in flight.
    if (isManualGenerationRef.current) return;
    // Skip if an auto-load is already running (prevents double-fire from
    // handleLogin + onAuthStateChanged both updating userId, causing two
    // loading→false transitions which each trigger this effect).
    if (isGeneratingPractice) return;
    if (!loading && generatedProblems.length === 0) {
      if (isPro) {
        fetchPracticeProblems(selectedLanguage);
      } else {
        setGeneratedProblems(generatePracticeProblems(3, selectedLanguage));
        setProblemsLanguage(selectedLanguage);
      }
    }
  }, [loading, isPro, generatedProblems.length, isGeneratingPractice, fetchPracticeProblems, generatePracticeProblems, selectedLanguage]);

  console.log('🔍 Dashboard State:', {
    loading,
    hasUserData: !!userData,
    sessionsCount: sessions.length,
    hasAnalytics: !!analytics
  });



  const prepareGradeData = useCallback(() => {
    if (!analytics) return [];
    return Object.entries(analytics.gradeStats).map(([grade, stats]) => ({
      grade: `Level ${grade}`,
      accuracy: stats.total > 0 ? parseFloat((stats.correct / stats.total * 100).toFixed(1)) : 0,
      correct: stats.correct, wrong: stats.wrong, skipped: stats.skipped, total: stats.total
    }));
  }, [analytics]);

  const prepareRadarData = () => {
    if (!sessions || sessions.length === 0) return [];

    // Calculate actual performance per problem type/conceptual area
    const performanceByType = {
      'addition': { correct: 0, total: 0 },
      'subtraction': { correct: 0, total: 0 },
      'multiplication': { correct: 0, total: 0 },
      'division': { correct: 0, total: 0 },
      'fractions': { correct: 0, total: 0 },
      'word_problems': { correct: 0, total: 0 },
      'woordproblemen': { correct: 0, total: 0 }
    };

    let unmatchedCount = 0; // Track questions that don't match any category

    sessions.forEach(session => {
      if (!session.questions) return;

      // Count correct answers
      if (session.questions.correct) {
        Object.values(session.questions.correct).forEach(question => {
          const area = (question.conceptualArea || question.problemType || '').toLowerCase().trim();
          if (performanceByType[area]) {
            performanceByType[area].correct++;
            performanceByType[area].total++;
          } else {
            unmatchedCount++;
          }
        });
      }

      // Count wrong and skipped answers
      ['wrong', 'skipped'].forEach(category => {
        if (session.questions[category]) {
          // For 'wrong', flatten the new nested attempts structure before iterating
          const entries = category === 'wrong'
            ? flattenWrongAttempts(session.questions[category])
            : Object.values(session.questions[category]);
          entries.forEach(question => {
            const area = (question.conceptualArea || question.problemType || '').toLowerCase().trim();
            if (performanceByType[area]) {
              performanceByType[area].total++;
            } else {
              unmatchedCount++;
            }
          });
        }
      });
    });

    // Log if there are unmatched questions
    if (unmatchedCount > 0) {
      console.log(`Skills Radar: ${unmatchedCount} questions didn't match any category`);
    }

    // Merge word problems (English and Dutch)
    performanceByType['word_problems'].correct += performanceByType['woordproblemen'].correct;
    performanceByType['word_problems'].total += performanceByType['woordproblemen'].total;

    // Log performance data for debugging
    console.log('Skills Radar Performance Data:', {
      addition: performanceByType['addition'],
      subtraction: performanceByType['subtraction'],
      multiplication: performanceByType['multiplication'],
      division: performanceByType['division'],
      fractions: performanceByType['fractions'],
      word_problems: performanceByType['word_problems']
    });

    // Dynamic Benchmark data based on age
    const age = userData?.profile?.age || 7;
    const benchmarkData = {
      'Addition': age >= 7 ? 85 : 70,
      'Subtraction': age >= 8 ? 80 : 65,
      'Multiplication': age >= 9 ? 75 : 50,
      'Division': age >= 10 ? 70 : 40,
      'Fractions': age >= 10 ? 65 : 30,
      'Word Problems': age >= 8 ? 75 : 60
    };

    // Calculate accuracy percentage for each type
    const calculateAccuracy = (stats) => {
      // If no attempts, default to a "developing" score (e.g., 20) instead of 0 for better chart visual, or keep 0
      return stats && stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
    };

    const radarResult = [
      { subject: 'Addition', child: calculateAccuracy(performanceByType['addition']), peer: benchmarkData['Addition'], attempts: performanceByType['addition']?.total || 0, fullMark: 100 },
      { subject: 'Subtraction', child: calculateAccuracy(performanceByType['subtraction']), peer: benchmarkData['Subtraction'], attempts: performanceByType['subtraction']?.total || 0, fullMark: 100 },
      { subject: 'Multiplication', child: calculateAccuracy(performanceByType['multiplication']), peer: benchmarkData['Multiplication'], attempts: performanceByType['multiplication']?.total || 0, fullMark: 100 },
      { subject: 'Division', child: calculateAccuracy(performanceByType['division']), peer: benchmarkData['Division'], attempts: performanceByType['division']?.total || 0, fullMark: 100 },
      { subject: 'Fractions', child: calculateAccuracy(performanceByType['fractions']), peer: benchmarkData['Fractions'], attempts: performanceByType['fractions']?.total || 0, fullMark: 100 },
      { subject: 'Word Problems', child: calculateAccuracy(performanceByType['word_problems']), peer: benchmarkData['Word Problems'], attempts: performanceByType['word_problems']?.total || 0, fullMark: 100 }
    ];

    console.log('Skills Radar Result:', radarResult);
    return radarResult;
  };

  // NEW: Extract detailed question-level analytics
  const getQuestionLevelAnalytics = () => {
    if (!sessions.length) return null;

    const analytics = {
      byConceptualArea: {},
      byErrorType: {},
      needsReview: [],
      masteryLevels: {},
      challengingQuestions: []
    };

    sessions.forEach(session => {
      ['wrong', 'skipped'].forEach(category => {
        if (!session.questions || !session.questions[category]) return;

        // For 'wrong', use flattenWrongAttempts to handle the new nested attempts structure.
        // For 'skipped', keep the existing flat iteration.
        const entries = category === 'wrong'
          ? flattenWrongAttempts(session.questions[category])
          : Object.values(session.questions[category]);

        entries.forEach(question => {
          // Group by conceptual area
          const area = question.conceptualArea || 'unknown';
          if (!analytics.byConceptualArea[area]) {
            analytics.byConceptualArea[area] = { total: 0, wrong: 0, skipped: 0, questions: [] };
          }
          analytics.byConceptualArea[area].total++;
          analytics.byConceptualArea[area][category]++;
          analytics.byConceptualArea[area].questions.push(question);

          // Group by error type
          if (question.errorType) {
            if (!analytics.byErrorType[question.errorType]) {
              analytics.byErrorType[question.errorType] = { count: 0, questions: [] };
            }
            analytics.byErrorType[question.errorType].count++;
            analytics.byErrorType[question.errorType].questions.push(question);
          }

          // Track needs review
          if (question.needsReview) {
            analytics.needsReview.push({
              ...question,
              category,
              grade: session.grade,
              difficulty: session.difficulty
            });
          }

          // Track mastery levels
          const mastery = question.masteryIndicator || 'unknown';
          analytics.masteryLevels[mastery] = (analytics.masteryLevels[mastery] || 0) + 1;

          // Track challenging questions (high retry priority)
          if (question.retryPriority === 'high' || question.retryPriority === 'medium') {
            analytics.challengingQuestions.push({
              ...question,
              category,
              grade: session.grade,
              difficulty: session.difficulty
            });
          }
        });
      });
    });

    return analytics;
  };

  // Prepare historical accuracy trend data with retake comparison
  const prepareHistoricalAccuracyTrends = (grade, difficulty) => {
    const relevantSessions = sessions.filter(s =>
      String(s.grade) === String(grade) && s.difficulty.toLowerCase() === difficulty.toLowerCase()
    ).sort((a, b) => {
      const dateA = new Date(a.metadata?.startedAt || a.startTime || 0);
      const dateB = new Date(b.metadata?.startedAt || b.startTime || 0);
      return dateA - dateB;
    });

    if (relevantSessions.length === 0) return [];

    return relevantSessions.map((session, index) => {
      const correct = session.summary?.totalCorrect || 0;
      const total = session.summary?.totalQuestions || 0;
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
      const date = new Date(session.metadata?.startedAt || session.startTime);

      return {
        attemptNumber: index + 1,
        accuracy,
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        timestamp: date.getTime(),
        totalQuestions: total,
        correct,
        wrong: session.summary?.totalWrong || 0,
        skipped: total - correct - (session.summary?.totalWrong || 0)
      };
    });
  };

  // Analyze time-of-day performance patterns
  const analyzeTimeOfDayPerformance = () => {
    if (!sessions.length) return null;

    const timeSlots = {
      morning: { sessions: [], correct: 0, total: 0 }, // 6am-12pm
      afternoon: { sessions: [], correct: 0, total: 0 }, // 12pm-5pm
      evening: { sessions: [], correct: 0, total: 0 }, // 5pm-9pm
      night: { sessions: [], correct: 0, total: 0 } // 9pm-6am
    };

    sessions.forEach(session => {
      const timestamp = session.metadata?.startedAt || session.startTime;
      if (!timestamp) return;

      const date = new Date(timestamp);
      const hour = date.getHours();

      let slot;
      if (hour >= 6 && hour < 12) slot = 'morning';
      else if (hour >= 12 && hour < 17) slot = 'afternoon';
      else if (hour >= 17 && hour < 21) slot = 'evening';
      else slot = 'night';

      const correct = session.summary?.totalCorrect || 0;
      const total = session.summary?.totalQuestions || 0;

      timeSlots[slot].sessions.push(session);
      timeSlots[slot].correct += correct;
      timeSlots[slot].total += total;
    });

    // Calculate accuracy for each time slot
    const results = Object.entries(timeSlots).map(([slot, data]) => ({
      slot,
      accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
      sessionCount: data.sessions.length,
      totalQuestions: data.total
    })).filter(r => r.sessionCount > 0);

    // Find best performing time slot
    const best = results.reduce((best, curr) =>
      curr.accuracy > best.accuracy ? curr : best
      , results[0] || { slot: 'morning', accuracy: 0, sessionCount: 0 });

    return { timeSlots: results, bestSlot: best };
  };

  const prepareSessionQualityData = () => {
    if (!analytics || !sessions.length) return [];
    let focusedCount = 0, rushedCount = 0, distractedCount = 0;
    sessions.forEach(session => {
      const duration = session.summary?.totalTimeSpent || 0;
      const questions = session.summary?.totalQuestions || 0;
      if (questions > 0) {
        const avgTimePerQuestion = duration / questions;
        if (avgTimePerQuestion >= 5 && avgTimePerQuestion <= 15) focusedCount++;
        else if (avgTimePerQuestion < 5) rushedCount++;
        else distractedCount++;
      }
    });
    const total = focusedCount + rushedCount + distractedCount;
    if (total === 0) return [];
    return [
      { name: 'Focused Sessions', value: Math.round((focusedCount / total) * 100), color: appColors.success, count: focusedCount, benchmark: 68 },
      { name: 'Rushed Sessions', value: Math.round((rushedCount / total) * 100), color: appColors.warning, count: rushedCount, benchmark: 22 },
      { name: 'Distracted Sessions', value: Math.round((distractedCount / total) * 100), color: appColors.error, count: distractedCount, benchmark: 10 }
    ];
  };

  const normalizeProblemType = (type) => {
    if (!type) return 'Unknown';

    const typeMap = {
      'word_problems': 'Word Problems',
      'woordproblemen': 'Word Problems',
      'словесни задачи': 'Word Problems',
      'събиране': 'Addition',
      'addition': 'Addition',
      'изваждане': 'Subtraction',
      'subtraction': 'Subtraction',
      'multiplication': 'Multiplication',
      'умножение': 'Multiplication',
      'division': 'Division',
      'деление': 'Division'
    };

    const normalized = typeMap[type.toLowerCase()] || type;
    return normalized;
  };

  const prepareDetailedGradePerformance = () => {
    if (Object.keys(gradeStats).length === 0) return {};
    const gradeStructure = {};

    // Build structure from DB stats keys
    Object.entries(gradeStats).forEach(([gradeNum]) => {
      const grade = parseInt(gradeNum, 10);
      gradeStructure[grade] = {
        easy: { total: 0, correct: 0, wrong: 0, skipped: 0, categories: {} },
        medium: { total: 0, correct: 0, wrong: 0, skipped: 0, categories: {} },
        hard: { total: 0, correct: 0, wrong: 0, skipped: 0, categories: {} }
      };
    });

    // Add category breakdown and dynamic column-level totals from sessions
    sessions.forEach(session => {
      const grade = session.grade;
      const difficulty = session.difficulty?.toLowerCase();

      if (!gradeStructure[grade] || !gradeStructure[grade][difficulty]) return;

      // Extract category-level data from questions
      if (session.questions) {
        ['correct', 'wrong', 'skipped'].forEach(category => {
          if (!session.questions[category]) return;

          // For 'wrong', flatten the new nested attempts structure before iterating
          const entries = category === 'wrong'
            ? flattenWrongAttempts(session.questions[category])
            : Object.values(session.questions[category]);

          entries.forEach(question => {
            const problemType = normalizeProblemType(question.conceptualArea || question.problemType || 'general');

            if (!gradeStructure[grade][difficulty].categories[problemType]) {
              gradeStructure[grade][difficulty].categories[problemType] = {
                total: 0,
                correct: 0,
                wrong: 0,
                skipped: 0
              };
            }

            // Increment category-level counters
            gradeStructure[grade][difficulty].categories[problemType].total += 1;
            gradeStructure[grade][difficulty].categories[problemType][category] += 1;

            // Increment column-level counters to keep them mathematically in sync
            gradeStructure[grade][difficulty].total += 1;
            gradeStructure[grade][difficulty][category] += 1;
          });
        });
      }
    });

    return gradeStructure;
  };

  // Flatten the new wrong-node structure into individual attempt objects.
  // New DB shape: questions.wrong[questionId] = { 1: {attemptNumber, questionText, ...}, 2: {...}, ... }
  // Returns a flat array of attempt objects each containing the question data.
  const flattenWrongAttempts = (wrongNode) => {
    if (!wrongNode || typeof wrongNode !== 'object') return [];
    const attempts = [];
    Object.values(wrongNode).forEach(questionEntry => {
      if (!questionEntry || typeof questionEntry !== 'object') return;
      // If it looks like an attempts map (keys are numeric strings "1","2",...), flatten them
      const keys = Object.keys(questionEntry);
      const isAttemptsMap = keys.length > 0 && keys.every(k => /^\d+$/.test(k));
      if (isAttemptsMap) {
        Object.values(questionEntry).forEach(attempt => {
          if (attempt && typeof attempt === 'object') attempts.push(attempt);
        });
      } else {
        // Legacy flat structure: the entry itself is the question object
        attempts.push(questionEntry);
      }
    });
    return attempts;
  };

  const getMostWrongAnswers = () => {
    if (!sessions.length) return [];
    const wrongQuestions = {};

    sessions.forEach(session => {
      // Process wrong answers from new data structure (each question has numbered attempts)
      if (session.questions && session.questions.wrong) {
        flattenWrongAttempts(session.questions.wrong).forEach(attempt => {
          const qKey = attempt.questionText || attempt.question || 'Unknown Question';
          if (!wrongQuestions[qKey]) {
            wrongQuestions[qKey] = {
              questionText: qKey,
              correctAnswer: attempt.correctAnswer,
              problemType: attempt.conceptualArea || attempt.problemType || 'general',
              grade: session.grade,
              difficulty: session.difficulty,
              wrongCount: 0,
              skipCount: 0,
              totalAttempts: 0,
              selectedAnswers: [],
              errorType: attempt.errorType || 'Concept Error',
              masteryIndicator: attempt.masteryIndicator || 'focus_needed',
              remediationSuggestion: attempt.remediationSuggestion || 'Needs step-by-step review',
              needsReview: attempt.needsReview !== undefined ? attempt.needsReview : true
            };
          }
          wrongQuestions[qKey].wrongCount++;
          wrongQuestions[qKey].totalAttempts++;
          if (attempt.userAnswer || attempt.selectedAnswer) {
            wrongQuestions[qKey].selectedAnswers.push(attempt.userAnswer || attempt.selectedAnswer);
          }
        });
      }

      // Process skipped answers as challenges
      if (session.questions && session.questions.skipped) {
        Object.values(session.questions.skipped).forEach(attempt => {
          const qKey = attempt.questionText || attempt.question || 'Unknown Question';
          if (!wrongQuestions[qKey]) {
            wrongQuestions[qKey] = {
              questionText: qKey,
              correctAnswer: attempt.correctAnswer || 'Not answered',
              problemType: attempt.conceptualArea || attempt.problemType || 'general',
              grade: session.grade,
              difficulty: session.difficulty,
              wrongCount: 0,
              skipCount: 0,
              totalAttempts: 0,
              selectedAnswers: ['Skipped'],
              errorType: attempt.errorType || 'Concept Skipped',
              masteryIndicator: attempt.masteryIndicator || 'focus_needed',
              remediationSuggestion: attempt.remediationSuggestion || 'Review basic concept steps',
              needsReview: attempt.needsReview !== undefined ? attempt.needsReview : true
            };
          }
          wrongQuestions[qKey].skipCount++;
          wrongQuestions[qKey].totalAttempts++;
        });
      }

      // Also track correct attempts for questions we've seen as wrong
      if (session.questions && session.questions.correct) {
        Object.values(session.questions.correct).forEach(question => {
          const qKey = question.questionText || question.question || 'Unknown Question';
          if (wrongQuestions[qKey]) {
            wrongQuestions[qKey].totalAttempts++;
          }
        });
      }
    });

    return Object.values(wrongQuestions).sort((a, b) => (b.wrongCount + b.skipCount) - (a.wrongCount + a.skipCount)).slice(0, 10);
  };

  const prepareWeeklyTrendsData = () => {
    if (!sessions.length) return [];
    const weeklyData = {};
    sessions.forEach(session => {
      const sessionDate = new Date(session.metadata?.startedAt || session.startTime);
      const weekNumber = Math.floor((Date.now() - sessionDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
      const weekKey = `W${Math.max(1, 8 - weekNumber)}`;
      const grade = session.grade;
      const difficulty = session.difficulty;
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { correct: 0, wrong: 0, skipped: 0, total: 0, byGrade: {}, byDifficulty: {} };
      }
      // Use session.summary from DB (per-session aggregates)
      const correct = session.summary?.totalCorrect || 0;
      const wrong = session.summary?.totalWrong || 0;
      const skipped = session.summary?.totalSkipped || 0;
      const total = correct + wrong + skipped;
      weeklyData[weekKey].correct += correct;
      weeklyData[weekKey].wrong += wrong;
      weeklyData[weekKey].skipped += skipped;
      weeklyData[weekKey].total += total;
      if (!weeklyData[weekKey].byGrade[grade]) {
        weeklyData[weekKey].byGrade[grade] = { correct: 0, wrong: 0, skipped: 0 };
      }
      weeklyData[weekKey].byGrade[grade].correct += correct;
      weeklyData[weekKey].byGrade[grade].wrong += wrong;
      weeklyData[weekKey].byGrade[grade].skipped += skipped;
      if (!weeklyData[weekKey].byDifficulty[difficulty]) {
        weeklyData[weekKey].byDifficulty[difficulty] = { correct: 0, wrong: 0, skipped: 0 };
      }
      weeklyData[weekKey].byDifficulty[difficulty].correct += correct;
      weeklyData[weekKey].byDifficulty[difficulty].wrong += wrong;
      weeklyData[weekKey].byDifficulty[difficulty].skipped += skipped;
    });
    return Object.entries(weeklyData)
      .map(([week, data]) => ({ week, ...data }))
      .sort((a, b) => parseInt(a.week.slice(1)) - parseInt(b.week.slice(1)))
      .slice(-8);
  };

  const prepareDailyTrendsData = () => {
    if (!sessions.length) return [];
    const dailyData = {};
    sessions.forEach(session => {
      const sessionDate = new Date(session.metadata?.startedAt || session.startTime);
      const dateKey = sessionDate.toISOString().split('T')[0];
      const grade = session.grade;
      const difficulty = session.difficulty;
      if (!dailyData[dateKey]) {
        dailyData[dateKey] = { date: dateKey, correct: 0, wrong: 0, skipped: 0, total: 0, byGrade: {}, byDifficulty: {} };
      }
      // Use session.summary from DB (per-session aggregates)
      const correct = session.summary?.totalCorrect || 0;
      const wrong = session.summary?.totalWrong || 0;
      const skipped = session.summary?.totalSkipped || 0;
      const total = correct + wrong + skipped;
      dailyData[dateKey].correct += correct;
      dailyData[dateKey].wrong += wrong;
      dailyData[dateKey].skipped += skipped;
      dailyData[dateKey].total += total;
      if (!dailyData[dateKey].byGrade[grade]) {
        dailyData[dateKey].byGrade[grade] = { correct: 0, wrong: 0, skipped: 0 };
      }
      dailyData[dateKey].byGrade[grade].correct += correct;
      dailyData[dateKey].byGrade[grade].wrong += wrong;
      dailyData[dateKey].byGrade[grade].skipped += skipped;
      if (!dailyData[dateKey].byDifficulty[difficulty]) {
        dailyData[dateKey].byDifficulty[difficulty] = { correct: 0, wrong: 0, skipped: 0 };
      }
      dailyData[dateKey].byDifficulty[difficulty].correct += correct;
      dailyData[dateKey].byDifficulty[difficulty].wrong += wrong;
      dailyData[dateKey].byDifficulty[difficulty].skipped += skipped;
    });
    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-14);
  };






  // Render Functions


  const calculateTimeMetrics = (grade, difficulty) => {
    const relevantSessions = sessions.filter(s =>
      String(s.grade) === String(grade) && s.difficulty.toLowerCase() === difficulty.toLowerCase()
    );
    const totalTime = relevantSessions.reduce((sum, s) => sum + (s.summary?.totalTimeSpent || 0), 0);
    const avgTime = relevantSessions.length > 0 ? totalTime / relevantSessions.length : 0;
    let focused = 0, rushed = 0, distracted = 0;

    // Count quality metrics at question level using flags from database
    relevantSessions.forEach(session => {
      if (session.questions) {
        // Check all question categories (correct, wrong, skipped)
        ['correct', 'wrong', 'skipped'].forEach(category => {
          if (session.questions[category]) {
            // For 'wrong', flatten the new nested attempts structure before iterating
            const entries = category === 'wrong'
              ? flattenWrongAttempts(session.questions[category])
              : Object.values(session.questions[category]);
            entries.forEach(question => {
              if (question.wasFocused) focused++;
              else if (question.wasRushed) rushed++;
              else if (question.wasDistracted) distracted++;
            });
          }
        });
      }
    });

    // Extract learning metrics from sessions
    const learningMetrics = {
      focusScore: 0,
      accuracyTrend: null,
      speedTrend: null,
      masteryLevel: null,
      concentrationLevel: null
    };

    if (relevantSessions.length > 0) {
      // Average focus score
      const focusScores = relevantSessions
        .filter(s => s.learningMetrics?.focusScore)
        .map(s => s.learningMetrics.focusScore);
      if (focusScores.length > 0) {
        learningMetrics.focusScore = Math.round(
          focusScores.reduce((sum, score) => sum + score, 0) / focusScores.length
        );
      }

      // Get latest trends
      const latestSession = relevantSessions[relevantSessions.length - 1];
      if (latestSession.learningMetrics) {
        learningMetrics.accuracyTrend = latestSession.learningMetrics.accuracyTrend;
        learningMetrics.speedTrend = latestSession.learningMetrics.speedTrend;
        learningMetrics.masteryLevel = latestSession.learningMetrics.masteryLevel;
        learningMetrics.concentrationLevel = latestSession.learningMetrics.concentrationLevel;
      }
    }

    return { totalTime, avgTime, sessionCount: relevantSessions.length, focused, rushed, distracted, learningMetrics };
  };

  // Memoize data that shouldn't recalculate when trendsView changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const radarData = useMemo(() => prepareRadarData(), [sessions]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sessionQualityData = useMemo(() => prepareSessionQualityData(), [sessions]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weeklyTrendsData = useMemo(() => prepareWeeklyTrendsData(), [sessions]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dailyTrendsData = useMemo(() => prepareDailyTrendsData(), [sessions]);

  const renderThisWeekTab = () => {
    const childName = userData?.profile?.name || 'MathHippo';
    const age = userData?.profile?.age || 10;
    const currentGrade = parseInt(userData?.progress?.currentGrade || 2, 10);
    const totalXP = userData?.progress?.totalXP || 0;
    const sessionsCount = sessions.length;
    const totalMinutes = Math.round((userData?.progress?.totalTimeSpent || 0) / 60);

    // Dynamic week date label (last 7 days formatted with hyphen, no em-dashes)
    const now = new Date();
    const startOfWeek = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
    const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
    const weekLabel = `WEEK OF ${formatDate(startOfWeek)} - ${formatDate(now)}`;

    // Dynamic calculation of real skill accuracy and focus score from DB sessions
    const skillDescriptions = {
      addition: { label: 'Addition', desc: 'combining numbers efficiently', insight: 'regrouping in tens place' },
      subtraction: { label: 'Subtraction', desc: 'finding differences accurately', insight: 'borrowing across zeros' },
      multiplication: { label: 'Multiplication', desc: 'repeated addition of groups', insight: 'confusing factors and products' },
      division: { label: 'Division', desc: 'sharing into equal groups', insight: 'errors are mostly "off by one"' },
      word_problems: { label: 'Word Problems', desc: 'translating stories to equations', insight: 'errors in multi-step setup' },
      geometry: { label: 'Geometry', desc: 'understanding shapes and angles', insight: 'identifying shape properties' },
      fractions: { label: 'Fractions', desc: 'parts of a whole', insight: 'comparing numerators' }
    };

    const areaStats = {};
    let totalQCount = 0;
    let totalRushedCount = 0;

    if (sessions && sessions.length > 0) {
      sessions.forEach(s => {
        totalQCount += (s.summary?.totalQuestions || 0);
        totalRushedCount += (s.summary?.rushedAnswers || 0);

        if (s.questions?.correct) {
          Object.values(s.questions.correct).forEach(q => {
            const area = (q.conceptualArea || q.problemType || 'addition').toLowerCase().trim();
            if (!areaStats[area]) areaStats[area] = { correct: 0, wrong: 0 };
            areaStats[area].correct++;
          });
        }
        if (s.questions?.wrong) {
          const wrongEntries = flattenWrongAttempts(s.questions.wrong);
          wrongEntries.forEach(q => {
            const area = (q.conceptualArea || q.problemType || 'addition').toLowerCase().trim();
            if (!areaStats[area]) areaStats[area] = { correct: 0, wrong: 0 };
            areaStats[area].wrong++;
          });
        }
        if (s.questions?.skipped) {
          Object.values(s.questions.skipped).forEach(q => {
            const area = (q.conceptualArea || q.problemType || 'addition').toLowerCase().trim();
            if (!areaStats[area]) areaStats[area] = { correct: 0, wrong: 0 };
            areaStats[area].wrong++; // Treat skips as incorrect attempts for accuracy calculation
          });
        }
      });
    }

    if (areaStats.woordproblemen) {
      if (!areaStats.word_problems) areaStats.word_problems = { correct: 0, wrong: 0 };
      areaStats.word_problems.correct += areaStats.woordproblemen.correct;
      areaStats.word_problems.wrong += areaStats.woordproblemen.wrong;
      delete areaStats.woordproblemen;
    }

    // Default stats if DB sessions don't have question-level areas
    if (Object.keys(areaStats).length === 0) {
      // Empty, no mock data fallback to prevent hallucination
    }

    const calculatedSkills = Object.entries(areaStats).map(([key, stat]) => {
      const total = stat.correct + stat.wrong;
      const acc = total > 0 ? Math.round((stat.correct / total) * 100) : 100;
      const meta = skillDescriptions[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), desc: 'core mathematical concepts', insight: 'needs review' };
      return {
        key,
        label: meta.label,
        desc: meta.desc,
        insight: meta.insight,
        accuracy: acc,
        wrongCount: stat.wrong,
        total
      };
    });

    // Sort by accuracy descending
    calculatedSkills.sort((a, b) => b.accuracy - a.accuracy);

    const topSkill1 = calculatedSkills[0] || { label: 'No skills practiced', accuracy: 0 };
    const topSkill2 = calculatedSkills[1] || { label: 'No skills practiced', accuracy: 0 };
    const focusScore = totalQCount > 0 ? Math.max(15, Math.round(((totalQCount - totalRushedCount) / totalQCount) * 100)) : 0;

    // Lowest performing skill for Box 2
    const lowestSkill = [...calculatedSkills].sort((a, b) => a.accuracy - b.accuracy)[0] || {
      label: 'None',
      desc: 'No skills practiced yet',
      insight: 'Keep practicing to see insights',
      accuracy: 0,
      wrongCount: 0
    };

    const getSkillBenchmark = (label, userAge) => {
      const bLabel = (label || '').toLowerCase();
      if (bLabel.includes('addition')) return userAge >= 7 ? 85 : 70;
      if (bLabel.includes('subtraction')) return userAge >= 8 ? 80 : 65;
      if (bLabel.includes('multiplication')) return userAge >= 9 ? 75 : 50;
      if (bLabel.includes('division')) return userAge >= 10 ? 70 : 40;
      if (bLabel.includes('fraction')) return userAge >= 10 ? 65 : 30;
      if (bLabel.includes('word')) return userAge >= 8 ? 75 : 60;
      return 70; // fallback
    };

    const belowBenchmarkSkills = calculatedSkills.filter(skill => {
      const benchmark = getSkillBenchmark(skill.label, age);
      return skill.accuracy < benchmark;
    });

    const strongSkills = calculatedSkills.filter(skill => {
      const benchmark = getSkillBenchmark(skill.label, age);
      return skill.accuracy >= benchmark;
    });

    const useCombinedWideBox = belowBenchmarkSkills.length > 1;

    const divisionAcc = lowestSkill.accuracy;
    const wrongDivisionCount = lowestSkill.wrongCount;

    // Dynamic color: RED only if below benchmark (<58%), BEIGE/GOLD if neutral around benchmark (58-68%), GREEN if above (>68%)
    const isBelowBenchmark = lowestSkill.accuracy < 58;
    const isNeutralBenchmark = lowestSkill.accuracy >= 58 && lowestSkill.accuracy <= 68;
    const box2Color = isBelowBenchmark ? '#F44336' : isNeutralBenchmark ? '#D69E2E' : '#81a550';
    const box2Badge = isBelowBenchmark ? '! Needs help' : isNeutralBenchmark ? '• Near benchmark' : '✓ Strong skill';

    // Select dinner practice for current grade
    const gradeKey = `grade${currentGrade}`;
    const gradeDinnerList = DEFAULT_DINNER_PRACTICES[gradeKey] || DEFAULT_DINNER_PRACTICES.grade2;
    const currentDinnerPractice = gradeDinnerList[dinnerPracticeIndex % gradeDinnerList.length];

    const checkedCount = Object.values(todoChecklist).filter(Boolean).length;
    const minutesLeft = checkedCount === 3 ? 0 : checkedCount === 2 ? 2 : checkedCount === 1 ? 4 : 6;

    // Calculate last played date from DB sessions
    let streakText = 'No play history';
    if (sessions && sessions.length > 0) {
      const validSessions = sessions
        .filter(s => s.metadata?.completedAt || s.metadata?.startedAt)
        .map(s => {
          const timestampStr = s.metadata?.completedAt || s.metadata?.startedAt;
          return {
            ...s,
            parsedDate: new Date(timestampStr)
          };
        })
        .filter(s => !isNaN(s.parsedDate.getTime()))
        .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime());

      if (validSessions.length > 0) {
        const lastSessionDate = validSessions[0].parsedDate;
        const now = new Date();
        
        // Calculate difference in calendar days
        const d1 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const d2 = new Date(lastSessionDate.getFullYear(), lastSessionDate.getMonth(), lastSessionDate.getDate());
        const diffTime = d1.getTime() - d2.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) {
          streakText = 'Last played today';
        } else if (diffDays === 1) {
          streakText = 'Last played yesterday';
        } else {
          streakText = `Last played ${lastSessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        }
      }
    }

    // Recent 3 sessions from DB sorted by date descending (or fallback if empty)
    const sortedDbSessions = [...sessions].sort((a, b) => {
      const dateA = new Date(a.metadata?.completedAt || a.metadata?.startedAt || 0).getTime();
      const dateB = new Date(b.metadata?.completedAt || b.metadata?.startedAt || 0).getTime();
      return dateB - dateA;
    });

    const displaySessions = sortedDbSessions.length > 0 ? sortedDbSessions.slice(0, 3).map((s, idx) => ({
      id: s.sessionId || idx,
      date: new Date(s.metadata?.completedAt || s.metadata?.startedAt || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      level: `Level ${s.grade || currentGrade || 1}`,
      summary: `${(s.difficulty || 'Easy').charAt(0).toUpperCase() + (s.difficulty || 'easy').slice(1)} · ${s.summary?.totalQuestions || 0} questions · ${s.summary?.rushedAnswers > 3 ? 'slightly rushed' : 'focused session'}`,
      duration: `${Math.round((s.summary?.totalTimeSpent || 0) / 60)} min`,
      accuracy: `${Math.round(s.summary?.accuracyPercent || 0)}%`,
      accNum: s.summary?.accuracyPercent || 0
    })) : [];
    // Calculate last 7 weeks accuracy history for the sparkline chart
    let accuracies = [0, 0, 0, 0, 0, 0, 0]; // default empty state to prevent hallucination
    if (sessions && sessions.length > 0) {
      let anchorDate = new Date();
      const validSessionDates = sessions
        .filter(s => s.metadata?.completedAt || s.metadata?.startedAt)
        .map(s => new Date(s.metadata?.completedAt || s.metadata?.startedAt))
        .filter(d => !isNaN(d.getTime()))
        .sort((a, b) => b.getTime() - a.getTime());

      if (validSessionDates.length > 0) {
        anchorDate = validSessionDates[0];
      }

      const anchorStart = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate());
      
      const weeks = Array(7).fill(0).map((_, i) => ({
        weekIndex: i,
        totalQuestions: 0,
        correctQuestions: 0,
      }));

      sessions.forEach(s => {
        const dateStr = s.metadata?.completedAt || s.metadata?.startedAt;
        if (!dateStr) return;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return;

        const d1 = new Date(anchorStart.getFullYear(), anchorStart.getMonth(), anchorStart.getDate()).getTime();
        const d2 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const diffDays = Math.floor((d1 - d2) / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);

        if (diffWeeks >= 0 && diffWeeks < 7) {
          let correctCount = 0;
          let wrongCount = 0;
          let skippedCount = 0;

          if (s.questions?.correct) {
            correctCount = Object.keys(s.questions.correct).length;
          }
          if (s.questions?.wrong) {
            wrongCount = flattenWrongAttempts(s.questions.wrong).length;
          }
          if (s.questions?.skipped) {
            skippedCount = Object.keys(s.questions.skipped).length;
          }

          const totalQ = correctCount + wrongCount + skippedCount;
          const correctQ = correctCount;

          if (totalQ > 0) {
            weeks[diffWeeks].totalQuestions += totalQ;
            weeks[diffWeeks].correctQuestions += correctQ;
          } else {
            // fallback to summary if no detailed questions available
            const totalFallback = s.summary?.totalQuestions || 12;
            const accFallback = s.summary?.accuracyPercent || 80;
            const correctFallback = Math.round(totalFallback * (accFallback / 100));
            
            weeks[diffWeeks].totalQuestions += totalFallback;
            weeks[diffWeeks].correctQuestions += correctFallback;
          }
        }
      });

      const reversedWeeks = [...weeks].reverse().map(w => {
        if (w.totalQuestions === 0) return null;
        return Math.round((w.correctQuestions / w.totalQuestions) * 100);
      });

      const firstValid = reversedWeeks.find(val => val !== null);
      let lastValid = firstValid !== undefined ? firstValid : 75;
      
      accuracies = [];
      reversedWeeks.forEach(val => {
        if (val !== null) {
          accuracies.push(val);
          lastValid = val;
        } else {
          accuracies.push(lastValid);
        }
      });
    }

    const mapY = (accuracy) => {
      const minAcc = 40;
      const maxAcc = 100;
      const minY = 43;
      const maxY = 5;
      const clamped = Math.max(minAcc, Math.min(maxAcc, accuracy));
      return minY - ((clamped - minAcc) / (maxAcc - minAcc)) * (minY - maxY);
    };

    const points = accuracies.map((acc, i) => {
      const x = 4 + i * 26.66;
      const y = mapY(acc);
      return `L ${x.toFixed(1)} ${y.toFixed(1)}`;
    });
    // Replace first L with M
    if (points.length > 0) {
      points[0] = points[0].replace("L", "M");
    }
    const pathD = points.join(' ');
    const lastY = mapY(accuracies[accuracies.length - 1]);
    const latestAccuracy = accuracies[accuracies.length - 1];
    const sparklineColor = latestAccuracy < 60 ? '#E53E3E' : latestAccuracy < 75 ? '#D69E2E' : 'rgb(129, 165, 80)';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Hero Summary Banner Card */}
        <div style={{
          background: 'white',
          borderRadius: '22px',
          boxShadow: '0 12px 28px -10px rgba(61,77,38,0.12)',
          padding: '28px',
          display: 'flex',
          gap: '24px',
          alignItems: isMobile ? 'flex-start' : 'stretch',
          flexWrap: 'wrap'
        }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
              width: '92px',
              height: '92px',
              borderRadius: '50%',
              backgroundColor: '#F5E4BA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={getAnimalImage(userData?.profile?.avatar)}
                alt={userData?.profile?.avatar || 'avatar'}
                style={{ width: '70px', height: '70px', objectFit: 'contain' }}
              />
            </div>
            {isPro && (
              <div style={{
                position: 'absolute',
                top: '-3px',
                right: '-3px',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Crown size={15} color="#F6C112" fill="#F6C112" />
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: '280px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#81a550', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                {weekLabel}
              </div>
            </div>
            {(() => {
              const latestAccuracy = accuracies[accuracies.length - 1] || 0;
              const previousAccuracy = accuracies[accuracies.length - 2] || 0;
              const accuracyChange = latestAccuracy - previousAccuracy;
              
              let accuracyText = '';
              if (sessionsCount === 0) {
                accuracyText = 'Welcome to MathZoo! Start practicing to track progress.';
              } else if (accuracies.length >= 2 && accuracyChange !== 0) {
                accuracyText = `accuracy is ${accuracyChange > 0 ? 'up' : 'down'} ${Math.abs(accuracyChange)} points`;
              } else {
                accuracyText = `maintaining a solid ${latestAccuracy}% accuracy`;
              }

              return (
                <h2 style={{ fontSize: '22px', fontWeight: 800, margin: '0 0 8px', color: '#333333', lineHeight: 1.3 }}>
                  {sessionsCount === 0
                    ? `Welcome to MathZoo! Start practicing to see ${childName}'s insights.`
                    : belowBenchmarkSkills.length > 1
                      ? `${childName} had a busy week (${accuracyText}) - and ${belowBenchmarkSkills.length} skills need your help.`
                      : belowBenchmarkSkills.length === 1
                        ? `${childName} had a busy week (${accuracyText}) - and one skill needs your help.`
                        : `${childName} had a busy week (${accuracyText}) - with all skills on track!`}
                </h2>
              );
            })()}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginTop: '10px',
              alignItems: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 12px',
                borderRadius: '10px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                fontSize: '13px',
                fontWeight: '600',
                color: '#4B5563',
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
              }}>
                <Activity size={14} color={appColors.primary} />
                <span>{sessionsCount} sessions</span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 12px',
                borderRadius: '10px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                fontSize: '13px',
                fontWeight: '600',
                color: '#4B5563',
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
              }}>
                <Clock size={14} color={appColors.primary} />
                <span>{totalMinutes} minutes</span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 12px',
                borderRadius: '10px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                fontSize: '13px',
                fontWeight: '600',
                color: '#4B5563',
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
              }}>
                <Sparkles size={14} color="#D69E2E" />
                <span>{totalXP} XP earned</span>
              </div>

              {strongSkills.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 12px',
                  borderRadius: '10px',
                  backgroundColor: '#E8F5E9',
                  border: '1px solid #C8E6C9',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#2E7D32',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <TrendingUp size={14} color="#2E7D32" />
                  <span>
                    {strongSkills.length >= 2
                      ? `${strongSkills[0].label} and ${strongSkills[1].label} climbing`
                      : `${strongSkills[0].label} climbing`}
                  </span>
                </div>
              )}

              {belowBenchmarkSkills.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 12px',
                  borderRadius: '10px',
                  backgroundColor: '#FFEBEE',
                  border: '1px solid #FFCDD2',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#C62828',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <AlertCircle size={14} color="#C62828" />
                  <span>
                    {[...belowBenchmarkSkills].sort((a, b) => a.accuracy - b.accuracy)[0].label} slipped to {[...belowBenchmarkSkills].sort((a, b) => a.accuracy - b.accuracy)[0].accuracy}% (plan targets it)
                  </span>
                </div>
              )}
            </div>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            width: isMobile ? '100%' : '180px',
            borderLeft: isMobile ? 'none' : '1px solid #E2E8F0',
            paddingLeft: isMobile ? '0' : '24px',
            flexShrink: 0
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#FFFBEB',
              border: '1px solid #F6E05E',
              padding: '2px 10px',
              height: '22px',
              boxSizing: 'border-box',
              borderRadius: '11px',
              fontSize: '11px',
              fontWeight: '700',
              color: '#B7791F',
              alignSelf: 'flex-end',
              marginBottom: '4px'
            }}>
              <Star size={11} color="#D69E2E" fill="#D69E2E" />
              <span>{streakText}</span>
            </div>
            <svg width="100%" height="48" viewBox="0 0 170 48" style={{ marginBottom: '4px' }}>
              <path d={pathD} fill="none" stroke={sparklineColor} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="164" cy={lastY} r="5" fill={sparklineColor} stroke="white" strokeWidth="2" />
            </svg>
            <div style={{ fontSize: '12.5px', color: '#666666', whiteSpace: 'nowrap', textAlign: 'left', lineHeight: 1.55 }}>
              Accuracy, last 7 weeks → <strong style={{ color: sparklineColor }}>{latestAccuracy}%</strong>
            </div>
          </div>
        </div>

        {/* 3 Column Widgets */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '20px'
        }}>
          {useCombinedWideBox ? (
            /* Combined Box spanning 2 columns */
            <div style={{
              gridColumn: isMobile ? 'auto' : 'span 2',
              backgroundColor: 'white',
              borderTop: '4px solid #E53E3E',
              borderLeft: '1px solid #F3EEDA',
              borderRight: '1px solid #F3EEDA',
              borderBottom: '1px solid #F3EEDA',
              borderRadius: '22px',
              padding: isMobile ? '20px 16px' : '24px 24px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 4px 14px rgba(0,0,0,0.02)'
            }}>
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '800',
                  color: '#E53E3E',
                  letterSpacing: '0.8px',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <AlertCircle size={16} color="#E53E3E" />
                  Needs Help in {belowBenchmarkSkills.length} Skills
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#333333', margin: '0 0 4px' }}>
                  Targeted practice recommended
                </h3>
                <p style={{ fontSize: '13px', color: '#666666', margin: '0 0 16px', lineHeight: '1.5' }}>
                  These skills are currently running below the target age benchmark for Grade {currentGrade}. Focus on these during upcoming sessions.
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '16px',
                  marginTop: '16px'
                }}>
                  {belowBenchmarkSkills.map(skill => {
                    const benchmark = getSkillBenchmark(skill.label, age);
                    return (
                      <div key={skill.key} style={{
                        padding: '16px',
                        borderRadius: '16px',
                        backgroundColor: '#FFF5F5',
                        border: '1px solid #FED7D7',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14.5px', fontWeight: 'bold', color: '#2D3748' }}>{skill.label}</span>
                            <span style={{ fontSize: '15px', fontWeight: '800', color: '#E53E3E' }}>{skill.accuracy}%</span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#718096', marginBottom: '12px' }}>{skill.desc}</div>
                          
                          {/* Progress Bar Container */}
                          <div style={{ position: 'relative', height: '8px', backgroundColor: '#FED7D7', borderRadius: '4px', marginBottom: '14px' }}>
                            <div style={{ width: `${skill.accuracy}%`, height: '100%', backgroundColor: '#E53E3E', borderRadius: '4px' }} />
                            {/* Target indicator line */}
                            <div style={{
                              position: 'absolute',
                              top: '-3px',
                              bottom: '-3px',
                              left: `${benchmark}%`,
                              width: '2px',
                              backgroundColor: '#2D3748',
                              opacity: 0.8
                            }} />
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                            <span style={{ color: '#E53E3E', fontWeight: '600' }}>{skill.insight}</span>
                            <span style={{ color: '#718096', fontWeight: 'bold' }}>Target: {benchmark}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', borderTop: '1px solid #F0EAD6', paddingTop: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#718096' }}>
                  Focus on these areas in upcoming MathZoo app sessions.
                </span>
                <div
                  onClick={() => handleTabChange('insights')}
                  style={{
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#E53E3E',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span>See the full skill breakdown →</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Column 1: GOING WELL matching screenshot */}
              <div style={{
                backgroundColor: 'white',
                borderTop: '4px solid rgb(129, 165, 80)',
                borderLeft: '1px solid #F3EEDA',
                borderRight: '1px solid #F3EEDA',
                borderBottom: '1px solid #F3EEDA',
                borderRadius: '22px',
                padding: isMobile ? '20px 16px' : '24px 24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 4px 14px rgba(0,0,0,0.02)'
              }}>
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '800',
                    color: '#81a550',
                    letterSpacing: '0.8px',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <CheckCircle size={16} color="#81a550" />
                    Going well
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#333333' }}>{topSkill1.label}</span>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#81a550' }}>{topSkill1.accuracy}%</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#333333' }}>{topSkill2.label}</span>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#81a550' }}>{topSkill2.accuracy}%</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#333333' }}>Focus in sessions</span>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#81a550' }}>{focusScore}%</span>
                    </div>
                  </div>
                </div>
                <div style={{
                  marginTop: '14px',
                  fontSize: '12.5px',
                  color: '#666666',
                  lineHeight: '1.5'
                }}>
                  {calculatedSkills.length >= 2 
                    ? `Both ${topSkill1.label} and ${topSkill2.label} are above the age benchmark. Worth a high-five tonight!` 
                    : 'Skills are above the age benchmark. Worth a high-five tonight!'}
                </div>
              </div>

              {/* Column 2: Skill benchmark box */}
              <div style={{
                backgroundColor: 'white',
                borderTop: `4px solid ${box2Color}`,
                borderLeft: '1px solid #F3EEDA',
                borderRight: '1px solid #F3EEDA',
                borderBottom: '1px solid #F3EEDA',
                borderRadius: '22px',
                padding: isMobile ? '20px 16px' : '24px 24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 4px 14px rgba(0,0,0,0.02)'
              }}>
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '800',
                    color: box2Color,
                    letterSpacing: '0.8px',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Activity size={16} color={box2Color} />
                    {box2Badge.replace(/[!✓•]\s*/, '')}
                  </div>
                  <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#333333', margin: '0 0 4px' }}>
                    {lowestSkill.label} - {lowestSkill.desc}
                  </h3>
                  <p style={{ fontSize: '13px', color: '#666666', margin: '0 0 12px', lineHeight: '1.5' }}>
                    {lowestSkill.accuracy}% accuracy · {lowestSkill.wrongCount} wrong answers · {lowestSkill.insight}
                  </p>

                  {/* Progress Bar vs Benchmark */}
                  <div style={{ position: 'relative', marginBottom: '6px' }}>
                    <div style={{ width: '100%', height: '12px', backgroundColor: 'rgba(233,208,155,0.5)', borderRadius: '6px', overflow: 'hidden' }}>
                      <div style={{ width: `${divisionAcc}%`, height: '100%', backgroundColor: box2Color, borderRadius: '6px' }} />
                    </div>
                    {/* Dynamic target benchmark line */}
                    <div style={{
                      position: 'absolute',
                      top: '-3px',
                      bottom: '-3px',
                      left: `${getSkillBenchmark(lowestSkill.label, age)}%`,
                      width: '2px',
                      backgroundColor: '#333333',
                      opacity: 0.45
                    }} />
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#999999', marginBottom: '12px' }}>
                    ┃ age benchmark {getSkillBenchmark(lowestSkill.label, age)}%
                  </div>
                </div>

                <div
                  onClick={() => handleTabChange('insights')}
                  style={{
                    fontSize: '13px',
                    fontWeight: '700',
                    color: box2Color,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span>See the full skill breakdown →</span>
                </div>
              </div>
            </>
          )}

          {/* Column 3: WHAT TO DO matching screenshot */}
          <div style={{
            backgroundColor: 'white',
            borderTop: '4px solid #e2c269',
            borderLeft: '1px solid #F3EEDA',
            borderRight: '1px solid #F3EEDA',
            borderBottom: '1px solid #F3EEDA',
            borderRadius: '22px',
            padding: isMobile ? '20px 16px' : '24px 24px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 4px 14px rgba(0,0,0,0.02)'
          }}>
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '800',
                color: '#b08d28',
                letterSpacing: '0.8px',
                marginBottom: '12px',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <ArrowRight size={16} color="#b08d28" />
                What to do
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div
                  onClick={() => {
                    setTodoChecklist(prev => ({ ...prev, item1: !prev.item1 }));
                    if (!todoChecklist.item1) handleTabChange('insights');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '11px',
                    padding: '9px 11px',
                    backgroundColor: 'rgba(245,228,186,0.35)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    width: '21px',
                    height: '21px',
                    borderRadius: '7px',
                    border: todoChecklist.item1 ? '2px solid #81a550' : '2px solid #e9d09b',
                    background: todoChecklist.item1 ? 'linear-gradient(135deg, #81a550, #66AA44)' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {todoChecklist.item1 && <Check size={12} color="white" strokeWidth={3.5} />}
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: todoChecklist.item1 ? '#999999' : '#333333', textDecoration: todoChecklist.item1 ? 'line-through' : 'none' }}>
                    Look at the division breakdown (2 min)
                  </span>
                </div>

                <div
                  onClick={() => setTodoChecklist(prev => ({ ...prev, item2: !prev.item2 }))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '11px',
                    padding: '9px 11px',
                    backgroundColor: 'rgba(245,228,186,0.35)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    width: '21px',
                    height: '21px',
                    borderRadius: '7px',
                    border: todoChecklist.item2 ? '2px solid #81a550' : '2px solid #e9d09b',
                    background: todoChecklist.item2 ? 'linear-gradient(135deg, #81a550, #66AA44)' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {todoChecklist.item2 && <Check size={12} color="white" strokeWidth={3.5} />}
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: todoChecklist.item2 ? '#999999' : '#333333', textDecoration: todoChecklist.item2 ? 'line-through' : 'none' }}>
                    Print this week's practice sheet
                  </span>
                </div>

                <div
                  onClick={() => setTodoChecklist(prev => ({ ...prev, item3: !prev.item3 }))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '11px',
                    padding: '9px 11px',
                    backgroundColor: 'rgba(245,228,186,0.35)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    width: '21px',
                    height: '21px',
                    borderRadius: '7px',
                    border: todoChecklist.item3 ? '2px solid #81a550' : '2px solid #e9d09b',
                    background: todoChecklist.item3 ? 'linear-gradient(135deg, #81a550, #66AA44)' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {todoChecklist.item3 && <Check size={12} color="white" strokeWidth={3.5} />}
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: todoChecklist.item3 ? '#999999' : '#333333', textDecoration: todoChecklist.item3 ? 'line-through' : 'none' }}>
                    Ask tonight's dinner-table question
                  </span>
                </div>
              </div>
            </div>

            <div style={{
              marginTop: '12px',
              fontSize: '12px',
              fontWeight: '700',
              color: '#81a550'
            }}>
              {checkedCount} of 3 done - about {minutesLeft} min left
            </div>
          </div>
        </div>

        {/* Dinner-Table Question Banner Card */}
        <div style={{
          backgroundColor: '#3B5833',
          borderRadius: '20px',
          padding: '24px',
          color: 'white',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: '20px',
          boxShadow: '0 4px 15px rgba(59, 88, 51, 0.25)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <MessageCircle size={22} color="rgb(226, 194, 105)" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'rgb(226, 194, 105)', letterSpacing: '0.8px', marginBottom: '6px' }}>
                {currentDinnerPractice.topicLabel}
              </div>
              <div style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', lineHeight: 1.4, color: 'white' }}>
                {currentDinnerPractice.question}
              </div>
              {currentDinnerPractice.followUp && (
                <div style={{ fontSize: '14px', fontWeight: '600', color: 'rgb(226, 194, 105)', marginTop: '8px' }}>
                  {currentDinnerPractice.followUp}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setDinnerPracticeIndex(prev => prev + 1)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(226, 194, 105, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '12px',
              border: '1px solid rgba(226, 194, 105, 0.4)',
              backgroundColor: 'transparent',
              color: 'rgb(226, 194, 105)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.2s'
            }}
          >
            <RefreshCw size={16} color="rgb(226, 194, 105)" />
            <span>Another one</span>
          </button>
        </div>

        {/* Recent sessions Card */}
        {/* Recent sessions Card matching screenshot */}
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #F3EEDA',
          borderRadius: '22px',
          padding: isMobile ? '20px 16px' : '24px 28px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#2D3748',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Activity size={24} color={appColors.primary} style={{ marginRight: '12px' }} />
            Recent sessions
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {displaySessions.length > 0 ? (
              displaySessions.map((session, idx) => (
              <div
                key={session.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 0',
                  borderBottom: idx === displaySessions.length - 1 ? 'none' : '1px solid #F5F0E6',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#A0AEC0', width: '56px', flexShrink: 0 }}>
                    {session.date || session.day}
                  </span>
                  <span style={{
                    backgroundColor: '#F1F7EB',
                    color: 'rgb(129, 165, 80)',
                    padding: '4px 12px',
                    borderRadius: '14px',
                    fontSize: '12px',
                    fontWeight: '800',
                    flexShrink: 0
                  }}>
                    {session.level}
                  </span>
                  <span style={{ fontSize: '14px', color: '#4A5568', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {session.summary ? session.summary.replace(/ - /g, ' · ').replace(/, /g, ' · ') : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexShrink: 0 }}>
                  <span style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>
                    {session.duration}
                  </span>
                  <span style={{
                    fontSize: '15px',
                    fontWeight: '800',
                    color: session.accNum >= 70 ? 'rgb(129, 165, 80)' : '#F6993F',
                    minWidth: '44px',
                    textAlign: 'right'
                  }}>
                    {session.accuracy}
                  </span>
                </div>
              </div>
              ))
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: '#718096', fontSize: '14.5px' }}>
                No study sessions completed this week yet.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPerformanceTab = () => {
    if (!analytics) {
      return (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#F2F7EC',
            border: '1px solid #E1ECC8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto'
          }}>
            <Activity size={32} color="#5E983B" />
          </div>
          <p style={{ fontSize: '16px', fontWeight: '600', color: appColors.primary, marginBottom: '8px' }}>
            No performance data yet!
          </p>
          <p style={{ fontSize: '13px', color: appColors.textSecondary }}>
            Start practicing in the MathZoo app to see your performance metrics here.
          </p>
        </div>
      );
    }

    const trendsData = trendsView === 'weekly' ? weeklyTrendsData : dailyTrendsData;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Skills Radar and Session Quality */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
          gap: '24px'
        }}>
          <FloatingCard style={{ padding: '24px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '0px',
              marginTop: '0px',
              display: 'flex',
              alignItems: 'center',
              color: appColors.textPrimary
            }}>
              <Activity size={24} color={appColors.secondary} style={{ marginRight: '12px' }} />
              Skills Radar
            </h3>
            <p style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '20px' }}>
              Comparing performance against age-appropriate benchmarks (age {userData?.profile?.age || 7})
            </p>
            {radarData.length === 0 ? (
              <div style={{
                height: '300px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '20px'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📊</div>
                <p style={{ fontSize: '14px', fontWeight: '600', color: appColors.textPrimary, marginBottom: '6px' }}>
                  No skill data yet
                </p>
                <p style={{ fontSize: '12px', color: appColors.textSecondary, lineHeight: '1.5' }}>
                  Complete some practice sessions in the MathZoo app to see your skill comparison chart here
                </p>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData} margin={{ top: 10, right: 60, bottom: 10, left: 60 }}>
                    <PolarGrid stroke="#99999933" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: appColors.textSecondary }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar name="Your Child" dataKey="child" stroke={appColors.primary} strokeWidth={2} fill={appColors.primary} fillOpacity={0.5} isAnimationActive={false} />
                    <Radar name="Peer Average" dataKey="peer" stroke={appColors.accent} strokeWidth={2} fill={appColors.accent} fillOpacity={0.3} isAnimationActive={false} />
                    <Tooltip contentStyle={{ borderRadius: '8px' }} />
                  </RadarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '16px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '4px', backgroundColor: appColors.primary + '80' }} />
                    <span style={{ color: appColors.textSecondary }}>Your Child</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '4px', backgroundColor: appColors.accent + '80' }} />
                    <span style={{ color: appColors.textSecondary }}>Peer Average</span>
                  </div>
                </div>
              </>
            )}
          </FloatingCard>

          <FloatingCard style={{ padding: '24px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '0px',
              marginTop: '0px',
              display: 'flex',
              alignItems: 'center',
              color: appColors.textPrimary
            }}>
              <Award size={24} color={appColors.accent} style={{ marginRight: '12px' }} />
              Session Quality
            </h3>
            <p style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '20px' }}>
              Quality distribution based on response time per question
            </p>
            {sessionQualityData.length === 0 ? (
              <div style={{
                height: '250px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '20px'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏱️</div>
                <p style={{ fontSize: '14px', fontWeight: '600', color: appColors.textPrimary, marginBottom: '6px' }}>
                  No session quality data yet
                </p>
                <p style={{ fontSize: '12px', color: appColors.textSecondary, lineHeight: '1.5' }}>
                  Complete practice sessions to see quality metrics based on your response times
                </p>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={sessionQualityData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${value}%`}
                      isAnimationActive={false}
                    >
                      {sessionQualityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ marginTop: '16px' }}>
                  {sessionQualityData.map((item, index) => {
                    // For Rushed and Distracted sessions, being ABOVE benchmark is BAD (inverted logic)
                    const isInvertedMetric = item.name === 'Rushed Sessions' || item.name === 'Distracted Sessions';
                    const isGood = isInvertedMetric
                      ? item.value < item.benchmark
                      : item.value > item.benchmark;
                    const isWithinRange = Math.abs(item.value - item.benchmark) <= 2;

                    return (
                      <div key={index} style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: item.color
                            }} />
                            <span style={{ fontSize: '13px', fontWeight: '500', color: appColors.textPrimary }}>
                              {item.name}
                            </span>
                          </div>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: isWithinRange
                              ? appColors.textSecondary
                              : isGood
                                ? appColors.success
                                : appColors.error
                          }}>
                            {item.value}%
                          </span>
                        </div>
                        <div style={{ fontSize: '11px', marginLeft: '20px' }}>
                          <span style={{ color: appColors.textSecondary }}>Benchmark: {item.benchmark}%</span>
                          {' • '}
                          <span style={{
                            fontWeight: '600',
                            color: isWithinRange
                              ? appColors.textSecondary
                              : isGood
                                ? appColors.success
                                : appColors.error
                          }}>
                            {isWithinRange
                              ? '≈ Within range'
                              : isGood
                                ? '✓ ' + (isInvertedMetric ? 'Below average' : 'Above average')
                                : '↓ ' + (isInvertedMetric ? 'Above average' : 'Below average')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </FloatingCard>

          {/* Progress Overview - Moved from Trends Tab */}
          <FloatingCard style={{ padding: '24px', position: 'relative' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              color: appColors.textPrimary,
              marginBottom: '0px',
              marginTop: '0px'
            }}>
              <TrendingUp size={24} color={appColors.success} style={{ marginRight: '12px' }} />
              Progress Overview
            </h3>

            <div style={{ position: 'absolute', top: '20px', right: '24px', display: 'flex', gap: '8px', padding: '4px', backgroundColor: appColors.background + '40', borderRadius: '12px' }}>
              <button
                onClick={() => setTrendsView('weekly')}
                style={{
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: trendsView === 'weekly' ? appColors.primary : 'transparent',
                  color: trendsView === 'weekly' ? 'white' : appColors.textSecondary,
                  fontWeight: '600',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                Weekly
              </button>
              <button
                onClick={() => setTrendsView('daily')}
                style={{
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: trendsView === 'daily' ? appColors.primary : 'transparent',
                  color: trendsView === 'daily' ? 'white' : appColors.textSecondary,
                  fontWeight: '600',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                Daily
              </button>
            </div>

            <p style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '20px' }}>
              Answered vs Wrong vs Skipped questions over time
            </p>
            <ResponsiveContainer width="100%" height={350} key={`overview-chart-${trendsView}`}>
              <AreaChart data={trendsData} margin={{ left: -20, right: 10, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#99999933" />
                <XAxis
                  dataKey={trendsView === 'weekly' ? 'week' : 'date'}
                  tick={{ fontSize: 12, fill: appColors.textSecondary }}
                  tickFormatter={(value) => trendsView === 'daily' ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : value}
                />
                <YAxis tick={{ fontSize: 12, fill: appColors.textSecondary }} />
                <Tooltip contentStyle={{ borderRadius: '8px' }} />
                <Area type="monotone" dataKey="correct" stackId="1" stroke={appColors.success} fill={appColors.success} fillOpacity={0.6} name="Correct" />
                <Area type="monotone" dataKey="wrong" stackId="1" stroke={appColors.error} fill={appColors.error} fillOpacity={0.6} name="Wrong" />
                <Area type="monotone" dataKey="skipped" stackId="1" stroke={appColors.warning} fill={appColors.warning} fillOpacity={0.6} name="Skipped" />
              </AreaChart>
            </ResponsiveContainer>
          </FloatingCard>
        </div>
      </div>
    );
  };

  const renderInsightsTab = () => {
    // Handle case where there's no analytics data yet
    if (!analytics) {
      return (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#FFFBEB',
            border: '1px solid #FEEBC8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto'
          }}>
            <Sparkles size={32} color="#DD6B20" />
          </div>
          <p style={{ fontSize: '16px', fontWeight: '600', color: appColors.accent, marginBottom: '8px' }}>
            No insights data yet!
          </p>
          <p style={{ fontSize: '13px', color: appColors.textSecondary }}>
            Start practicing in the MathZoo app to see detailed insights and analytics here.
          </p>
        </div>
      );
    }

    const detailedGradeData = prepareDetailedGradePerformance();
    const gradeData = prepareGradeData();
    const questionAnalytics = getQuestionLevelAnalytics();

    const bestGrade = gradeData.reduce((best, curr) =>
      curr.accuracy > best.accuracy ? curr : best, gradeData[0] || {});

    const skipRate = ((analytics.skippedQuestions / analytics.totalQuestions) * 100).toFixed(1);

    // Analyze question-level data for insights
    const topErrorType = questionAnalytics && Object.keys(questionAnalytics.byErrorType).length > 0
      ? Object.entries(questionAnalytics.byErrorType).sort((a, b) => b[1].count - a[1].count)[0]
      : null;

    const topWeakArea = questionAnalytics && Object.keys(questionAnalytics.byConceptualArea).length > 0
      ? Object.entries(questionAnalytics.byConceptualArea).sort((a, b) => b[1].total - a[1].total)[0]
      : null;

    const needsReviewCount = questionAnalytics ? questionAnalytics.needsReview.length : 0;
    const needsPracticeCount = questionAnalytics ? (questionAnalytics.masteryLevels['needs_practice'] || 0) : 0;
    const developingCount = questionAnalytics ? (questionAnalytics.masteryLevels['developing'] || 0) : 0;

    const hasHighMultiplication = bestGrade.accuracy > 85;

    // Analyze time-of-day performance
    const timeOfDayData = analyzeTimeOfDayPerformance();
    const hasBestTimeSlot = timeOfDayData && timeOfDayData.bestSlot.sessionCount >= 3;

    const stops = [
      { name: 'Addition', subject: 'Addition' },
      { name: 'Subtraction', subject: 'Subtraction' },
      { name: 'Word Problems', subject: 'Word Problems' },
      { name: 'Multiplication', subject: 'Multiplication' },
      { name: 'Division', subject: 'Division' },
      { name: 'Fractions', subject: 'Fractions' }
    ];

    // --- Redesign calculations from DB ---
    const childName = userData?.profile?.name || 'MathHippo';
    const userGrade = parseInt(userData?.profile?.grade || '1', 10);

    const milestoneAnimal = userGrade === 1 ? 'Elephant' : userGrade === 2 ? 'Tiger' : 'Bear';
    const categoryTotals = {
      'Addition': 80,
      'Subtraction': 80,
      'Word Problems': 60,
      'Multiplication': 50,
      'Division': 50,
      'Fractions': 40
    };
    const normalizeCategoryName = (type) => {
      if (!type) return 'Other';
      const typeMap = {
        'сложение': 'Addition',
        'addition': 'Addition',
        'вычитание': 'Subtraction',
        'subtraction': 'Subtraction',
        'текстовые задачи': 'Word Problems',
        'word problems': 'Word Problems',
        'word_problems': 'Word Problems',
        'woordproblemen': 'Word Problems',
        'умножение': 'Multiplication',
        'multiplication': 'Multiplication',
        'деление': 'Division',
        'division': 'Division',
        'дроби': 'Fractions',
        'fractions': 'Fractions'
      };
      return typeMap[type.toLowerCase()] || type;
    };
    const getCategoryProblemsLeft = (subject) => {
      const target = categoryTotals[subject] || 50;
      let completed = 0;
      sessions.forEach(s => {
        if (s.grade === userGrade && s.questions?.correct) {
          Object.values(s.questions.correct).forEach(q => {
            const type = normalizeCategoryName(q.conceptualArea || q.problemType);
            if (type === subject) {
              completed++;
            }
          });
        }
      });
      return Math.max(0, target - completed);
    };

    const isGradeLocked = (grade) => {
      if (grade === 1) return false;
      const prevStatus = userData?.progress?.gradeStatuses?.[`grade${grade - 1}`];
      return prevStatus !== 'completed';
    };

    let focusedCount = 0, focusedCorrect = 0;
    let steadyCount = 0, steadyCorrect = 0;
    let rushedCount = 0, rushedCorrect = 0;
    sessions.forEach(s => {
      if (s.questions) {
        ['correct', 'wrong'].forEach(cat => {
          if (s.questions[cat]) {
            const entries = cat === 'wrong' ? flattenWrongAttempts(s.questions[cat]) : Object.values(s.questions[cat]);
            entries.forEach(q => {
              const isCorrect = cat === 'correct';
              if (q.wasFocused) {
                focusedCount++;
                if (isCorrect) focusedCorrect++;
              } else if (q.wasRushed) {
                rushedCount++;
                if (isCorrect) rushedCorrect++;
              } else {
                steadyCount++;
                if (isCorrect) steadyCorrect++;
              }
            });
          }
        });
      }
    });
    const focusedAccuracy = focusedCount > 0 ? Math.round((focusedCorrect / focusedCount) * 100) : 0;
    const steadyAccuracy = steadyCount > 0 ? Math.round((steadyCorrect / steadyCount) * 100) : 0;
    const rushedAccuracy = rushedCount > 0 ? Math.round((rushedCorrect / rushedCount) * 100) : 0;
    const rushingCost = (focusedCount > 0 && rushedCount > 0) ? Math.max(0, focusedAccuracy - rushedAccuracy) : 0;
    const finalFocusedCount = focusedCount;
    const finalSteadyCount = steadyCount;
    const finalRushedCount = rushedCount;

    const activeSlots = timeOfDayData?.timeSlots || [];
    const morningSlot = activeSlots.find(s => s.slot === 'morning') || { accuracy: 0, sessionCount: 0 };
    const afternoonSlot = activeSlots.find(s => s.slot === 'afternoon') || { accuracy: 0, sessionCount: 0 };
    const eveningSlot = activeSlots.find(s => s.slot === 'evening') || { accuracy: 0, sessionCount: 0 };
    const morningDiff = (morningSlot.sessionCount > 0 && eveningSlot.sessionCount > 0) 
      ? Math.max(0, morningSlot.accuracy - eveningSlot.accuracy) 
      : 0;

    const correctQuestions = [];
    sessions.forEach(s => {
      if (s.questions?.correct) {
        Object.values(s.questions.correct).forEach(q => {
          correctQuestions.push({
            ...q,
            timestamp: q.timestamp || s.metadata?.completedAt || s.metadata?.startedAt
          });
        });
      }
    });
    correctQuestions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    const recentCorrect = correctQuestions.slice(0, 20);
    const lowConfidenceRecent = recentCorrect.filter(q => q.confidenceLevel === 'low').length;
    const finalLowConfidenceRecent = lowConfidenceRecent;
    const totalRecentCorrect = recentCorrect.length;

    let skipsBeforeTrying = 0;
    let skipsAfterAttempt = 0;
    sessions.forEach(s => {
      if (s.questions?.skipped) {
        Object.values(s.questions.skipped).forEach(q => {
          if (q.timeSpent < 4) {
            skipsBeforeTrying++;
          } else {
            skipsAfterAttempt++;
          }
        });
      }
    });
    const finalSkipsBefore = skipsBeforeTrying;
    const finalSkipsAfter = skipsAfterAttempt;

    const appSwitches = sessions.reduce((sum, s) => sum + (s.summary?.backgroundSwitches || s.backgroundSwitches || 0), 0);
    const midSessionPauses = sessions.reduce((sum, s) => sum + (s.summary?.pauseCount || s.pauseCount || 0), 0);
    let distractedCount = 0;
    sessions.forEach(s => {
      if (s.questions) {
        ['correct', 'wrong', 'skipped'].forEach(cat => {
          if (s.questions[cat]) {
            const entries = cat === 'wrong' ? flattenWrongAttempts(s.questions[cat]) : Object.values(s.questions[cat]);
            entries.forEach(q => {
              if (q.wasDistracted) distractedCount++;
            });
          }
        });
      }
    });
    const finalDistractedCount = distractedCount;

    let cleanCorrect = 0, cleanTotal = 0;
    let switchCorrect = 0, switchTotal = 0;
    sessions.forEach(s => {
      const isClean = (s.summary?.backgroundSwitches || s.backgroundSwitches || 0) === 0;
      const correct = s.summary?.totalCorrect || 0;
      const total = s.summary?.totalQuestions || 0;
      if (isClean) {
        cleanCorrect += correct;
        cleanTotal += total;
      } else {
        switchCorrect += correct;
        switchTotal += total;
      }
    });
    const cleanAccuracy = cleanTotal > 0 ? Math.round((cleanCorrect / cleanTotal) * 100) : 0;
    const switchAccuracy = switchTotal > 0 ? Math.round((switchCorrect / switchTotal) * 100) : 0;
    const switchDiff = (cleanTotal > 0 && switchTotal > 0) ? Math.max(0, cleanAccuracy - switchAccuracy) : 0;

    let under3Correct = 0, under3Total = 0;
    let between3and10Correct = 0, between3and10Total = 0;
    let between10and30Correct = 0, between10and30Total = 0;
    let over30Correct = 0, over30Total = 0;
    sessions.forEach(s => {
      if (s.questions) {
        ['correct', 'wrong'].forEach(cat => {
          if (s.questions[cat]) {
            const entries = cat === 'wrong' ? flattenWrongAttempts(s.questions[cat]) : Object.values(s.questions[cat]);
            entries.forEach(q => {
              const time = q.timeSpent;
              if (time === undefined || time < 0) return;
              const isCorrect = cat === 'correct';
              if (time < 3) {
                under3Total++;
                if (isCorrect) under3Correct++;
              } else if (time <= 10) {
                between3and10Total++;
                if (isCorrect) between3and10Correct++;
              } else if (time <= 30) {
                between10and30Total++;
                if (isCorrect) between10and30Correct++;
              } else {
                over30Total++;
                if (isCorrect) over30Correct++;
              }
            });
          }
        });
      }
    });
    const accUnder3 = under3Total > 0 ? Math.round((under3Correct / under3Total) * 100) : 0;
    const acc3to10 = between3and10Total > 0 ? Math.round((between3and10Correct / between3and10Total) * 100) : 0;
    const acc10to30 = between10and30Total > 0 ? Math.round((between10and30Correct / between10and30Total) * 100) : 0;
    const accOver30 = over30Total > 0 ? Math.round((over30Correct / over30Total) * 100) : 0;

    const getCanonicalErrorType = (type) => {
      if (!type) return 'conceptual';
      const t = type.toLowerCase().trim();
      if (t === 'off_by_one') return 'off_by_one';
      if (t === 'typing_slip') return 'typing_slip';
      if (t === 'place_value_error') return 'place_value_error';
      return 'conceptual';
    };

    const errorTypeCounts = {};
    sessions.forEach(s => {
      if (s.questions?.wrong) {
        flattenWrongAttempts(s.questions.wrong).forEach(q => {
          const rawType = q.errorType || 'conceptual';
          const type = getCanonicalErrorType(rawType);
          errorTypeCounts[type] = (errorTypeCounts[type] || 0) + 1;
        });
      }
    });
    const sortedErrors = Object.entries(errorTypeCounts)
      .map(([type, count]) => {
        let label = 'Concept mix-up';
        let desc = 'incorrect operation or logic used';
        if (type === 'off_by_one') {
          label = 'Off-by-one counting';
          desc = 'last group in division slips';
        } else if (type === 'typing_slip') {
          label = 'Typing slip';
          desc = 'knew it, mistyped it';
        } else if (type === 'place_value_error') {
          label = 'Place value slip';
          desc = 'digit alignment or carrying error';
        }
        return { type, count, label, desc };
      })
      .sort((a, b) => b.count - a.count);
    const finalErrors = sortedErrors;
    const totalErrorsCount = finalErrors.reduce((sum, e) => sum + e.count, 0);
    const topErrorObj = finalErrors[0] || { count: 0, label: '', desc: '' };

    const widgetFooterStyle = {
      marginTop: 'auto',
      padding: '12px 16px',
      backgroundColor: '#FDFBF7',
      border: '1px solid #EAE2CA',
      borderRadius: '12px',
      fontSize: '13px',
      color: '#4A5568',
      lineHeight: '1.5',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px'
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Row 1: Journey so far (Full width) */}
        <div>
          {/* Card 1: Journey so far */}
          <FloatingCard style={{ padding: '24px', backgroundColor: 'white', borderRadius: '18px', border: '1px solid #EAE2CA', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              margin: '0 0 4px 0',
              display: 'flex',
              alignItems: 'center',
              color: '#2D3748'
            }}>
              <Compass size={24} color={appColors.primary} style={{ marginRight: '12px' }} />
              The journey so far
            </h3>
            <p style={{ fontSize: '13px', color: '#718096', margin: '0 0 24px 0' }}>
              Same journey {userData?.profile?.name || 'MathHippo'} sees in the app - with the parent view of each stop
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              overflowX: 'auto',
              padding: '10px 0',
              width: '100%',
              flexGrow: 1
            }}>
              {stops.map((stop, idx) => {
                const skillData = radarData.find(item => item.subject === stop.subject) || { child: 0, attempts: 0 };
                const score = skillData.child;
                const attempts = skillData.attempts;
                
                let borderStyle, bgStyle, textColor, statusLabel;
                if (attempts === 0) {
                  borderStyle = '2.5px dashed #CBD5E0';
                  bgStyle = '#F7FAFC';
                  textColor = '#A0AEC0';
                  statusLabel = 'Locked';
                } else if (score >= 70) {
                  borderStyle = '2.5px solid #5E983B';
                  bgStyle = '#F2F7EC';
                  textColor = '#5E983B';
                  statusLabel = 'Strong ✓';
                } else if (score >= 55) {
                  borderStyle = '2.5px solid #D69E2E';
                  bgStyle = '#FEF9E7';
                  textColor = '#D69E2E';
                  statusLabel = 'Developing';
                } else {
                  borderStyle = '2.5px solid #E53E3E';
                  bgStyle = '#FFF5F5';
                  textColor = '#E53E3E';
                  statusLabel = 'Focus ⚠';
                }

                return (
                  <React.Fragment key={stop.name}>
                    <div
                      onClick={() => {
                        if (attempts > 0) {
                          triggerCategoryHighlight(stop.name);
                        }
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '90px',
                        flexShrink: 0,
                        cursor: attempts > 0 ? 'pointer' : 'default'
                      }}
                    >
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        border: borderStyle,
                        backgroundColor: bgStyle,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: '800',
                        color: textColor,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                        flexShrink: 0
                      }}>
                        {attempts === 0 ? <Lock size={16} color={textColor} /> : `${score}%`}
                      </div>
                      <div style={{ marginTop: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#2D3748', whiteSpace: 'nowrap' }}>
                          {stop.name}
                        </div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: textColor, marginTop: '2px', whiteSpace: 'nowrap' }}>
                          {statusLabel}
                        </div>
                      </div>
                    </div>
                    {idx < stops.length - 1 && (
                      <div style={{
                        height: '2px',
                        borderTop: '2px dashed #E2E8F0',
                        flexGrow: 1,
                        minWidth: '8px',
                        margin: '0 4px',
                        marginTop: '-26px'
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Bottom Section: Next Bonus Collectable and Benchmark Cards side-by-side */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1.1fr 1.9fr',
              gap: '20px',
              marginTop: '20px'
            }}>
              {/* Left Column: Next Bonus Collectable Card */}
              <div style={{
                padding: '16px 20px',
                backgroundColor: '#FDFBF7',
                border: '1px solid #EAE2CA',
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '1px solid #EAE2CA', paddingBottom: '10px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    backgroundColor: 'white',
                    border: '1.5px solid #EAE2CA',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                  }}>
                    <img
                      src={`/animals/${milestoneAnimal.toLowerCase()}3.png`}
                      alt={milestoneAnimal}
                      style={{ width: '38px', height: '38px', objectFit: 'contain' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2D3748' }}>
                      Next Bonus Collectable: {milestoneAnimal}
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#718096', marginTop: '2px' }}>
                      Complete the started level to collect it!
                    </div>
                  </div>
                </div>

                {/* Grid of Categories and Problems Left */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}>
                  {stops.map(stop => {
                    const left = getCategoryProblemsLeft(stop.subject);
                    const total = categoryTotals[stop.subject] || 50;
                    const completed = Math.max(0, total - left);
                    const pct = Math.round((completed / total) * 100);
                    const isHighlighted = highlightedCategory === stop.name;
                    const skillData = radarData.find(item => item.subject === stop.subject) || { child: 0, attempts: 0 };
                    const score = skillData.child;
                    const attempts = skillData.attempts;

                    let highlightBorderColor = '#F3EFE0';
                    let highlightBgColor = 'white';
                    let highlightShadow = 'none';

                    if (isHighlighted) {
                      if (attempts === 0) {
                        highlightBorderColor = '#CBD5E0';
                        highlightBgColor = '#F7FAFC';
                        highlightShadow = '0 0 12px rgba(203, 213, 224, 0.2)';
                      } else if (score >= 70) {
                        highlightBorderColor = '#5E983B';
                        highlightBgColor = '#F2F7EC';
                        highlightShadow = '0 0 12px rgba(94, 152, 59, 0.3)';
                      } else if (score >= 55) {
                        highlightBorderColor = '#D69E2E';
                        highlightBgColor = '#FEF9E7';
                        highlightShadow = '0 0 12px rgba(214, 158, 46, 0.3)';
                      } else {
                        highlightBorderColor = '#E53E3E';
                        highlightBgColor = '#FFF5F5';
                        highlightShadow = '0 0 12px rgba(229, 62, 62, 0.3)';
                      }
                    }
                    
                    return (
                      <div key={stop.subject} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        backgroundColor: isHighlighted ? highlightBgColor : 'white',
                        border: isHighlighted ? `1.5px solid ${highlightBorderColor}` : '1px solid #F3EFE0',
                        boxShadow: isHighlighted ? highlightShadow : 'none',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        transform: isHighlighted ? 'scale(1.05)' : 'scale(1)',
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px' }}>
                          <span style={{ fontWeight: 'bold', color: '#2D3748' }}>{stop.name}</span>
                          <span style={{ fontWeight: 'bold', color: left === 0 ? '#5E983B' : '#718096' }}>
                            {left === 0 ? 'Completed ✓' : `${left} left`}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            flexGrow: 1,
                            height: '6px',
                            borderRadius: '3px',
                            backgroundColor: '#E2E8F0',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${pct}%`,
                              height: '100%',
                              backgroundColor: left === 0 ? '#5E983B' : '#81a550'
                            }} />
                          </div>
                          <span style={{ fontSize: '10px', color: '#718096', fontWeight: 'bold', width: '32px', textAlign: 'right' }}>
                            {completed}/{total}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Column: Benchmark Cards (the 3 cards) */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px'
              }}>
                {/* 1. Overall Success Rate */}
                <div style={{
                  textAlign: 'center',
                  padding: '16px 8px',
                  borderRadius: '16px',
                  border: '1px solid #EAE2CA',
                  backgroundColor: parseFloat(analytics.accuracy) > 70 ? appColors.success + '10' : appColors.warning + '10',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '26px', fontWeight: 'bold', marginBottom: '6px', color: parseFloat(analytics.accuracy) > 70 ? appColors.success : appColors.warning }}>
                    {analytics.accuracy}%
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '4px', color: appColors.textPrimary }}>
                    Overall Success Rate
                  </div>
                  <div style={{ fontSize: '11px', color: appColors.textSecondary, marginBottom: '8px' }}>
                    {analytics.correctAnswers} correct / {analytics.totalQuestions} total
                  </div>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    padding: '3px 8px',
                    borderRadius: '8px',
                    backgroundColor: parseFloat(analytics.accuracy) > 70 ? appColors.success + '20' : appColors.warning + '20',
                    color: parseFloat(analytics.accuracy) > 70 ? appColors.success : appColors.warning
                  }}>
                    {parseFloat(analytics.accuracy) > 70 ? '✓ Above' : '↓ Below'} avg (70%)
                  </div>
                </div>

                {/* 2. Skip Rate */}
                <div style={{
                  textAlign: 'center',
                  padding: '16px 8px',
                  borderRadius: '16px',
                  border: '1px solid #EAE2CA',
                  backgroundColor: parseFloat(skipRate) < 20 ? appColors.success + '10' : appColors.error + '10',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '26px', fontWeight: 'bold', marginBottom: '6px', color: parseFloat(skipRate) < 20 ? appColors.success : appColors.error }}>
                    {skipRate}%
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '4px', color: appColors.textPrimary }}>
                    Skip Rate
                  </div>
                  <div style={{ fontSize: '11px', color: appColors.textSecondary, marginBottom: '8px' }}>
                    {analytics.skippedQuestions} questions skipped
                  </div>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    padding: '3px 8px',
                    borderRadius: '8px',
                    backgroundColor: parseFloat(skipRate) < 15 ? appColors.success + '20' : appColors.error + '20',
                    color: parseFloat(skipRate) < 15 ? appColors.success : appColors.error
                  }}>
                    {parseFloat(skipRate) < 15 ? '✓ Below' : '↑ Above'} avg (15%)
                  </div>
                </div>

                {/* 3. Avg Response Time */}
                <div style={{
                  textAlign: 'center',
                  padding: '16px 8px',
                  borderRadius: '16px',
                  border: '1px solid #EAE2CA',
                  backgroundColor: parseFloat(analytics.avgResponseTime) < 35 ? appColors.success + '10' : appColors.primary + '10',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '26px', fontWeight: 'bold', marginBottom: '6px', color: parseFloat(analytics.avgResponseTime) < 35 ? appColors.success : appColors.primary }}>
                    {analytics.avgResponseTime}s
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '4px', color: appColors.textPrimary }}>
                    Avg Response Time
                  </div>
                  <div style={{ fontSize: '11px', color: appColors.textSecondary, marginBottom: '8px' }}>
                    Per question answered
                  </div>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    padding: '3px 8px',
                    borderRadius: '8px',
                    backgroundColor: parseFloat(analytics.avgResponseTime) < 30 ? appColors.success + '20' : appColors.primary + '20',
                    color: parseFloat(analytics.avgResponseTime) < 30 ? appColors.success : appColors.primary
                  }}>
                    {parseFloat(analytics.avgResponseTime) < 30 ? '✓ Faster' : '= Normal'} (30s avg)
                  </div>
                </div>
              </div>
            </div>
          </FloatingCard>
        </div>
        {/* Row 2: Performance by level (Full width card) */}
        <FloatingCard style={{ padding: '24px', borderRadius: '18px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#2D3748',
            margin: '0 0 6px 0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Target size={24} color={appColors.primary} style={{ marginRight: '12px' }} />
            Performance by level
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#718096',
            margin: '0 0 24px 0',
            fontWeight: '500'
          }}>
            Easy, Medium and Hard at every level, and why "skipped" is not the same as "wrong"
          </p>

          {/* Level Selector */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            {[1, 2, 3].map(grade => {
              const locked = isGradeLocked(grade);
              const isCurrent = grade === userGrade;
              const isSelected = selectedGrade === grade;

              let btnStyle = {
                flex: 1,
                padding: '14px',
                borderRadius: '14px',
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              };

              if (isSelected) {
                btnStyle.border = '2px solid #5E983B';
                btnStyle.backgroundColor = '#F2F7EC';
                btnStyle.color = '#5E983B';
              } else if (locked) {
                btnStyle.border = '1.5px solid #EAE2CA';
                btnStyle.backgroundColor = '#FAF8F5';
                btnStyle.color = '#B2A994';
              } else {
                btnStyle.border = '1.5px solid #EAE2CA';
                btnStyle.backgroundColor = 'white';
                btnStyle.color = '#718096';
              }

              return (
                <button
                  key={grade}
                  onClick={() => setSelectedGrade(grade)}
                  style={btnStyle}
                >
                  Level {grade}
                  {isCurrent && ' · current'}
                  {locked && <Lock size={15} color="#B2A994" />}
                </button>
              );
            })}
          </div>

          {/* Selected Level Details */}
          {(() => {
            const isLocked = isGradeLocked(selectedGrade);
            const totalLevelQuestions = (detailedGradeData[selectedGrade]?.easy?.total || 0) + (detailedGradeData[selectedGrade]?.medium?.total || 0) + (detailedGradeData[selectedGrade]?.hard?.total || 0);
            const isNotStarted = !isLocked && totalLevelQuestions === 0;

            const difficultyColors = {
              easy: '#5E983B',
              medium: '#DD6B20',
              hard: '#718096'
            };

            const columnStyles = {
              easy: { bg: '#F9FBF7', border: '1.5px solid #E2ECD5' },
              medium: { bg: '#FDFBF7', border: '1.5px solid #FDF3E5' },
              hard: { bg: '#F8FAFC', border: '1.5px solid #E2E8F0' }
            };

            const pillStyles = {
              easy: { bg: '#E1ECC8', text: '#5E983B' },
              medium: { bg: '#FEEBC8', text: '#DD6B20' },
              hard: { bg: '#EDF2F7', text: '#718096' }
            };

            return (
              <div>


                {/* Level Summary */}
                <div style={{
                  padding: '16px 20px',
                  borderRadius: '14px',
                  backgroundColor: '#FAF5E6',
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#2D3748', margin: '0 0 4px 0' }}>
                      Level {selectedGrade}{selectedGrade === userGrade ? ' · current' : isLocked ? ' · locked' : isNotStarted ? ' · not started' : ' · completed'}
                    </h4>
                    <p style={{ fontSize: '13px', color: '#718096', margin: 0 }}>
                      {selectedGrade === userGrade ? 13 : (selectedGrade < userGrade && !isNotStarted) ? 100 : 0}% of the level done
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#2D3748', fontSize: '14px', fontWeight: 'bold' }}>
                    <Clock size={16} color="#718096" style={{ marginRight: '8px' }} />
                    {(() => {
                      const totalSeconds = gradeStats[selectedGrade]?.stats?.totalTimeSeconds || 0;
                      return `${Math.round(totalSeconds / 60)} min total`;
                    })()}
                  </div>
                </div>

                {/* Uniform 3-Column Difficulty Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
                  gap: '20px',
                  position: 'relative'
                }}>
                  {/* Locked / Not Started Overlay */}
                  {(isLocked || isNotStarted) && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.85)',
                      backdropFilter: 'blur(2.5px)',
                      borderRadius: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      border: '1px dashed #E2E8F0',
                      textAlign: 'center',
                      padding: '20px'
                    }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: '#F7FAFC',
                        border: '1.5px solid #E2E8F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '12px'
                      }}>
                        <Lock size={24} color="#718096" />
                      </div>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#2D3748' }}>
                        Level {selectedGrade} is {isLocked ? 'Locked' : 'Not Started'}
                      </span>
                      <p style={{ fontSize: '13px', color: '#718096', margin: '6px 0 0 0', maxWidth: '250px' }}>
                        {isLocked 
                          ? 'Complete previous level to unlock content.' 
                          : 'Start practicing to unlock metrics.'}
                      </p>
                    </div>
                  )}

                  {['easy', 'medium', 'hard'].map(difficulty => {
                    const data = detailedGradeData[selectedGrade]?.[difficulty] || { total: 0, correct: 0, wrong: 0, skipped: 0, categories: {} };
                    const accuracy = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
                    const color = difficultyColors[difficulty];
                    const colStyle = columnStyles[difficulty];
                    const pillStyle = pillStyles[difficulty];

                    // Ensure standard subjects exist
                    const subjects = ['Addition', 'Subtraction', 'Word Problems'];
                    const displayCategories = {};
                    subjects.forEach(sub => {
                      displayCategories[sub] = data.categories[sub] || { total: 0, correct: 0, wrong: 0, skipped: 0 };
                    });

                    // Add any extra subjects found in DB
                    Object.entries(data.categories || {}).forEach(([catName, catStats]) => {
                      if (!displayCategories[catName]) {
                        displayCategories[catName] = catStats;
                      }
                    });

                    return (
                      <div key={difficulty} style={{
                        padding: '20px',
                        borderRadius: '16px',
                        backgroundColor: colStyle.bg,
                        border: colStyle.border,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        minHeight: '400px'
                      }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2D3748', margin: 0 }}>
                            {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                          </h4>
                          <span style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: pillStyle.text,
                            backgroundColor: pillStyle.bg,
                            padding: '4px 10px',
                            borderRadius: '8px'
                          }}>
                            {data.total > 0 ? `${accuracy}%` : '0%'}
                          </span>
                        </div>

                        {/* Subjects List */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flexGrow: 1 }}>
                          {Object.entries(displayCategories).map(([category, catStats]) => {
                            const catAccuracy = catStats.total > 0 ? Math.round((catStats.correct / catStats.total) * 100) : 0;
                            return (
                              <div key={category} style={{
                                backgroundColor: 'white',
                                border: '1px solid #FAF5E6',
                                borderRadius: '12px',
                                padding: '14px 16px',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.01)'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '8px' }}>
                                  <span style={{ fontWeight: 'bold', color: '#2D3748' }}>{category}</span>
                                  <span style={{ fontWeight: 'bold', color: color }}>
                                    {catStats.total > 0 ? `${catAccuracy}%` : '0%'}
                                  </span>
                                </div>
                                <div style={{
                                  width: '100%',
                                  height: '8px',
                                  borderRadius: '4px',
                                  backgroundColor: '#FAF5E6',
                                  overflow: 'hidden',
                                  marginBottom: '8px'
                                }}>
                                  <div style={{
                                    width: `${catStats.total > 0 ? catAccuracy : 2}%`,
                                    height: '100%',
                                    backgroundColor: color,
                                    transition: 'width 0.5s'
                                  }} />
                                </div>
                                <div style={{ fontSize: '11px', color: '#718096', fontWeight: '500' }}>
                                  {catStats.correct}/{catStats.total} correct · {catStats.wrong} wrong · {catStats.skipped} skipped
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Pedagogic Banner */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          fontSize: '12.5px',
                          color: '#4A5568',
                          lineHeight: '1.5',
                          fontWeight: '500',
                          marginTop: 'auto'
                        }}>
                          {difficulty === 'easy' && (
                            <>
                              <Check size={16} color="#5E983B" style={{ flexShrink: 0, marginTop: '2px' }} />
                              <span>Effectively mastered: fast and accurate. The app will start serving fewer Easy questions automatically.</span>
                            </>
                          )}
                          {difficulty === 'medium' && (
                            <>
                              <ArrowRight size={16} color="#DD6B20" style={{ flexShrink: 0, marginTop: '2px' }} />
                              <span>This is where the real learning happens right now. Wrong answers cluster in division-style sharing questions, following the same pattern as the focus skill below.</span>
                            </>
                          )}
                          {difficulty === 'hard' && (
                            <>
                              <Info size={16} color="#718096" style={{ flexShrink: 0, marginTop: '2px' }} />
                              <span>Untouched so far, which is normal for this level progress. Expect first Hard attempts once Medium passes ~70%.</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            );
          })()}
        </FloatingCard>

        {/* Row 3: First 3 Visual Analytics Widgets */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
          gap: '20px'
        }}>
          {/* Card 1: How your Kid answers */}
          <FloatingCard style={{ padding: '24px', display: 'flex', flexDirection: 'column', minHeight: '340px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2D3748', margin: '0 0 4px 0' }}>
              How your Kid answers
            </h4>
            <p style={{ fontSize: '13px', color: '#718096', margin: '0 0 24px 0' }}>
              Every answer is tagged focused, steady or rushed
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
              {[
                { label: 'Focused', count: finalFocusedCount, accuracy: focusedAccuracy, color: '#5E983B' },
                { label: 'Steady', count: finalSteadyCount, accuracy: steadyAccuracy, color: '#DD6B20' },
                { label: 'Rushed', count: finalRushedCount, accuracy: rushedAccuracy, color: '#FF9800' }
              ].map((row, idx) => (
                <div key={idx}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 'bold', color: '#2D3748' }}>
                      {row.label} <span style={{ fontWeight: 'normal', color: '#718096' }}>· {row.count} answers</span>
                    </span>
                    <span style={{ fontWeight: 'bold', color: row.color }}>
                      {row.accuracy}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: '#FAF5E6',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${row.accuracy}%`,
                      height: '100%',
                      backgroundColor: row.color,
                      transition: 'width 0.5s'
                    }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={widgetFooterStyle}>
              <span style={{ color: '#81a550', fontWeight: 'bold', fontSize: '15px', lineHeight: '1.2' }}>→</span>
              <span>
                {finalRushedCount > 0 
                  ? (rushingCost > 0 
                      ? <>Rushed answers cost {childName} <strong>{rushingCost}% in accuracy</strong>. Steady practice is key.</>
                      : <>Rushing hasn't impacted accuracy so far, but maintaining a steady pace is recommended.</>
                    )
                  : <>No rushed answers detected so far! Keep up the focused and steady practice.</>
                }
              </span>
            </div>
          </FloatingCard>

          {/* Card 2: When practice works best */}
          <FloatingCard style={{ padding: '24px', display: 'flex', flexDirection: 'column', minHeight: '340px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2D3748', margin: '0 0 4px 0' }}>
              When practice works best
            </h4>
            <p style={{ fontSize: '13px', color: '#718096', margin: '0 0 20px 0' }}>
              Accuracy by time of day
            </p>
            
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', justifyContent: 'space-around', height: '140px', marginBottom: '20px' }}>
              {[
                { label: 'Morning', val: morningSlot.accuracy, count: morningSlot.sessionCount, color: '#5E983B' },
                { label: 'Afternoon', val: afternoonSlot.accuracy, count: afternoonSlot.sessionCount, color: '#DD6B20' },
                { label: 'Evening', val: eveningSlot.accuracy, count: eveningSlot.sessionCount, color: '#718096' }
              ].map((col, idx) => (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '60px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: col.color, marginBottom: '6px' }}>{col.val}%</span>
                  <div style={{
                    width: '32px',
                    height: `${(col.val / 100) * 100}px`,
                    backgroundColor: col.color,
                    borderRadius: '6px 6px 0 0',
                    transition: 'height 0.5s'
                  }} />
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#2D3748', marginTop: '8px' }}>{col.label}</span>
                  <span style={{ fontSize: '10px', color: '#718096', marginTop: '2px' }}>{col.count} sess</span>
                </div>
              ))}
            </div>

            <div style={widgetFooterStyle}>
              <span style={{ color: '#81a550', fontWeight: 'bold', fontSize: '15px', lineHeight: '1.2' }}>→</span>
              <span>
                {morningSlot.sessionCount > 0 && eveningSlot.sessionCount > 0
                  ? <>Morning sessions are <strong>{morningDiff}% more accurate</strong> than evening sessions.</>
                  : <>More session data at different times of day is needed to compare patterns.</>
                }
              </span>
            </div>
          </FloatingCard>

          {/* Card 3: The confidence gap */}
          <FloatingCard style={{ padding: '24px', display: 'flex', flexDirection: 'column', minHeight: '340px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2D3748', margin: '0 0 4px 0' }}>
              The confidence gap
            </h4>
            <p style={{ fontSize: '13px', color: '#718096', margin: '0 0 24px 0' }}>
              Underconfidence and skips before trying
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: '#FAF8F5',
                border: '1px solid #EAE2CA',
                borderRadius: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <AlertCircle size={20} color="#D69E2E" />
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#2D3748' }}>{finalLowConfidenceRecent}/{totalRecentCorrect}</div>
                    <div style={{ fontSize: '11px', color: '#718096', marginTop: '2px' }}>
                      recent <strong>correct</strong> answers {childName} marked as "not sure"
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: '#FAF8F5',
                border: '1px solid #EAE2CA',
                borderRadius: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <AlertCircle size={20} color="#718096" />
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#2D3748' }}>{finalSkipsBefore} vs {finalSkipsAfter}</div>
                    <div style={{ fontSize: '11px', color: '#718096', marginTop: '2px' }}>
                      skips before trying vs skips after attempt
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={widgetFooterStyle}>
              <span style={{ color: '#81a550', fontWeight: 'bold', fontSize: '15px', lineHeight: '1.2' }}>→</span>
              <span>
                {finalSkipsBefore > 0
                  ? <>High rate of instant skips suggests fear of failure. Encourage attempting even when unsure.</>
                  : <>Confidence looks great! No instant skips detected so far during sessions.</>
                }
              </span>
            </div>
          </FloatingCard>
        </div>

        {/* Row 4: Remaining 3 Visual Analytics Widgets */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
          gap: '20px'
        }}>
          {/* Card 4: Distraction meter */}
          <FloatingCard style={{ padding: '24px', display: 'flex', flexDirection: 'column', minHeight: '340px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2D3748', margin: '0 0 4px 0' }}>
              Distraction meter
            </h4>
            <p style={{ fontSize: '13px', color: '#718096', margin: '0 0 24px 0' }}>
              App-switches, pauses and distracted answers per session
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
              {[
                { val: appSwitches, label: 'app switches this week' },
                { val: midSessionPauses, label: 'mid-session pauses' },
                { val: finalDistractedCount, label: 'answers flagged distracted' }
              ].map((box, idx) => (
                <div key={idx} style={{
                  padding: '12px 8px',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FEEBC8',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#DD6B20', marginBottom: '4px' }}>
                    {box.val}
                  </div>
                  <div style={{ fontSize: '10px', color: '#718096', lineHeight: 1.3 }}>
                    {box.label}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '600', color: '#4A5568' }}>Clean sessions</span>
                  <span style={{ fontWeight: 'bold', color: '#5E983B', marginLeft: 'auto' }}>{cleanAccuracy}%</span>
                </div>
                <div style={{ width: '100%', height: '6px', borderRadius: '3px', backgroundColor: '#E2E8F0', overflow: 'hidden' }}>
                  <div style={{ width: `${cleanAccuracy}%`, height: '100%', backgroundColor: '#5E983B' }} />
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '600', color: '#4A5568' }}>Sessions with app-switching</span>
                  <span style={{ fontWeight: 'bold', color: '#FF9800', marginLeft: 'auto' }}>{switchAccuracy}%</span>
                </div>
                <div style={{ width: '100%', height: '6px', borderRadius: '3px', backgroundColor: '#E2E8F0', overflow: 'hidden' }}>
                  <div style={{ width: `${switchAccuracy}%`, height: '100%', backgroundColor: '#FF9800' }} />
                </div>
              </div>
            </div>
            <div style={widgetFooterStyle}>
              <span style={{ color: '#81a550', fontWeight: 'bold', fontSize: '15px', lineHeight: '1.2' }}>→</span>
              <span>
                {switchTotal > 0
                  ? <>App-switching impact: Clean sessions are <strong>{switchDiff}% more accurate</strong>.</>
                  : <>No app-switching detected so far. Great focus during study sessions!</>
                }
              </span>
            </div>
          </FloatingCard>

          {/* Card 5: The thinking sweet spot */}
          <FloatingCard style={{ padding: '24px', display: 'flex', flexDirection: 'column', minHeight: '340px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2D3748', margin: '0 0 4px 0' }}>
              The thinking sweet spot
            </h4>
            <p style={{ fontSize: '13px', color: '#718096', margin: '0 0 20px 0' }}>
              Accuracy by how long each answer took
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
              {[
                { label: '< 3 sec', desc: 'guessing', accuracy: accUnder3, color: '#FF9800' },
                { label: '3–10 sec', desc: '★ sweet spot', accuracy: acc3to10, color: '#5E983B' },
                { label: '10–30 sec', desc: 'working it out', accuracy: acc10to30, color: '#D69E2E' },
                { label: '> 30 sec', desc: 'drifting off', accuracy: accOver30, color: '#E53E3E' }
              ].map((row, idx) => (
                <div key={idx}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 'bold', color: '#2D3748' }}>
                      {row.label} <span style={{ fontWeight: 'normal', color: '#718096' }}>· {row.desc}</span>
                    </span>
                    <span style={{ fontWeight: 'bold', color: row.color }}>
                      {row.accuracy}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: '#FAF5E6',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${row.accuracy}%`,
                      height: '100%',
                      backgroundColor: row.color,
                      transition: 'width 0.5s'
                    }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={widgetFooterStyle}>
              <span style={{ color: '#81a550', fontWeight: 'bold', fontSize: '15px', lineHeight: '1.2' }}>→</span>
              <span>
                {between3and10Total > 0
                  ? <>The 3–10 second zone is the sweet spot, with <strong>{acc3to10}% accuracy</strong>.</>
                  : <>Not enough sweet spot session data to analyze thinking duration yet.</>
                }
              </span>
            </div>
          </FloatingCard>

          {/* Card 6: What kind of mistakes */}
          <FloatingCard style={{ padding: '24px', display: 'flex', flexDirection: 'column', minHeight: '340px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2D3748', margin: '0 0 4px 0' }}>
              What kind of mistakes
            </h4>
            <p style={{ fontSize: '13px', color: '#718096', margin: '0 0 24px 0' }}>
              Every wrong answer is classified by the app
            </p>
            {finalErrors.length > 0 ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                  {finalErrors.map((err, idx) => {
                    const badgeStyle = err.type === 'off_by_one'
                      ? { bg: '#FFF5F5', txt: '#E53E3E', label: 'HIGH' }
                      : err.type === 'conceptual'
                        ? { bg: '#FFFBEB', txt: '#DD6B20', label: 'MED' }
                        : { bg: '#F7FAFC', txt: '#4A5568', label: 'LOW' };
                    return (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <span style={{ fontSize: '20px', fontWeight: '800', color: badgeStyle.txt }}>{err.count}</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#2D3748' }}>{err.label}</div>
                            <div style={{ fontSize: '11px', color: '#718096', marginTop: '2px' }}>{err.desc}</div>
                          </div>
                        </div>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: badgeStyle.txt,
                          backgroundColor: badgeStyle.bg,
                          padding: '4px 8px',
                          borderRadius: '6px'
                        }}>
                          {badgeStyle.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={widgetFooterStyle}>
                  <span style={{ color: '#81a550', fontWeight: 'bold', fontSize: '15px', lineHeight: '1.2' }}>→</span>
                  <span>
                    {topErrorObj.count} of {totalErrorsCount} errors are the <strong>same slip</strong>, not {finalErrors.length} different problems. One targeted fix clears most of the red.
                  </span>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', margin: 'auto', textAlign: 'center' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  backgroundColor: '#F2F7EC',
                  border: '1.5px solid #E2ECD5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '12px'
                }}>
                  <CheckCircle size={28} color="#5E983B" />
                </div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2D3748' }}>No mistakes recorded!</div>
                <div style={{ fontSize: '11.5px', color: '#718096', marginTop: '4px', maxWidth: '200px', lineHeight: '1.4' }}>
                  Perfect accuracy in completed sessions. Keep up the amazing work!
                </div>
              </div>
            )}
          </FloatingCard>
        </div>
      </div>
    );
  };

  const renderAITutorTab = () => {
    if (!analytics) {
      return (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#F2F7EC',
            border: '1px solid #E1ECC8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto'
          }}>
            <Brain size={32} color="#5E983B" />
          </div>
          <p style={{ fontSize: '16px', fontWeight: '600', color: appColors.secondary, marginBottom: '8px' }}>
            No AI insights available yet!
          </p>
          <p style={{ fontSize: '13px', color: appColors.textSecondary }}>
            Start practicing in the MathZoo app to get personalized AI-powered insights and practice recommendations.
          </p>
        </div>
      );
    }

    const mostWrongAnswers = getMostWrongAnswers();
    const questionAnalytics = getQuestionLevelAnalytics();
    const gradeData = prepareGradeData();
    const weakestGrade = gradeData.reduce((worst, curr) =>
      curr.accuracy < worst.accuracy ? curr : worst, gradeData[0] || {});



    const handleLanguageChange = (e) => {
      const val = e.target.value;
      setSelectedLanguage(val);
      setNeedsGeneration(true);
    };

    const handleGenerateNew = async () => {
      if (!isPro) {
        setGeneratedProblems(generatePracticeProblems(3, selectedLanguage));
        setProblemsLanguage(selectedLanguage);
        setNeedsGeneration(false);
        return;
      }

      // Signal to the auto-load init effect that a manual generation is in flight.
      // This prevents the effect from firing a duplicate request when we clear problems.
      isManualGenerationRef.current = true;
      setIsGeneratingPractice(true);
      setNeedsGeneration(false);
      setGeneratedProblems([]); // Clear stale problems immediately so old cards don't show

      try {
        const targetGradeInt = parseInt(weakestGrade.grade?.replace('Level ', '') || userData?.progress?.currentGrade || 1);
        const gradeSessions = sessions.filter(s => parseInt(s.grade) === targetGradeInt);
        gradeSessions.sort((a, b) => {
          const dateA = new Date(b.metadata?.completedAt || b.metadata?.startedAt || 0);
          const dateB = new Date(a.metadata?.completedAt || a.metadata?.startedAt || 0);
          return dateA - dateB;
        });
        const currentDifficulty = gradeSessions.length > 0 ? (gradeSessions[0].difficulty || 'easy') : 'easy';

        // Unified behavioralStats — same calculation used in auto-path
        const buttonBehavioralStats = {
          focused_pct:    sessionQualityData.find(d => d.name === 'Focused Sessions')?.value    || 0,
          rushed_pct:     sessionQualityData.find(d => d.name === 'Rushed Sessions')?.value     || 0,
          distracted_pct: sessionQualityData.find(d => d.name === 'Distracted Sessions')?.value || 0,
        };

        const payload = {
          language: selectedLanguage,
          targetGrade: targetGradeInt,
          count: 9,
          weakestGrade: { ...weakestGrade, difficulty: currentDifficulty },
          behavioralStats: buttonBehavioralStats,
          mostWrongAnswers: mostWrongAnswers.slice(0, 5)
        };

        let apiUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3002' : '');
        if (apiUrl.endsWith('/')) {
          apiUrl = apiUrl.slice(0, -1);
        }

        console.log(`🚀[Button] Fetching practice for Grade ${targetGradeInt}, Difficulty: ${currentDifficulty}, Lang: ${selectedLanguage}`);
        console.log(`   URL: ${apiUrl}/api/generate-practice`);

        const authHeaderObj = await authHeaders();
        if (!authHeaderObj.Authorization && window.location.hostname === 'localhost') {
          console.log('⚠️ [Button Local Dev Notice] No auth token available — using local practice problem generation.');
          setGeneratedProblems(generatePracticeProblems(9, selectedLanguage));
          setProblemsLanguage(selectedLanguage);
          setProblemsFromAPI(false);
          setIsGeneratingPractice(false);
          isManualGenerationRef.current = false;
          return;
        }

        const response = await fetch(`${apiUrl}/api/generate-practice`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaderObj,
          },
          body: JSON.stringify(payload)
        });

        if (response.status === 429) {
          const errData = await response.json().catch(() => ({}));
          setRateLimitInfo({ type: 'practice', resetAt: errData.resetAt || null });
          // Fall back to local problems so the auto-load useEffect doesn't
          // re-fire immediately and hammer the rate-limited endpoint.
          setGeneratedProblems(generatePracticeProblems(9, selectedLanguage));
          setProblemsLanguage(selectedLanguage);
          setProblemsFromAPI(false);
          setIsGeneratingPractice(false);
          isManualGenerationRef.current = false;
          return;
        }

        if (response.status === 401 || response.status === 403) {
          console.warn(`⚠️ [Button API Auth Notice] Status ${response.status} - Using local practice problem generation.`);
          setGeneratedProblems(generatePracticeProblems(9, selectedLanguage));
          setProblemsLanguage(selectedLanguage);
          setProblemsFromAPI(false);
          setIsGeneratingPractice(false);
          isManualGenerationRef.current = false;
          return;
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌[Button API Error] Status ${response.status}:`, errorText);
          throw new Error('Failed to generate practice problems');
        }

        const data = await response.json();
        console.log('✅[Button API Success] Received problems:', data.length);
        setGeneratedProblems(data);
        setProblemsLanguage(selectedLanguage);
        setProblemsFromAPI(true);
      } catch (error) {
        console.error('❌[Button Fetch Error] Error fetching practice problems:', error);
        console.log('⚠️ Falling back to local generation...');
        setGeneratedProblems(generatePracticeProblems(9, selectedLanguage));
        setProblemsLanguage(selectedLanguage);
        setProblemsFromAPI(false);
      } finally {
        setIsGeneratingPractice(false);
        isManualGenerationRef.current = false;
      }
    };

    const handlePrint = () => {
      const printContent = document.getElementById('practice-sheet-print');
      if (printContent) {
        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write('<html><head><title></title>'); /* Empty title to prevent header text */
        printWindow.document.write(`<style>
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        @page {
          margin: 0;
          background-color: white;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          background-color: white !important;
          margin: 0;
          padding: 1cm;
        }
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { display: none; }
          html, body {
            background-color: white !important;
            margin: 0;
            padding: 20px 40px; /* Simulate margins (Top/Bottom 20px, L/R 40px) */
            zoom: 0.9;
          }
          @page {
            size: auto;
            margin: 0 !important; /* Force remove browser headers/footers */
          }
        }
        .problem-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          background-color: white;
        }
        .problem-card {
          padding: 12px;
          border-radius: 12px;
          border: 2px solid #e9d09b;
          background-color: white !important;
          display: flex;
          flex-direction: column;
          min-height: 220px; /* Increased min-height */
          padding-bottom: 5px;
          margin-bottom: 8px;
          position: relative;
        }
        .problem-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0px;
        }
        .problem-number {
          font-size: 18px;
          font-weight: bold;
          color: #81a550;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        }
        .problem-type {
          padding: 4px 10px;
          border-radius: 8px;
          background-color: rgba(255, 183, 77, 0.15);
          font-size: 10px;
          font-weight: 600;
          color: #ffb74d;
        }
        .problem-question {
          text-align: center;
          padding: 10px 4px;
          height: 80px; /* Fixed height to align dashed lines across row */
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden; /* Prevent massive overflow */
        }
        .problem-question > * {
          width: 100%;
        }
        .problem-answer {
          margin: 0 -12px -2px -12px; /* Fix margins to align with bottom */
          padding: 12px 16px 16px;
          border-top: 2px dashed #e9d09b;
          background-color: #F9F7F0;
          border-bottom-left-radius: 12px;
          border-bottom-right-radius: 12px;
        }
        .answer-label {
          font-size: 11px;
          color: #666;
          margin-bottom: 6px;
          font-weight: 600;
        }
        .answer-box {
          width: 100%;
          height: 35px;
          border: 2px solid #e9d09b;
          border-radius: 8px;
          background-color: white !important;
        }
        .solution-section {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid #eee;
          padding-bottom: 0;
          margin-bottom: 0;
        }
      </style>`);
        printWindow.document.write('</head><body>');
        printWindow.document.write(`
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; font-family: sans-serif;">
          <div style="display: flex; flex-direction: column; align-items: flex-start;">
            <img src="./mathzoo-logo.png" style="height:24px; margin-bottom: 4px;">
          </div>
          <div style="font-size: 14px; font-weight: 600; color: #333;">Practice Sheet</div>
          <div style="font-size: 12px; color: #666;">${new Date().toLocaleDateString()}</div>
        </div>
        <div class="problem-grid">
      `);

        const pdfLabels = {
          en: { answer: 'Your Answer:', explanation: 'Step-by-Step Solutions', steps: 'Detailed explanations' },
          nl: { answer: 'Jouw Antwoord:', explanation: 'Stapsgewijze Oplossingen', steps: 'Gedetailleerde uitleg' },
          de: { answer: 'Deine Antwort:', explanation: 'Schritt-für-Schritt-Lösungen', steps: 'Detaillierte Erklärungen' },
          es: { answer: 'Tu Respuesta:', explanation: 'Soluciones Paso a Paso', steps: 'Explicaciones detalladas' },
          bg: { answer: 'Твоят отговор:', explanation: 'Решение стъпка по стъпка', steps: 'Подробни обяснения' },
          ru: { answer: 'Твой ответ:', explanation: 'Пошаговое решение', steps: 'Подробные объяснения' }
        };

        const labels = pdfLabels[problemsLanguage] || pdfLabels.en;

        // Write each problem card with proper structure
        generatedProblems.forEach(problem => {
          const fontSize = '14px';
          const fontFamily = defaultFontFamily;
          const fontWeight = '600';

          printWindow.document.write(`
          <div class="problem-card">
            <div class="problem-header">
              <div class="problem-number">${problem.id}</div>
              <div class="problem-type">${problem.type}</div>
            </div>
            <div class="problem-question">
              <div style="font-size: ${fontSize}; font-family: ${fontFamily}; font-weight: ${fontWeight}; color: #333; line-height: 1.3;">
                ${problem.question}
              </div>
            </div>
            <div class="problem-answer">
              <div class="answer-label">${labels.answer}</div>
              <div class="answer-box"></div>
            </div>
            ${isPro ? `
            <div class="solution-section">
               <div style="font-size: 12px; font-weight: bold; color: #81a550; margin-bottom: 3px;">
                 <span style="margin-right: 5px;">✓</span>${labels.explanation}
               </div>
               <div style="font-size: 12px; color: #4A5568; margin-bottom: 6px; line-height: 1.5;">
                 ${problem.explanation}
               </div>
               <div style="font-size: 12px; font-weight: bold; color: #81a550; margin-bottom: 3px;">
                 <span style="margin-right: 5px;">💡</span>${labels.steps}
               </div>
               <div style="font-size: 12px; color: #4A5568; line-height: 1.5;">
                 ${problem.steps?.map(step => `<div style="margin-bottom: 3px;">• ${step}</div>`).join('')}
               </div>
            </div>
            ` : ''}
          </div>
        `);
        });

        printWindow.document.write('</div></body></html>');
        printWindow.document.close();
        printWindow.print();
      }
    };

    return (

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Payment Success Notification */}
        {showPaymentSuccess && (
          <div style={{
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '16px 32px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '90%'
          }}>
            <CheckCircle size={24} />
            <div>
              <strong>Welcome to PRO!</strong>
              <p style={{ margin: 0, fontSize: '14px' }}>Your subscription is now active</p>
            </div>
          </div>
        )}

        {/* Rate Limit Notification — only shown for report limit (practice limit is inline) */}
        {rateLimitInfo && rateLimitInfo.type !== 'report' && rateLimitInfo.type !== 'practice' && (
          <div style={{
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#FF9800',
            color: 'white',
            padding: '16px 28px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            maxWidth: '92%',
            width: '480px',
          }}>
            <span style={{ fontSize: '28px' }}>⏰</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '15px' }}>
                Daily limit reached
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.92 }}>
                Come back tomorrow for more!
                {rateLimitInfo.resetAt && (() => {
                  try {
                    const resetDate = new Date(rateLimitInfo.resetAt);
                    const timeStr = resetDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    const dateStr = resetDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
                    return ` Resets on ${dateStr} at ${timeStr}.`;
                  } catch { return ''; }
                })()}
              </p>
            </div>
            <button
              onClick={() => setRateLimitInfo(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '22px',
                lineHeight: 1,
                padding: '0 4px',
                flexShrink: 0,
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Payment Error Notification */}
        {paymentError && (
          <div style={{
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#f44336',
            color: 'white',
            padding: '16px 32px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '90%'
          }}>
            <AlertCircle size={24} />
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '14px' }}>{paymentError}</p>
            </div>
            <button
              onClick={() => setPaymentError(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '20px',
                marginLeft: '8px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Personal Report Card with PRO Button */}
        <FloatingCard style={{
          padding: '24px',
          backgroundColor: '#F4F7EE', // Soft light green/cream background
          border: '1px solid rgba(129, 165, 80, 0.15)',
          borderRadius: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'stretch',
            gap: '24px'
          }}>
            {/* Left Column: Report Info and Grid */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '20px'
            }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: 'rgb(245, 230, 193)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Brain size={24} color="#D69E2E" />
                </div>
                <div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '800',
                    color: '#2D3748',
                    margin: 0
                  }}>
                    {userData?.profile?.name || 'MathHippo'}'s AI Report
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#718096',
                    margin: '4px 0 0 0'
                  }}>
                    Built from {analytics?.totalSessions || 10} practice sessions · updated after every session
                  </p>
                </div>
              </div>

              {/* Grid of 4 cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '12px'
              }}>
                {/* Card 1: Strengths */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '12px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ color: 'rgb(129, 165, 80)', marginTop: '2px' }}>
                    <TrendingUp size={20} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '800', color: '#2D3748', margin: '0 0 4px 0' }}>Strengths</h4>
                    <p style={{ fontSize: '12px', color: '#718096', margin: 0, lineHeight: 1.4 }}>What's already working, by topic</p>
                  </div>
                </div>

                {/* Card 2: Where help is needed */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '12px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ color: 'rgb(129, 165, 80)', marginTop: '2px' }}>
                    <Search size={20} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '800', color: '#2D3748', margin: '0 0 4px 0' }}>Where help is needed</h4>
                    <p style={{ fontSize: '12px', color: '#718096', margin: 0, lineHeight: 1.4 }}>Exact question types that trip {userData?.profile?.name || 'MathHippo'} up</p>
                  </div>
                </div>

                {/* Card 3: 7-day plan */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '12px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ color: 'rgb(129, 165, 80)', marginTop: '2px' }}>
                    <Calendar size={20} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '800', color: '#2D3748', margin: '0 0 4px 0' }}>7-day plan</h4>
                    <p style={{ fontSize: '12px', color: '#718096', margin: 0, lineHeight: 1.4 }}>Ten minutes an evening, written for parents</p>
                  </div>
                </div>

                {/* Card 4: Teacher summary */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '12px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ color: 'rgb(129, 165, 80)', marginTop: '2px' }}>
                    <FileText size={20} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '800', color: '#2D3748', margin: '0 0 4px 0' }}>Teacher summary</h4>
                    <p style={{ fontSize: '12px', color: '#718096', margin: 0, lineHeight: 1.4 }}>One shareable PDF page for school</p>
                  </div>
                </div>
              </div>

              {/* Tagline / Badges */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
                marginTop: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '800', color: '#2D3748' }}>
                  <Crown size={14} color="#D69E2E" fill="#D69E2E" />
                  <span>Also in PRO:</span>
                </div>
                {['9-problem sheets', 'Step-by-step solutions', 'Answer keys', '6 languages'].map(badge => (
                  <span key={badge} style={{
                    backgroundColor: 'rgba(129, 165, 80, 0.1)',
                    color: '#4A5D33',
                    border: '1px solid rgba(129, 165, 80, 0.15)',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Right Column: Generation / Limit or Upgrade Card */}
            <div style={{
              flex: isMobile ? '1 1 auto' : '0 0 340px',
              maxWidth: isMobile ? '100%' : '340px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {isPro ? (
                rateLimitInfo?.type === 'report' ? (
                  /* ── Rate limit card ── */
                  <div style={{
                    padding: '24px',
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                    color: 'white',
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(255,152,0,0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>⏰</div>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '8px' }}>
                      Daily Report Limit Reached
                    </div>
                    <p style={{ fontSize: '12px', opacity: 0.95, lineHeight: 1.5, margin: '0 0 16px 0' }}>
                      3 reports per day maximum.
                      {rateLimitInfo.resetAt && (() => {
                        try {
                          const resetDate = new Date(rateLimitInfo.resetAt);
                          return ` Come back on ${resetDate.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${resetDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`;
                        } catch { return ' Come back tomorrow!'; }
                      })()}
                    </p>
                    <button
                      onClick={() => setRateLimitInfo(null)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '10px',
                        border: '1px solid rgba(255,255,255,0.6)',
                        backgroundColor: 'transparent',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                ) : (
                  /* ── Normal Generation Box ── */
                  <div style={{
                    padding: '24px',
                    borderRadius: '20px',
                    backgroundColor: 'rgb(129, 165, 80)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    boxShadow: '0 4px 12px rgba(129, 165, 80, 0.25)',
                    height: '100%',
                    boxSizing: 'border-box',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <FileText size={20} color="white" />
                        <span style={{ fontSize: '16px', fontWeight: '800' }}>Generate today's report</span>
                      </div>
                      <p style={{ fontSize: '12.5px', opacity: 0.9, lineHeight: 1.4, margin: 0 }}>
                        Fresh analysis of the latest sessions, ready as a PDF in under a minute.
                      </p>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', marginBottom: '4px', opacity: 0.9 }}>
                        <span>Reports today</span>
                        <span>{reportsCount} of 3 used</span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: 'rgba(255,255,255,0.25)',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${Math.min(100, (reportsCount / 3) * 100)}%`,
                          height: '100%',
                          backgroundColor: '#FEF3C7',
                          borderRadius: '3px'
                        }} />
                      </div>
                    </div>

                    <button
                      onClick={() => handleGenerateReport((generatedReport && !hasDownloadedLatestReport) ? false : true)}
                      disabled={isGeneratingReport}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '14px',
                        border: 'none',
                        backgroundColor: (generatedReport && !hasDownloadedLatestReport) ? '#FEF3C7' : 'white',
                        color: 'rgb(129, 165, 80)',
                        fontWeight: 'bold',
                        fontSize: '15px',
                        cursor: isGeneratingReport ? 'not-allowed' : 'pointer',
                        width: '100%',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!isGeneratingReport) e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      {isGeneratingReport ? 'Processing...' : (generatedReport && !hasDownloadedLatestReport) ? 'Download PDF' : 'Generate New Report'}
                    </button>

                    <div style={{ borderTop: '0.8px solid rgba(255, 255, 255, 0.2)', paddingTop: '10px', fontSize: '11.5px', opacity: 0.9, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Last report: {generatedReport?.createdAt ? new Date(generatedReport.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'None'}</span>
                      {generatedReport && (
                        <span
                          onClick={() => handleGenerateReport((generatedReport && !hasDownloadedLatestReport) ? true : false)}
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          {(generatedReport && !hasDownloadedLatestReport) ? 'Generate New Report' : 'Download PDF again'}
                        </span>
                      )}
                    </div>
                  </div>
                )
              ) : (
                /* ── Unlock PRO Box ── */
                <div style={{
                  padding: '24px',
                  borderRadius: '20px',
                  backgroundColor: 'rgb(129, 165, 80)',
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(129, 165, 80, 0.25)',
                  height: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
                    <Crown size={20} color="#FFD700" fill="#FFD700" />
                    <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', margin: 0 }}>
                      Unlock MathZoo PRO
                    </h4>
                  </div>

                  <div style={{
                    textAlign: 'left',
                    margin: '0 auto 10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    fontSize: '13px',
                    lineHeight: '1.4'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                      <span style={{ color: '#FFD700', fontWeight: 'bold', fontSize: '15px', lineHeight: 1 }}>✓</span>
                      <span>See <strong>exactly</strong> where your child struggles, updated after every session</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                      <span style={{ color: '#FFD700', fontWeight: 'bold', fontSize: '15px', lineHeight: 1 }}>✓</span>
                      <span>AI report PDF you can <strong>share with the teacher</strong></span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                      <span style={{ color: '#FFD700', fontWeight: 'bold', fontSize: '15px', lineHeight: 1 }}>✓</span>
                      <span>Unlimited <strong>targeted practice sheets</strong> with step-by-step solutions</span>
                    </div>
                  </div>

                  <div style={{
                    fontSize: '24px',
                    fontWeight: '800',
                    color: '#FFD700',
                    marginBottom: '20px'
                  }}>
                    9.99 EUR<span style={{ fontSize: '14px', fontWeight: '600' }}>/month</span>
                  </div>

                  <button
                    onClick={handleUpgradeToPro}
                    disabled={isProcessingPayment}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '14px',
                      border: 'none',
                      backgroundColor: 'white',
                      color: 'rgb(129, 165, 80)',
                      fontWeight: 'bold',
                      fontSize: '15px',
                      cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                      width: '100%',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isProcessingPayment) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.backgroundColor = 'rgb(240, 245, 235)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isProcessingPayment) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    {isProcessingPayment ? 'Processing...' : 'Upgrade to PRO'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </FloatingCard>


        {/* Analysis Summary - Above Challenging Questions */}
        <FloatingCard style={{ padding: '24px', backgroundColor: 'white', borderRadius: '18px', border: '1px solid #EAE2CA' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: appColors.textPrimary,
            margin: '0 0 4px 0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <TrendingUp size={24} color={appColors.secondary} style={{ marginRight: '12px' }} />
            Challenge Analysis Summary
          </h3>
          <p style={{ fontSize: '14px', color: appColors.textSecondary, marginBottom: '20px' }}>
            What the practice sheet below is built from - {sessions.length} sessions analyzed
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px',
            marginBottom: mostWrongAnswers.length > 0 ? '16px' : '0px'
          }}>
            {/* Card 1: Most Errors */}
            <div style={{
              padding: '18px 16px',
              borderRadius: '14px',
              backgroundColor: mostWrongAnswers.length === 0 ? '#F2F7EC' : '#FFF5F5',
              border: `1px solid ${mostWrongAnswers.length === 0 ? '#E1ECC8' : '#FED7D7'}`,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: mostWrongAnswers.length === 0 ? '#5E983B' : '#E53E3E', lineHeight: 1.1, marginBottom: '6px' }}>
                {mostWrongAnswers.length > 0 ? mostWrongAnswers[0].wrongCount : 0}
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#2D3748', lineHeight: 1.3 }}>
                most errors
              </div>
              <div style={{ fontSize: '12px', color: '#718096', lineHeight: 1.3 }}>
                {mostWrongAnswers.length === 0 ? 'nothing needs fixing' : 'on top challenge'}
              </div>
            </div>

            {/* Card 2: Challenging Questions */}
            <div style={{
              padding: '18px 16px',
              borderRadius: '14px',
              backgroundColor: '#FFFBEB',
              border: '1px solid #FEEBC8',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#DD6B20', lineHeight: 1.1, marginBottom: '6px' }}>
                {mostWrongAnswers.length}
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#2D3748', lineHeight: 1.3 }}>
                challenging questions
              </div>
              <div style={{ fontSize: '12px', color: '#718096', lineHeight: 1.3 }}>
                {mostWrongAnswers.length === 0 ? 'none identified yet' : 'questions identified'}
              </div>
            </div>

            {/* Card 3: Focus Level */}
            <div style={{
              padding: '18px 16px',
              borderRadius: '14px',
              backgroundColor: '#F2F7EC',
              border: '1px solid #E1ECC8',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#5E983B', lineHeight: 1.1, marginBottom: '6px' }}>
                Level {weakestGrade?.grade?.replace('Level ', '') || userData?.profile?.grade || '1'}
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#2D3748', lineHeight: 1.3 }}>
                focus level
              </div>
              <div style={{ fontSize: '12px', color: '#718096', lineHeight: 1.3 }}>
                {weakestGrade?.accuracy ?? 75}% accuracy - biggest upside
              </div>
            </div>
          </div>

          {/* Bottom 2 Cards - Only shown when there are challenges */}
          {((questionAnalytics && Object.keys(questionAnalytics.byErrorType || {}).length > 0) || 
            (questionAnalytics && (questionAnalytics.needsReview?.length || 0) > 0)) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: (questionAnalytics && Object.keys(questionAnalytics.byErrorType || {}).length > 0) && (questionAnalytics && (questionAnalytics.needsReview?.length || 0) > 0)
                ? (window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)')
                : '1fr',
              gap: '16px'
            }}>
              {/* Card 4: Common error types */}
              {questionAnalytics && Object.keys(questionAnalytics.byErrorType || {}).length > 0 && (
                <div style={{
                  padding: '18px 20px',
                  borderRadius: '14px',
                  backgroundColor: '#FFF5F5',
                  border: '1px solid #FED7D7'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#2D3748', marginBottom: '10px' }}>
                    Common error types
                  </div>
                  {Object.entries(questionAnalytics.byErrorType)
                    .sort((a, b) => b[1].count - a[1].count)
                    .slice(0, 3)
                    .map(([errorType, data], idx) => (
                      <div key={idx} style={{ fontSize: '13px', color: '#718096', marginBottom: '6px' }}>
                        • {errorType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {data.count} questions
                      </div>
                    ))}
                </div>
              )}

              {/* Card 5: Needs review */}
              {questionAnalytics && (questionAnalytics.needsReview?.length || 0) > 0 && (
                <div style={{
                  padding: '18px 20px',
                  borderRadius: '14px',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FEEBC8'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#2D3748', marginBottom: '10px' }}>
                    Needs review
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '24px', fontWeight: '800', color: '#DD6B20' }}>
                      {questionAnalytics.needsReview.length}
                    </span>
                    <span style={{ fontSize: '13px', color: '#718096' }}>
                      questions flagged for remediation
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </FloatingCard>

        {/* Most Challenging Questions - Top 3 with PRO Overlay */}
        <FloatingCard style={{ padding: '24px', position: 'relative' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '8px',
            marginTop: '0px',
            display: 'flex',
            alignItems: 'center',
            color: appColors.textPrimary
          }}>
            <AlertCircle size={24} color={appColors.error} style={{ marginRight: '12px' }} />
            Most Challenging Questions
          </h3>
          <p style={{ fontSize: '14px', color: appColors.textSecondary, marginBottom: '20px' }}>
            Top questions your child struggles with - perfect for targeted practice
          </p>

          {mostWrongAnswers.length === 0 ? (
            <div style={{
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#F3F8EE',
              borderRadius: '10px',
              border: '1px dashed #D0E3C4'
            }}>
              <Award size={20} color="#5E983B" style={{ marginRight: '10px', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4B7B2B' }}>
                  No wrong answers yet!
                </span>
                <span style={{ fontSize: '13px', color: appColors.textSecondary, marginLeft: '6px' }}>
                  Keep up the great work and check back here after more practice sessions.
                </span>
              </div>
            </div>
          ) : mostWrongAnswers.length === 1 ? (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '16px',
                marginBottom: '16px',
                maxWidth: '400px'
              }}>
                {mostWrongAnswers.slice(0, 1).map((question, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '14px',
                      borderRadius: '12px',
                      backgroundColor: '#FFFBEB',
                      border: '2px solid #FEEBC8',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: '#FEEBC8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        color: '#DD6B20'
                      }}>
                        #{index + 1}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: question.wrongCount > 0 ? appColors.error : '#DD6B20'
                        }}>
                          {question.wrongCount > 0 ? question.wrongCount : question.skipCount}
                        </div>
                        <div style={{ fontSize: '9px', color: appColors.textSecondary }}>
                          {question.wrongCount > 0 ? 'wrong' : 'skipped'}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: appColors.textPrimary,
                      marginBottom: '10px',
                      lineHeight: '1.5',
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      {question.questionText}
                    </div>
                    <div style={{ fontSize: '12px', color: appColors.textSecondary, textAlign: 'center', marginTop: '6px', marginBottom: isPro ? '10px' : '0px' }}>
                      L{question.grade} • {question.difficulty?.charAt(0).toUpperCase() + question.difficulty?.slice(1)}
                    </div>

                    {/* PRO-only details */}
                    {isPro && (
                      <>
                        <div style={{
                          padding: '10px',
                          borderRadius: '8px',
                          backgroundColor: appColors.success + '15',
                          border: `1px solid ${appColors.success}40`,
                          marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle size={14} color={appColors.success} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '2px' }}>
                                Correct:
                              </div>
                              <div style={{ fontWeight: 'bold', color: appColors.success, fontSize: '14px' }}>
                                {question.correctAnswer}
                              </div>
                              {question.selectedAnswers && question.selectedAnswers.length > 0 && (
                                <div style={{ fontSize: '12px', color: appColors.textSecondary, marginTop: '4px' }}>
                                  Answers Given: {question.selectedAnswers.join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {question.errorType && (
                          <div style={{
                            padding: '8px',
                            borderRadius: '8px',
                            backgroundColor: appColors.warning + '15',
                            border: `1px solid ${appColors.warning}40`,
                            marginBottom: '8px'
                          }}>
                            <div style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '2px' }}>
                              Error Type:
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: appColors.warning }}>
                              {question.errorType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                          </div>
                        )}

                        {question.remediationSuggestion && (
                          <div style={{
                            padding: '8px',
                            borderRadius: '8px',
                            backgroundColor: appColors.primary + '10',
                            border: `1px solid ${appColors.primary}30`,
                            marginBottom: '8px'
                          }}>
                            <div style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '2px' }}>
                              Suggestion:
                            </div>
                            <div style={{ fontSize: '12px', color: appColors.textPrimary, lineHeight: '1.3' }}>
                              {question.remediationSuggestion}
                            </div>
                          </div>
                        )}

                        <div style={{
                          marginTop: 'auto',
                          paddingTop: '8px',
                          fontSize: '12px',
                          color: appColors.textSecondary,
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          {question.totalAttempts > 0 && (
                            question.wrongCount > 0
                              ? `${((question.wrongCount / question.totalAttempts) * 100).toFixed(0)}% error rate`
                              : `${((question.skipCount / question.totalAttempts) * 100).toFixed(0)}% skip rate`
                          )}
                          {question.needsReview && ' • Flagged for review'}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div style={{
                padding: '16px',
                textAlign: 'center',
                backgroundColor: appColors.accent + '10',
                borderRadius: '12px',
                border: `1px solid ${appColors.accent}30`
              }}>
                <p style={{ fontSize: '13px', color: appColors.textSecondary, margin: 0 }}>
                  Keep playing to discover more patterns in challenging questions!
                </p>
              </div>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {/* Questions Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
                gap: '16px',
                marginBottom: '16px'
              }}>
                {mostWrongAnswers.slice(0, 3).map((question, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '14px',
                      borderRadius: '12px',
                      backgroundColor: '#FFFBEB',
                      border: '2px solid #FEEBC8',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: '#FEEBC8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        color: '#DD6B20'
                      }}>
                        #{index + 1}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: question.wrongCount > 0 ? appColors.error : '#DD6B20'
                        }}>
                          {question.wrongCount > 0 ? question.wrongCount : question.skipCount}
                        </div>
                        <div style={{ fontSize: '9px', color: appColors.textSecondary }}>
                          {question.wrongCount > 0 ? 'wrong' : 'skipped'}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: appColors.textPrimary,
                      fontFamily: defaultFontFamily,
                      marginBottom: '0px',
                      textAlign: 'center',
                      padding: '8px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {question.questionText}
                    </div>

                    <div style={{ fontSize: '12px', color: appColors.textSecondary, marginTop: '6px', marginBottom: isPro ? '10px' : '0px', textAlign: 'center' }}>
                      L{question.grade} • {question.difficulty.charAt(0).toUpperCase() + question.difficulty.slice(1)}
                    </div>

                    {/* PRO-only details */}
                    {isPro && (
                      <>
                        <div style={{
                          padding: '10px',
                          borderRadius: '8px',
                          backgroundColor: appColors.success + '15',
                          border: `1px solid ${appColors.success}40`,
                          marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle size={14} color={appColors.success} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '2px' }}>
                                Correct:
                              </div>
                              <div style={{ fontWeight: 'bold', color: appColors.success, fontSize: '14px' }}>
                                {question.correctAnswer}
                              </div>
                              {question.selectedAnswers && question.selectedAnswers.length > 0 && (
                                <div style={{ fontSize: '12px', color: appColors.textSecondary, marginTop: '4px' }}>
                                  Answers Given: {question.selectedAnswers.join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {question.errorType && (
                          <div style={{
                            padding: '8px',
                            borderRadius: '8px',
                            backgroundColor: appColors.warning + '15',
                            border: `1px solid ${appColors.warning}40`,
                            marginBottom: '8px'
                          }}>
                            <div style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '2px' }}>
                              Error Type:
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: appColors.warning }}>
                              {question.errorType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                          </div>
                        )}

                        {question.remediationSuggestion && (
                          <div style={{
                            padding: '8px',
                            borderRadius: '8px',
                            backgroundColor: appColors.primary + '10',
                            border: `1px solid ${appColors.primary}30`,
                            marginBottom: '8px'
                          }}>
                            <div style={{ fontSize: '12px', color: appColors.textSecondary, marginBottom: '2px' }}>
                              Suggestion:
                            </div>
                            <div style={{ fontSize: '12px', color: appColors.textPrimary, lineHeight: '1.3' }}>
                              {question.remediationSuggestion}
                            </div>
                          </div>
                        )}

                        <div style={{
                          marginTop: 'auto',
                          paddingTop: '8px',
                          fontSize: '12px',
                          color: appColors.textSecondary,
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          {question.wrongCount > 0
                            ? `${((question.wrongCount / question.totalAttempts) * 100).toFixed(0)}% error rate`
                            : `${((question.skipCount / question.totalAttempts) * 100).toFixed(0)}% skip rate`}
                          {question.needsReview && ' • Flagged for review'}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Upgrade to PRO Box (Basic users) - styled similar to the Practice box */}
              {!isPro && (
                <div style={{
                  padding: '24px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  boxShadow: 'none',
                  boxSizing: 'border-box',
                  marginTop: '16px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#EAF4E3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '10px'
                  }}>
                    <Lock size={16} color="rgb(129, 165, 80)" />
                  </div>
                  <h4 style={{
                    fontSize: '15px',
                    fontWeight: 'bold',
                    color: '#2D3748',
                    margin: '0 0 8px 0',
                    lineHeight: '1.4'
                  }}>
                    Unlock all challenging questions with PRO
                  </h4>
                  <p style={{
                    fontSize: '12px',
                    color: '#718096',
                    margin: '0 0 20px 0',
                    lineHeight: '1.5',
                    maxWidth: '480px'
                  }}>
                    PRO accounts track the exact questions your kid struggles with, updated after every session, including correct answers, errors, and hints.
                  </p>
                  <button
                    onClick={handleUpgradeToPro}
                    disabled={isProcessingPayment}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: isProcessingPayment ? '#e2e8f0' : 'rgb(129, 165, 80)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '13.5px',
                      cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 6px rgba(110, 168, 75, 0.2)',
                      transition: 'all 0.2s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isProcessingPayment) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.filter = 'brightness(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isProcessingPayment) {
                        e.currentTarget.style.transform = 'none';
                        e.currentTarget.style.filter = 'none';
                      }
                    }}
                  >
                    {isProcessingPayment ? (
                      'Processing...'
                    ) : (
                      <>
                        <Crown size={14} color="white" fill="white" />
                        <span>Unlock challenging questions with PRO</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </FloatingCard>

        {/* Practice Problems Section */}
        <FloatingCard style={{ padding: '24px' }}>
          <div style={{ marginBottom: '0px' }}>
            <div style={{
              display: 'flex',
              flexDirection: window.innerWidth <= 1024 ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: window.innerWidth <= 1024 ? 'flex-start' : 'center',
              gap: '16px',
              marginBottom: '8px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                color: appColors.textPrimary
              }}>
                <BookOpen size={24} color={appColors.secondary} style={{ marginRight: '12px' }} />
                Personalized Practice Problems
              </h3>

              {/* Action Buttons Top Right aligned with Title */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
                flexShrink: 0
              }}>
                {isPro && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '0 16px',
                    borderRadius: '12px',
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    height: '46px',
                    boxSizing: 'border-box',
                    border: '1px solid rgba(0,0,0,0.05)'
                  }}>
                    <Globe size={18} color={appColors.primary} />
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: appColors.textPrimary }}>Print Language:</span>
                    <select
                      value={selectedLanguage}
                      onChange={handleLanguageChange}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        backgroundColor: isGeneratingPractice ? '#f5f5f5' : '#efefef',
                        fontSize: '13px',
                        color: isGeneratingPractice ? '#999' : '#333',
                        cursor: isGeneratingPractice ? 'wait' : 'pointer',
                        outline: 'none',
                        height: '28px',
                        pointerEvents: isGeneratingPractice ? 'none' : 'auto'
                      }}
                    >
                      <option value="en">English (Original)</option>
                      <option value="nl">Nederlands</option>
                      <option value="de">Deutsch</option>
                      <option value="es">Español</option>
                      <option value="bg">Български</option>
                      <option value="ru">Русский</option>
                    </select>
                  </div>
                )}

                <button
                  className={needsGeneration ? 'pulse-button' : ''}
                  onClick={(e) => { e.preventDefault(); handleGenerateNew(); }}
                  style={{
                    height: '46px',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: `2px solid ${needsGeneration ? appColors.accent : appColors.primary}`,
                    backgroundColor: needsGeneration ? appColors.accent : 'white',
                    color: needsGeneration ? 'white' : appColors.primary,
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: isGeneratingPractice ? 'wait' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxSizing: 'border-box',
                    opacity: isGeneratingPractice ? 0.7 : 1,
                    pointerEvents: isGeneratingPractice ? 'none' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    if (!isGeneratingPractice && !needsGeneration) {
                      e.currentTarget.style.backgroundColor = appColors.primary + '10';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isGeneratingPractice && !needsGeneration) {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {isGeneratingPractice ? (
                    <>
                      <RefreshCw size={18} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} />
                      Generate New
                      {problemsFromAPI && (
                        <span style={{ color: '#c8e6a0', fontSize: '16px', lineHeight: 1 }}>✨</span>
                      )}
                    </>
                  )}
                </button>

                <button
                  onClick={(e) => { e.preventDefault(); handlePrint(); }}
                  style={{
                    height: '46px',
                    padding: '12px 32px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: (selectedLanguage !== problemsLanguage || isGeneratingPractice) ? '#ccc' : appColors.primary,
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: (selectedLanguage !== problemsLanguage || isGeneratingPractice) ? 'not-allowed' : 'pointer',
                    boxShadow: (selectedLanguage !== problemsLanguage || isGeneratingPractice) ? 'none' : '0 4px 12px rgba(129, 165, 80, 0.3)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxSizing: 'border-box',
                    pointerEvents: (selectedLanguage !== problemsLanguage || isGeneratingPractice) ? 'none' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedLanguage === problemsLanguage && !isGeneratingPractice) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.filter = 'brightness(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedLanguage === problemsLanguage && !isGeneratingPractice) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.filter = 'none';
                    }
                  }}
                >
                  <Printer size={18} />
                  Print Practice Sheet ({selectedLanguage.toUpperCase()})
                </button>
              </div>
            </div>

            {/* Info text underneath full width */}
            <p style={{ fontSize: '14px', color: appColors.textSecondary, marginTop: '0px', marginBottom: '20px' }}>
              Based on performance analysis, these problems target weak areas in {weakestGrade.grade} (current accuracy: {weakestGrade.accuracy}%). Practice these to strengthen understanding.
            </p>

            {/* Why These Problems — PRO only */}
            {isPro && (
              <div style={{
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: appColors.primary + '10',
                marginBottom: '20px'
              }}>
                <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: appColors.textPrimary, marginBottom: '12px', marginTop: '0px' }}>
                  Why These Problems?
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', fontSize: '13px' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: appColors.error, marginBottom: '4px' }}>Weakest Area</div>
                    <div style={{ color: appColors.textSecondary }}>{weakestGrade.grade} with {weakestGrade.accuracy}% accuracy</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', color: appColors.warning, marginBottom: '4px' }}>Questions Skipped</div>
                    <div style={{ color: appColors.textSecondary }}>{weakestGrade.skipped} questions need retry</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', color: appColors.primary, marginBottom: '4px' }}>Target Improvement</div>
                    <div style={{ color: appColors.textSecondary }}>Aim for 80%+ accuracy</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Practice Problems Grid */}
          <div id="practice-sheet-print">
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
              gap: '16px',
              marginBottom: '20px'
            }}>

              {generatedProblems.map((problem) => (
                <div
                  key={problem.id}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: `2px solid ${appColors.separatorBrown}`,
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '230px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#E8E8E8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: appColors.primary,
                      fontFamily: defaultFontFamily
                    }}>
                      {problem.id}
                    </div>
                    <div style={{
                      padding: '4px 12px',
                      borderRadius: '8px',
                      backgroundColor: '#FFF5E6',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#E8B15C'
                    }}>
                      {({
                        en: { Addition:'Addition', Subtraction:'Subtraction', Multiplication:'Multiplication', Division:'Division', Fractions:'Fractions', Comparison:'Comparison', 'Word Problems':'Word Problems' },
                        bg: { Addition:'Събиране', Subtraction:'Изваждане', Multiplication:'Умножение', Division:'Деление', Fractions:'Дроби', Comparison:'Сравнение', 'Word Problems':'Текстови задачи' },
                        nl: { Addition:'Optellen', Subtraction:'Aftrekken', Multiplication:'Vermenigvuldigen', Division:'Delen', Fractions:'Breuken', Comparison:'Vergelijking', 'Word Problems':'Woordproblemen' },
                        de: { Addition:'Addition', Subtraction:'Subtraktion', Multiplication:'Multiplikation', Division:'Division', Fractions:'Brüche', Comparison:'Vergleich', 'Word Problems':'Textaufgaben' },
                        ru: { Addition:'Сложение', Subtraction:'Вычитание', Multiplication:'Умножение', Division:'Деление', Fractions:'Дроби', Comparison:'Сравнение', 'Word Problems':'Задачи' },
                        es: { Addition:'Suma', Subtraction:'Resta', Multiplication:'Multiplicación', Division:'División', Fractions:'Fracciones', Comparison:'Comparación', 'Word Problems':'Problemas' },
                      }[problemsLanguage] || {})[problem.type] || problem.type}
                    </div>
                  </div>

                  <div style={{
                    textAlign: 'center',
                    padding: '10px 4px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                    lineHeight: '1.3',
                    width: '100%'
                  }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#1a1a1a',
                      textAlign: 'center',
                      width: '100%',
                      margin: '0 auto'
                    }}>
                      {problem.question}
                    </div>
                  </div>

                  <div style={{
                    marginTop: 'auto',
                    paddingTop: '12px',
                    borderTop: `2px dashed ${appColors.separatorBrown}`,
                    backgroundColor: '#F9F7F0',
                    marginLeft: '-16px',
                    marginRight: '-16px',
                    marginBottom: '-16px',
                    padding: '12px 16px 16px 16px',
                    borderBottomLeftRadius: '12px',
                    borderBottomRightRadius: '12px'
                  }}>
                    <div style={{
                      fontSize: '11px',
                      color: appColors.textSecondary,
                      marginBottom: '6px',
                      fontWeight: '600'
                    }}>
                      {problemsLanguage === 'nl' ? 'Jouw Antwoord:' :
                        problemsLanguage === 'de' ? 'Deine Antwort:' :
                          problemsLanguage === 'es' ? 'Tu Respuesta:' :
                            problemsLanguage === 'bg' ? 'Твоят Отговор:' :
                              problemsLanguage === 'ru' ? 'Ваш Ответ:' : 'Your Answer:'}
                    </div>
                    <div style={{
                      width: '100%',
                      height: '35px',
                      border: `2px solid ${appColors.separatorBrown}`,
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      marginBottom: '12px'
                    }} />

                    {/* Step-by-Step Solutions (PRO) */}
                    {isPro && (
                      <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#81a550', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '13px' }}>✓</span> {problemsLanguage === 'nl' ? 'Stapsgewijze Oplossingen' :
                            problemsLanguage === 'de' ? 'Schritt-für-Schritt-Lösungen' :
                              problemsLanguage === 'es' ? 'Soluciones Paso a Paso' :
                                problemsLanguage === 'bg' ? 'Решение стъпка по стъпка' :
                                  problemsLanguage === 'ru' ? 'Пошаговое решение' : 'Step-by-Step Solutions'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#4A5568', marginBottom: '8px', lineHeight: '1.5' }}>
                          {problem.explanation}
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#81a550', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '13px' }}>💡</span> {problemsLanguage === 'nl' ? 'Gedetailleerde uitleg' :
                            problemsLanguage === 'de' ? 'Detaillierte Erklärungen' :
                              problemsLanguage === 'es' ? 'Explicaciones detalladas' :
                                problemsLanguage === 'bg' ? 'Подробни обяснения' :
                                  problemsLanguage === 'ru' ? 'Подробные объяснения' : 'Detailed explanations'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#4A5568', lineHeight: '1.5' }}>
                          {problem.steps?.map((step, idx) => (
                            <div key={idx} style={{ marginBottom: '4px' }}>• {step}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {!isPro && (
                <div style={{
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  boxShadow: 'none',
                  boxSizing: 'border-box',
                  gridColumn: window.innerWidth <= 768 ? 'auto' : '1 / -1'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#EAF4E3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '10px'
                  }}>
                    <Lock size={16} color="rgb(129, 165, 80)" />
                  </div>
                  <h4 style={{
                    fontSize: '15px',
                    fontWeight: 'bold',
                    color: '#2D3748',
                    margin: '0 0 8px 0',
                    lineHeight: '1.4'
                  }}>
                    6 more Personalized  problems + step-by-step solutions with PRO
                  </h4>
                  <p style={{
                    fontSize: '12px',
                    color: '#718096',
                    margin: '0 0 20px 0',
                    lineHeight: '1.5',
                    maxWidth: '480px'
                  }}>
                    PRO sheets target the exact questions your Kid got wrong, refreshed after every session - in 6 different languages and with solutions you can explain together.
                  </p>
                  <button
                    onClick={handleUpgradeToPro}
                    disabled={isProcessingPayment}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: isProcessingPayment ? '#e2e8f0' : 'rgb(129, 165, 80)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '13.5px',
                      cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 6px rgba(110, 168, 75, 0.2)',
                      transition: 'all 0.2s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isProcessingPayment) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.filter = 'brightness(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isProcessingPayment) {
                        e.currentTarget.style.transform = 'none';
                        e.currentTarget.style.filter = 'none';
                      }
                    }}
                  >
                    {isProcessingPayment ? (
                      'Processing...'
                    ) : (
                      <>
                        <Crown size={14} color="white" fill="white" />
                        <span>Unlock full sheet with PRO</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {(isPro || rateLimitInfo?.type === 'practice') && (
            <div
              ref={generatorRef}
              tabIndex={-1}
              style={{
                padding: '12px 16px',
                borderRadius: '16px',
                backgroundColor: appColors.primary + '10',
                outline: 'none'
              }}>
              {isPro && (
                <p style={{
                  fontSize: '13px',
                  color: appColors.textSecondary,
                  fontStyle: 'italic',
                  lineHeight: '1.5',
                  margin: '0',
                  marginBottom: rateLimitInfo?.type === 'practice' ? '12px' : '0',
                  textAlign: 'left'
                }}>
                  <strong>AI Tutor Tip:</strong>{' '}
                  Take your time with each problem. If stuck, break the problem into smaller steps. Making mistakes is part of learning!
                </p>
              )}

              {/* ── Practice rate-limit inline banner — full width below ── */}
              {rateLimitInfo?.type === 'practice' && (
                <div style={{
                  marginTop: '16px',
                  padding: '14px 20px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 2px 10px rgba(255,152,0,0.3)',
                  width: '100%',
                  boxSizing: 'border-box',
                }}>
                  <span style={{ fontSize: '24px', flexShrink: 0 }}>⏰</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '14px' }}>
                      Daily practice limit reached (10 per day)
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: '12px', opacity: 0.92 }}>
                      Come back tomorrow for more AI-generated problems!
                      {rateLimitInfo.resetAt && (() => {
                        try {
                          const resetDate = new Date(rateLimitInfo.resetAt);
                          const timeStr = resetDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                          const dateStr = resetDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
                          return ` Resets on ${dateStr} at ${timeStr}.`;
                        } catch { return ''; }
                      })()}
                    </p>
                  </div>
                  <button
                    onClick={() => setRateLimitInfo(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      padding: '0 4px',
                      flexShrink: 0,
                    }}
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          )}


        </FloatingCard >
      </div >
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${appColors.background} 0%, #f8f4e6 100%)`
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🐨</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: appColors.textPrimary }}>Loading Dashboard...</div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'thisWeek', label: 'This Week', icon: Target },
    { id: 'insights', label: 'Skills Journey', icon: Brain, hasNotification: hasNewDataNotification },
    { id: 'aitutor', label: 'AI Tutor', icon: Sparkles, isPro: true }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${appColors.background} 0%, #f8f4e6 100%)`,
      backgroundAttachment: 'fixed',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Simplified Header with Child Profile Switcher Popover */}
      <div style={{ padding: isMobile ? '0 10px' : '0 24px', backgroundColor: 'transparent', position: 'relative', zIndex: 50 }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexDirection: 'column', padding: '12px 0' }}>

          {/* Top row: Logo (left) + Child Switcher & Buttons (right) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
              <img
                src="./mathzoo-logo.png"
                alt="MathZoo Junior Logo"
                style={{ height: isMobile ? '60px' : '88px' }}
              />
              {!isMobile && (
                <div>
                  <div style={{ fontSize: '16px', color: '#81a550', fontWeight: 'bold' }}>Parent Section</div>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', color: appColors.textPrimary }}>
                    Kid's Performance Dashboard
                  </div>
                </div>
              )}
            </div>

            {/* Right side: Child Switcher + PRO CTA + Logout */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
              {/* Child Profile Switcher Button */}
              <div ref={childProfileRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setChildProfileOpen(!childProfileOpen)}
                  onMouseEnter={() => setHoveredHeaderBtn('switcher')}
                  onMouseLeave={() => setHoveredHeaderBtn(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '5px 14px 5px 6px',
                    borderRadius: '28px',
                    backgroundColor: hoveredHeaderBtn === 'switcher' ? 'rgb(255, 251, 235)' : '#FFFDF5',
                    border: hoveredHeaderBtn === 'switcher' ? '1px solid #D6C8A6' : '1px solid #E8DFCA',
                    cursor: 'pointer',
                    textAlign: 'left',
                    height: '46px',
                    boxShadow: hoveredHeaderBtn === 'switcher' ? '0 2px 8px rgba(0,0,0,0.06)' : '0 1px 3px rgba(0,0,0,0.03)',
                    transform: 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      backgroundColor: '#F5E4BA',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      <img
                        src={getAnimalImage(userData?.profile?.avatar)}
                        alt={userData?.profile?.avatar || 'avatar'}
                        style={{ width: '26px', height: '26px', objectFit: 'contain' }}
                      />
                    </div>
                    {isPro && (
                      <div style={{
                        position: 'absolute',
                        top: '-3px',
                        right: '-3px',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Crown size={8} color="#F6C112" fill="#F6C112" />
                      </div>
                    )}
                  </div>
                  {!isMobile && (
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '800', color: '#2D3748', lineHeight: 1.2 }}>
                        {userData?.profile?.name || 'MathHippo'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#8A785D', fontWeight: '500', lineHeight: 1.2 }}>
                        Age {userData?.profile?.age || 10} . Level {userData?.progress?.currentGrade || 2}
                      </div>
                    </div>
                  )}
                  <ChevronDown size={16} color="#8A785D" style={{ marginLeft: '2px' }} />
                </button>

                {/* Dropdown Card */}
                {childProfileOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 10px)',
                    right: 0,
                    width: isMobile ? '270px' : '290px',
                    backgroundColor: 'white',
                    borderRadius: '18px',
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                    padding: '16px',
                    zIndex: 100
                  }}>
                    {/* Header inside popover */}
                    {/* Header inside popover */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          backgroundColor: '#F5E4BA',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}>
                          <img
                            src={getAnimalImage(userData?.profile?.avatar)}
                            alt={userData?.profile?.avatar || 'avatar'}
                            style={{ width: '34px', height: '34px', objectFit: 'contain' }}
                          />
                        </div>
                        {isPro && (
                          <div style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Crown size={10} color="#F6C112" fill="#F6C112" />
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                          <div style={{ fontSize: '16px', fontWeight: '800', color: '#1A1A1A', lineHeight: 1.1 }}>
                            {userData?.profile?.name || 'MathHippo'}
                          </div>
                          {isPro && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 9px',
                              fontSize: '11px',
                              fontWeight: '800',
                              color: 'white',
                              backgroundColor: 'rgb(129, 165, 80)',
                              borderRadius: '12px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                              flexShrink: 0
                            }}>
                              <Crown size={11} color="#F6C112" fill="#F6C112" />
                              PRO
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px', whiteSpace: 'nowrap' }}>
                          Age {userData?.profile?.age || 10} . playing since March 2026
                        </div>
                        {auth.currentUser?.email && (
                          <div style={{
                            fontSize: '11px',
                            color: '#5E983B',
                            marginTop: '3px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                          }}>
                            <span style={{ fontWeight: '600' }}>Parent:</span> {auth.currentUser.email}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stats 2x2 Grid with actual DB progress */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '10px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ backgroundColor: '#F5F7E8', padding: '12px', borderRadius: '14px' }}>
                        <div style={{ fontSize: '11px', color: '#718096', fontWeight: '600', marginBottom: '4px' }}>Total XP</div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#5E983B' }}>{userData?.progress?.totalXP || 0}</div>
                      </div>
                      <div style={{ backgroundColor: '#F5F7E8', padding: '12px', borderRadius: '14px' }}>
                        <div style={{ fontSize: '11px', color: '#718096', fontWeight: '600', marginBottom: '4px' }}>Play time</div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#1A1A1A' }}>
                          {(() => {
                            let totalSecs = userData?.progress?.totalTimeSpent || 0;
                            if (!totalSecs && gradeStats) {
                              Object.values(gradeStats).forEach(gd => {
                                totalSecs += gd.stats?.totalTimeSeconds || 0;
                              });
                            }
                            return `${Math.max(1, Math.round(totalSecs / 60))} min`;
                          })()}
                        </div>
                      </div>
                      <div style={{ backgroundColor: '#F5F7E8', padding: '12px', borderRadius: '14px' }}>
                        <div style={{ fontSize: '11px', color: '#718096', fontWeight: '600', marginBottom: '4px' }}>Sessions</div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#1A1A1A' }}>
                          {analytics?.completedSessions ?? sessions.length}{' '}
                          {(analytics?.inProgressSessions > 0) && (
                            <span style={{ fontSize: '13px', color: '#DD6B20', fontWeight: '700' }}>
                              +{analytics.inProgressSessions} open
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ backgroundColor: '#F5F7E8', padding: '12px', borderRadius: '14px' }}>
                        <div style={{ fontSize: '11px', color: '#718096', fontWeight: '600', marginBottom: '4px' }}>Level {userData?.progress?.currentGrade || 2} progress</div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#1A1A1A' }}>
                          {(() => {
                            const gr = userData?.progress?.currentGrade || 1;
                            const gKey = `grade${gr}`;
                            if (userData?.progress?.gradeStatuses?.[gKey] === 'completed') return '100%';
                            const gs = gradeStats?.[gKey]?.stats || gradeStats?.[gr]?.stats;
                            if (gs?.accuracyPercent) return `${Math.min(100, Math.round(gs.accuracyPercent))}%`;
                            if (gs?.totalQuestionsAttempted) return `${Math.min(100, Math.round((gs.totalQuestionsAttempted / 50) * 100))}%`;
                            const gSess = sessions.filter(s => parseInt(s.grade) === parseInt(gr));
                            if (gSess.length > 0) return `${Math.min(100, gSess.length * 15)}%`;
                            return userData?.progress?.levelProgress ? `${userData.progress.levelProgress}%` : '0%';
                          })()}
                        </div>
                      </div>
                    </div>



                    {/* Footer Info Notice with icon (No em-dashes) */}
                    <div style={{
                      backgroundColor: '#FFFBEB',
                      border: '1px solid #FEEBC8',
                      borderRadius: '12px',
                      padding: '10px 12px',
                      fontSize: '12px',
                      color: '#975A16',
                      lineHeight: 1.4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <AlertCircle size={16} color="#975A16" style={{ flexShrink: 0 }} />
                      <span>One child profile per account for now . multiple kids coming later</span>
                    </div>

                    {/* Actions row: Manage Subscription + Logout (Last elements, reduced border size) */}
                    <div style={{ display: 'flex', gap: '8px', width: '100%', marginTop: '12px' }}>
                      <button
                        onClick={handleManageSubscription}
                        disabled={isProcessingPayment}
                        style={{
                          flex: 1,
                          height: '36px',
                          padding: '0 12px',
                          borderRadius: '12px',
                          border: isPro ? '1px solid #D2E2C5' : 'none',
                          background: isPro ? '#FAFBF4' : 'rgb(129, 165, 80)',
                          color: isPro ? '#6E9B48' : 'white',
                          fontSize: '13px',
                          fontWeight: '800',
                          cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.85';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      >
                        {isPro ? (
                          <>
                            <CreditCard size={14} color="#6E9B48" />
                            <span style={{ whiteSpace: 'nowrap' }}>Manage Subscription</span>
                          </>
                        ) : (
                          <>
                            <Crown size={14} color="white" fill="white" />
                            <span style={{ whiteSpace: 'nowrap' }}>Try PRO</span>
                          </>
                        )}
                      </button>

                      <button
                        onClick={onLogout}
                        title="Logout"
                        aria-label="Logout"
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '12px',
                          border: '1px solid #D2E2C5',
                          background: '#FAFBF4',
                          color: '#6E9B48',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.85';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      >
                        <LogOut size={16} color="#6E9B48" />
                      </button>
                    </div>
                  </div>
                )}
              </div>




            </div>
          </div>

          {isMobile && (
            <div style={{ paddingTop: '8px' }}>
              <div style={{ fontSize: '12px', color: '#68A344', fontWeight: '800' }}>Parent Section</div>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#1A1A1A' }}>Kid's Dashboard</div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: isMobile ? '0 10px 16px' : '0 24px 24px', flex: '1', display: 'flex', flexDirection: 'column' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', flex: '1' }}>

          {/* Navigation Tabs Bar full width matching design */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px',
            backgroundColor: 'white',
            border: '1px solid #E2E8F0',
            padding: '8px 12px',
            borderRadius: '20px',
            width: '100%',
            boxSizing: 'border-box',
            boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
          }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                onMouseEnter={() => setHoveredTab(tab.id)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: activeTab === tab.id
                    ? 'rgb(129, 165, 80)'
                    : (hoveredTab === tab.id ? '#F1F5F9' : 'transparent'),
                  color: activeTab === tab.id ? 'white' : '#4A5568',
                  fontSize: '14px',
                  fontWeight: '700',
                  transition: 'all 0.2s'
                }}
              >
                <tab.icon size={18} />
                <span>{tab.label}</span>
                {tab.hasNotification && (
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#F56565',
                    display: 'inline-block'
                  }} />
                )}
                {tab.isPro && (
                  <span style={{
                    backgroundColor: 'rgb(229, 178, 59)',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '800',
                    padding: '2px 7px',
                    borderRadius: '10px'
                  }}>
                    PRO
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div>
            {(activeTab === 'thisWeek' || activeTab === 'performance') && renderThisWeekTab()}
            {activeTab === 'insights' && renderInsightsTab()}
            {activeTab === 'aitutor' && renderAITutorTab()}
          </div>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: '32px', paddingBottom: '16px' }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '22px',
              border: '1px solid #E2E8F0',
              boxShadow: '0 12px 28px -10px rgba(61,77,38,0.08)',
              padding: '18px 24px'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '10px' : '24px',
                fontSize: '14px',
                color: appColors.textSecondary
              }}>
                {/* Line 1: LIVE badge + Connected text */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Trophy size={16} color={appColors.accent} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: `${appColors.success}20`, padding: '4px 8px', borderRadius: '12px', border: `1px solid ${appColors.success}40` }}>
                    <div className="pulsating-dot" style={{ backgroundColor: appColors.success }} />
                    <span style={{ fontSize: '10px', fontWeight: 'bold', color: appColors.success, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Live</span>
                  </div>
                  <span>Connected to MathZoo Application Data</span>
                </div>
                {/* Line 2: Star + User ID */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Star size={16} color={appColors.primary} />
                  <span>User: {userId}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App component with Firebase authentication
const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [, setFirebaseUser] = useState(null);

  // Helper function to find user data by email
  // Uses emailIndex in Firebase for secure email-to-userId lookup
  const findUserByEmail = async (userEmail) => {
    try {
      console.log('🔍 Searching for user with email:', userEmail);
      const result = await getUserByEmail(userEmail);
      if (result) {
        console.log('✅ Found userId from emailIndex:', result.userId);
      } else {
        console.log('❌ No userId found in emailIndex for:', userEmail);
      }
      return result;
    } catch (err) {
      console.error('Error finding user:', err);
      return null;
    }
  };

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in
        console.log('Firebase Auth: User signed in', user.email);
        setFirebaseUser(user);

        // Try to find user data in database
        const userResult = await findUserByEmail(user.email);
        if (userResult) {
          setCurrentUserId(userResult.userId);
          setCurrentUserData(userResult.userData);
        } else {
          // Create a basic user ID from Firebase UID
          setCurrentUserId(`user_${user.uid.substring(0, 8).toUpperCase()}`);
          setCurrentUserData(null);
        }
        setIsAuthenticated(true);
      } else {
        // User is signed out
        console.log('Firebase Auth: User signed out');
        setFirebaseUser(null);
        setCurrentUserId(null);
        setCurrentUserData(null);
        setIsAuthenticated(false);
      }
      setAuthLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (userId, userData, fbUser) => {
    setCurrentUserId(userId);
    setCurrentUserData(userData);
    setFirebaseUser(fbUser);
    setIsAuthenticated(true);
    trackPageView('Dashboard');
  };

  const handleLogout = async () => {
    trackEvent('logout');
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear state even if signOut fails
      setIsAuthenticated(false);
      setCurrentUserId(null);
      setCurrentUserData(null);
      setFirebaseUser(null);
    }
  };

  // Public routes — accessible without authentication
  if (window.location.pathname === '/privacy') {
    return <PrivacyPage />;
  }

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: appColors.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <img
            src="/mathzoo-logo.png"
            alt="MathZoo Junior"
            style={{ height: '80px', marginBottom: '24px' }}
          />
          <div style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${appColors.separatorBrown}`,
            borderTopColor: appColors.primary,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: appColors.textSecondary, marginTop: '16px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} appColors={appColors} />;
  }

  return (
    <Dashboard
      userId={currentUserId}
      initialUserData={currentUserData}
      onLogout={handleLogout}
    />
  );
};

export default App;